<template>
  <div class="mel-page">
    <header class="mel-hero">
      <div class="mel-shell mel-hero-layout">
        <h1>Préparer une demande de mise en ligne</h1>
        <button
          type="button"
          class="mel-excel-shortcut"
          @click="activeTab = activeTab === 'excel' ? 'form' : 'excel'"
        >
          <i
            :class="activeTab === 'excel' ? 'fas fa-arrow-left' : 'fas fa-file-excel'"
            aria-hidden="true"
          ></i>
          <span class="mel-excel-label-long">
            {{ activeTab === "excel" ? "Revenir au formulaire" : "Importer avec Excel" }}
          </span>
          <span class="mel-excel-label-short">
            {{ activeTab === "excel" ? "Formulaire" : "Excel" }}
          </span>
        </button>
      </div>
    </header>

    <div ref="formTopAnchorRef" class="mel-shell mel-content">
      <!-- Onglet Formulaire en ligne -->
      <div v-if="activeTab === 'form'">
        <!-- Indicateur de mode édition -->
        <div
          v-if="editingReferenceId"
          class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
        >
          <div class="flex items-center justify-between">
            <span class="text-yellow-800 text-sm">
              <i class="fas fa-edit mr-2"></i>
              Mode édition - Référence: {{ formData.reference }}
            </span>
            <button
              type="button"
              @click="cancelEdit"
              class="text-yellow-600 hover:text-yellow-800 text-sm"
            >
              <i class="fas fa-times mr-1"></i>
              Annuler
            </button>
          </div>
        </div>

        <!-- Formulaire principal -->
        <form @submit.prevent="handleSubmit">
          <div class="mel-form-card">
            <div class="mel-section-heading">
              <span>1</span>
              <div>
                <h2>
                  {{
                    editingReferenceId
                      ? "Modifier les informations du produit"
                      : "Informations du produit"
                  }}
                </h2>
              </div>
            </div>

            <div class="space-y-2">
              <!-- Informations de base -->
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <BaseInput
                  v-model.trim="formData.reference"
                  label="Référence"
                  label-class="font-bold"
                  placeholder="Référence du produit"
                  required
                  :force-validate="showValidationErrors"
                />
                <BaseInput
                  v-model="formData.marque"
                  label="Marque"
                  label-class="font-bold"
                  type="select"
                  open-on-focus
                  :options="marqueOptions"
                  required
                  :force-validate="showValidationErrors"
                />
                <BaseInput
                  v-model="formData.collection"
                  label="Collection"
                  label-class="font-bold"
                  type="select"
                  open-on-focus
                  :options="collectionOptions"
                  required
                  :force-validate="showValidationErrors"
                />
                <BaseInput
                  v-model="formData.paysOrigine"
                  label="Pays d'origine"
                  label-class="font-bold"
                  type="select"
                  open-on-focus
                  :options="paysOptions"
                  required
                  :force-validate="showValidationErrors"
                />
              </div>

              <!-- Recherche rapide + selects en cascade -->
              <div class="mb-3">
                <CategoryTreeAutocomplete
                  :tree="categoriesTree"
                  :cat-id="formData.categorie"
                  :sous-id="selectedSousCategorie"
                  :sous-sous-id="selectedSousSousCategorie"
                  label="Recherche rapide catégorie"
                  placeholder="Rechercher une catégorie (ex. robe, jean…)"
                  :required="false"
                  @select="onCategoryPathSelect"
                  @clear="onCategoryPathClear"
                />
              </div>

              <!-- Catégories en cascade -->
              <div class="grid grid-cols-1 xl:grid-cols-3 gap-5">
                <BaseInput
                  v-model="formData.categorie"
                  label="Catégories"
                  label-class="font-bold"
                  type="select"
                  open-on-focus
                  :options="categorieOptions"
                  placeholder="Choisir une catégorie"
                  required
                  :force-validate="showValidationErrors"
                  @change="onCategorieSelect"
                />
                <BaseInput
                  v-model="selectedSousCategorie"
                  label="Sous-catégorie"
                  label-class="font-bold"
                  type="select"
                  open-on-focus
                  :options="sousCategorieOptions"
                  placeholder="Choisir une sous-catégorie"
                  :disabled="!formData.categorie"
                  required
                  :force-validate="showValidationErrors"
                  @change="onSousCategorieSelect"
                />
                <BaseInput
                  v-model="selectedSousSousCategorie"
                  label="Sous-sous-catégorie"
                  label-class="font-bold"
                  type="select"
                  open-on-focus
                  :options="sousSousCategorieOptions"
                  placeholder="Choisir une sous-sous-catégorie"
                  :disabled="!selectedSousCategorie"
                  :required="sousSousCategorieOptions.length > 0"
                  :force-validate="showValidationErrors"
                  @change="onSousSousCategorieSelect"
                />
              </div>
            </div>
          </div>

          <!-- Composition + Caractéristiques -->
          <div class="mel-form-card">
            <div class="mel-section-heading">
              <span>2</span>
              <div>
                <h2>Composition et caractéristiques</h2>
              </div>
            </div>
            <!-- Composition -->
            <CompositionForm
              v-model="formCompositions"
              :compositionType="formCompositionType"
              :localisations="formLocalisations"
              :compositionsList="formCompositionsList"
              :autocompleteOptions="compositionAutocompleteOptions"
              :showAutocompleteDropdown="showCompositionDropdown"
              :isLoadingAutocomplete="isLoadingCompositionAutocomplete"
              @composition-input="handleCompositionInput"
              @hide-dropdown="hideCompositionDropdown"
              @select-autocomplete="selectComposition"
            />

            <!-- Caractéristiques -->
            <div
              v-if="formCaracteristiques.length > 0"
              class="mt-4 pt-4 border-t border-gray-200"
            >
              <div
                class="w-full mb-3 -mx-2 px-2 py-2 flex items-center justify-between rounded-md cursor-pointer hover:bg-gray-50"
                role="button"
                tabindex="-1"
                :aria-expanded="isFormCaracteristiquesOpen"
                @click="
                  isFormCaracteristiquesOpen = !isFormCaracteristiquesOpen
                "
                @keydown.enter="
                  isFormCaracteristiquesOpen = !isFormCaracteristiquesOpen
                "
                @keydown.space.prevent="
                  isFormCaracteristiquesOpen = !isFormCaracteristiquesOpen
                "
              >
                <h4 class="text-base font-semibold text-black">
                  Caractéristiques
                  <span class="font-normal text-black">(Optionnel)</span>
                </h4>
                <div class="flex items-center gap-2">
                  <button
                    v-if="formSelectedCaracteristiques.length"
                    type="button"
                    tabindex="-1"
                    class="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 transition-colors hover:border-red-300 hover:bg-red-100"
                    @click.stop="formSelectedCaracteristiques = []"
                  >
                    <i class="fas fa-times" aria-hidden="true"></i>
                    Tout effacer
                  </button>
                  <div class="flex items-center gap-2">
                    <span class="text-xs text-black">
                      Sélectionnées: {{ formSelectedCaracteristiques.length }}/5
                    </span>
                    <i
                      class="fas fa-chevron-down text-gray-400 text-xs transition-transform"
                      :class="isFormCaracteristiquesOpen ? 'rotate-180' : ''"
                    ></i>
                  </div>
                </div>
              </div>
              <p
                v-show="isFormCaracteristiquesOpen"
                class="text-xs text-black italic mt-1"
              >
                Pour mieux vous trouver dans les filtres
              </p>
              <div
                v-show="isFormCaracteristiquesOpen"
                class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2"
              >
                <label
                  v-for="carac in formCaracteristiques"
                  :key="carac.id_caracteristique"
                  class="flex items-center space-x-2 cursor-pointer hover:bg-white px-2 py-1 rounded text-xs border border-gray-200"
                >
                  <input
                    type="checkbox"
                    tabindex="-1"
                    :checked="
                      formSelectedCaracteristiques.includes(
                        carac.id_caracteristique,
                      )
                    "
                    :disabled="
                      !formSelectedCaracteristiques.includes(
                        carac.id_caracteristique,
                      ) && formSelectedCaracteristiques.length >= 5
                    "
                    @change="
                      toggleFormCaracteristique(carac.id_caracteristique)
                    "
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3 h-3 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <span
                    class="text-xs text-black break-words"
                    :class="{
                      'opacity-50':
                        !formSelectedCaracteristiques.includes(
                          carac.id_caracteristique,
                        ) && formSelectedCaracteristiques.length >= 5,
                    }"
                    >{{ carac.libelle }}</span
                  >
                </label>
              </div>
              <p
                v-show="isFormCaracteristiquesOpen"
                class="text-xs text-black mt-2"
              >
                Maximum 5 caractéristiques
              </p>
            </div>
          </div>

          <!-- Vente, collection, prix et reste du formulaire -->
          <div class="mel-form-card">
            <div class="mel-section-heading">
              <span>3</span>
              <div>
                <h2>Vente et tarification</h2>
              </div>
            </div>
            <div class="space-y-4">
              <!-- Mode de vente et poids -->
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <BaseInput
                  v-model="formData.venduPar"
                  label="Vendu par"
                  label-class="font-bold"
                  type="select"
                  open-on-focus
                  :options="venduParOptions"
                />
                <div>
                  <BaseInput
                    v-model="formData.poids"
                    label="Poids unitaire (kg)"
                    label-class="font-bold"
                    type="number"
                    placeholder="Poids (kg)"
                    required
                    step="0.001"
                    min="0.001"
                    :max="MAX_MEL_UNIT_WEIGHT_KG"
                    :force-validate="showValidationErrors"
                  />
                  <p
                    v-if="isUnitWeightOverMax"
                    class="text-xs font-semibold text-red-600 mt-1"
                  >
                    {{ MAX_MEL_UNIT_WEIGHT_ERROR }}
                  </p>
                  <p
                    v-else-if="isUnitWeightOver2kg"
                    class="text-xs font-semibold text-orange-600 mt-1"
                  >
                    Le poids saisi semble élevé (> 2 kg). Vérifiez qu’il
                    correspond bien au produit.
                  </p>
                </div>
              </div>

              <!-- Taille, quantité, prix et remises -->
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div class="grid grid-cols-[1fr_auto] items-end gap-2">
                  <div>
                    <BaseInput
                      v-model="formData.taillePaquet"
                      label="Taille du paquet"
                      label-class="font-bold"
                      type="select"
                      open-on-focus
                      :options="tailleOptions"
                      required
                      :force-validate="showValidationErrors"
                    />
                  </div>
                  <div class="flex justify-end">
                    <button
                      type="button"
                      tabindex="-1"
                      @click="router.push('/catalog/sizerange?from=mel')"
                      class="mb-2 inline-flex items-center justify-center self-end w-8 h-8 rounded-full border border-blue-500 bg-white text-blue-600 shadow-sm hover:bg-blue-50 hover:scale-105 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-offset-1 transition-transform transition-colors duration-150"
                      title="Gérer les gammes de tailles"
                    >
                      <i class="fas fa-plus text-xs"></i>
                    </button>
                  </div>
                </div>
                <div
                  v-if="
                    formData.venduPar !== 'tailles' && formData.taillePaquet
                  "
                  class="grid grid-cols-[1fr_auto] items-end gap-2"
                >
                  <div>
                    <BaseInput
                      v-model="formData.quantitePaquet"
                      label="Quantité du paquet"
                      label-class="font-bold"
                      type="select"
                      open-on-focus
                      :options="quantiteOptions"
                      required
                      :force-validate="showValidationErrors"
                    />
                  </div>
                  <div class="flex justify-end">
                    <button
                      type="button"
                      tabindex="-1"
                      @click="router.push('/catalog/packagemanagement?from=mel')"
                      class="mb-2 inline-flex items-center justify-center self-end w-8 h-8 rounded-full border border-blue-500 bg-white text-blue-600 shadow-sm hover:bg-blue-50 hover:scale-105 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-offset-1 transition-transform transition-colors duration-150"
                      title="Gérer les packs"
                    >
                      <i class="fas fa-plus text-xs"></i>
                    </button>
                  </div>
                </div>

                <!-- Prix normal -->
                <BaseInput
                  v-model="formData.prix"
                  label="Prix HT"
                  label-class="font-bold"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  required
                  :force-validate="showValidationErrors"
                />

                <BaseInput
                  v-model="formData.prixReduit"
                  label="Prix réduit HT"
                  label-class="font-bold"
                  type="number"
                  skip-tab
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  :max="
                    formData.prix
                      ? (parseFloat(formData.prix) - 0.01).toFixed(2)
                      : undefined
                  "
                />

                <BaseInput
                  v-model="formData.pourcentageRemise"
                  label="Pourcentage de remise (%)"
                  label-class="font-bold"
                  type="number"
                  skip-tab
                  min="0"
                  max="100"
                  step="1"
                  placeholder="0"
                />

                <!-- Minimum de commande : affiché seulement si venduPar = "tailles" -->
                <div v-if="shouldShowMinimumCommande">
                  <BaseInput
                    v-model="formData.minimumCommande"
                    label="Minimum de commande (Facultatif)"
                    type="number"
                    placeholder="Nombre minimum"
                  />
                  <p class="text-xs text-black mt-0.5">
                    Nombre min. de pièces à commander
                  </p>
                </div>
              </div>

              <!-- Avertissement si conflit entre prix réduit manuel et remise en pourcentage -->
              <div
                v-if="hasPriceRemiseConflict"
                class="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
              >
                <div class="flex items-start gap-2">
                  <span class="text-yellow-600 text-lg">⚠️</span>
                  <div class="flex-1">
                    <p class="text-sm font-medium text-yellow-800">
                      Attention : Conflit détecté
                    </p>
                    <p class="text-xs text-yellow-700 mt-1">
                      Vous avez saisi un prix réduit manuel ({{
                        formData.prixReduit
                      }}€) ET une remise en pourcentage ({{
                        formData.pourcentageRemise
                      }}%).
                      <br />
                      <strong>Le prix réduit manuel sera conservé</strong> et la
                      remise en pourcentage ne sera pas appliquée automatiquement.
                      <br />
                      Si vous souhaitez utiliser la remise en pourcentage, laissez
                      le champ "Prix réduit HT" vide.
                    </p>
                  </div>
                </div>
              </div>

              <div v-if="shouldShowDimensions" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <BaseInput
                    v-model="formData.dimensions"
                    :label="isShoeProduct ? 'Dimensions du colis (cm, facultatif)' : 'Dimensions (Facultatif)'"
                    :force-validate="showValidationErrors"
                  />
                  <p class="text-xs text-black mt-0.5">
                    Longueur × largeur × hauteur
                  </p>
                  <div
                    v-if="formData.dimensions && !isDimensionsFormatValid"
                    class="text-xs text-orange-600 mt-1"
                  >
                    ⚠️ Format attendu : longueur × largeur × hauteur
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Couleurs, Descriptions et Boutons -->
          <div class="mel-form-card">
            <div class="mel-section-heading">
              <span>4</span>
              <div>
                <h2>Couleurs et descriptions</h2>
              </div>
            </div>
            <div class="space-y-4">
              <!-- Couleurs avec sélection multiple -->
              <CouleursSelector
                ref="couleursSelectorRef"
                v-model="formCouleurs"
                :couleursList="couleursList"
                :taillesList="formData.venduPar === 'melangees' ? taillesListForMelangees : []"
                :force-validate="showValidationErrors"
                @selection-complete="handleColorSelectionComplete"
              />

              <!-- Message informatif pour les couleurs mélangées -->
              <div
                v-if="formData.venduPar === 'melangees'"
                class="mt-2 p-3 bg-blue-50 border-l-4 border-blue-500 rounded-r"
              >
                <div
                  v-if="!formData.taillePaquet"
                  class="text-sm text-blue-700"
                >
                  ⚠️ Veuillez sélectionner la taille du paquet et ajouter
                  d'autres couleurs
                </div>
                <div
                  v-else-if="formCouleurs.length <= 1"
                  class="text-sm text-blue-700"
                >
                  ⚠️ Veuillez ajouter d'autres couleurs
                </div>
                <div v-else class="text-xs text-blue-600">
                  💡 Pour chaque couleur, spécifiez la quantité par taille (ex:
                  1 Rouge en S, 2 en M, 1 en L)
                </div>
              </div>

              <!-- Gérer le stock -->
              <div
                v-if="(formData.venduPar === 'couleurs' || formData.venduPar === 'tailles' || formData.venduPar === 'melangees') && formData.taillePaquet && formCouleurs.length > 0"
                class="mt-2"
              >
                <button
                  type="button"
                  @click="openStockModal"
                  class="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-white border border-blue-500 rounded-md hover:bg-blue-50"
                >
                  <i class="fas fa-boxes mr-2"></i>
                  Gérer le stock
                </button>
              </div>

              <!-- Descriptions + AI Generate -->
              <div class="space-y-4">
                <div class="flex items-center justify-between">
                  <label class="block text-sm font-bold text-black">Descriptions ( Détection automatique )</label>
                  <button
                    type="button"
                    @click="autoTranslateMel"
                    :disabled="isTranslatingMel"
                    class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg v-if="isTranslatingMel" class="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    <span v-else>🤖</span>
                    {{ isTranslatingMel ? 'Translating...' : 'AI Traduction' }}
                  </button>
                </div>
                <textarea
                  ref="descriptionInputMelRef"
                  v-model="descriptionInputMel"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm text-black placeholder-gray-400"
                  rows="3"
                  placeholder="Entrez la description, puis cliquez AI Traduction"
                ></textarea>
                <div class="text-right text-xs mt-1" :class="descriptionInputMel.length > 3000 ? 'text-red-500 font-semibold' : 'text-gray-400'">
                  {{ descriptionInputMel.length }} / 3000
                </div>
                <div v-if="descriptionInputMel.length > 3000" class="text-red-500 text-xs font-medium mt-1">
                  ⚠️ La description dépasse 3000 caractères. Veuillez raccourcir votre texte.
                </div>
                <div
                  v-if="formData.descriptionFr || formData.descriptionEn || formData.descriptionIt || formData.descriptionEs || formData.descriptionDe"
                  class="grid grid-cols-1 md:grid-cols-2 gap-4"
                >
                  <div>
                    <label class="block text-sm font-medium text-gray-600 mb-1">Français</label>
                    <textarea v-model="formData.descriptionFr" class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-900" rows="3"></textarea>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-600 mb-1">English</label>
                    <textarea v-model="formData.descriptionEn" class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-900" rows="3"></textarea>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-600 mb-1">Italiano</label>
                    <textarea v-model="formData.descriptionIt" class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-900" rows="3"></textarea>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-600 mb-1">Español</label>
                    <textarea v-model="formData.descriptionEs" class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-900" rows="3"></textarea>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-600 mb-1">Deutsch</label>
                    <textarea v-model="formData.descriptionDe" class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-900" rows="3"></textarea>
                  </div>
                </div>
              </div>

              <!-- Boutons d'action -->
              <div class="pt-4 border-t border-gray-200">
                <div class="mel-actions flex flex-col sm:flex-row gap-3 justify-end">
                  <ActionButton
                    :label="
                      editingReferenceId
                        ? 'Mettre à jour la référence'
                        : '+ Enregistrer et ajouter une référence'
                    "
                    :variant="editingReferenceId ? 'success' : 'primary'"
                    type="submit"
                    :loading="isSubmitting"
                    :disabled="isSubmitting"
                    class="w-full sm:w-auto"
                  />
                  <ActionButton
                    :label="
                      editingReferenceId
                        ? 'Annuler la modification'
                        : 'Réinitialiser le formulaire'
                    "
                    variant="warning"
                    @click="editingReferenceId ? cancelEdit() : resetForm()"
                    class="w-full sm:w-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>

      </div>

      <!-- Onglet Fichier Excel -->
      <div v-if="activeTab === 'excel'">
        <MelExcelUploadZone
          class="mel-excel"
          :selectedFile="selectedExcelFile"
          :isAnalyzing="isAnalyzingExcel"
          :isDownloadingTemplate="isDownloadingTemplate"
          @file-selected="handleExcelFileSelected"
          @analyze="analyzeExcelFile"
          @download-template="downloadExcelTemplate"
        />

        <div class="mt-3">
          <div v-if="excelAnalysisErrors.length > 0" class="bg-red-50 border border-red-200 rounded-lg mb-4">
            <button 
              @click="showErrors = !showErrors"
              type="button"
              class="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-red-100 transition-colors rounded-lg"
            >
              <span class="font-medium text-red-800">
                <i class="fas fa-exclamation-circle mr-2"></i>
                Erreurs ({{ excelAnalysisErrors.length }})
              </span>
              <i :class="['fas transition-transform', showErrors ? 'fa-chevron-up' : 'fa-chevron-down']" class="text-red-600"></i>
            </button>
            <div v-show="showErrors" class="px-4 pb-4">
              <ul class="list-disc list-inside text-sm text-red-700 space-y-1">
                <li v-for="(error, idx) in excelAnalysisErrors" :key="idx">{{ error }}</li>
              </ul>
            </div>
          </div>
          
          <!-- Avertissements (non bloquants) - Dépliable -->
          <div v-if="excelAnalysisWarnings.length > 0" class="bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
            <button 
              @click="showWarnings = !showWarnings"
              type="button"
              class="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-yellow-100 transition-colors rounded-lg"
            >
              <span class="font-medium text-yellow-800">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                Avertissements ({{ excelAnalysisWarnings.length }})
              </span>
              <i :class="['fas transition-transform', showWarnings ? 'fa-chevron-up' : 'fa-chevron-down']" class="text-yellow-600"></i>
            </button>
            <div v-show="showWarnings" class="px-4 pb-4">
              <ul class="list-disc list-inside text-sm text-yellow-700 space-y-1">
                <li v-for="(warning, idx) in excelAnalysisWarnings" :key="idx">{{ warning }}</li>
              </ul>
            </div>
          </div>
        </div>
        <!-- <MelExcelAnalysisResults
          :results="excelAnalysisResults"
          :errors="excelAnalysisErrors"
          :warnings="excelAnalysisWarnings"
          :lookupData="{
            marques,
            categories,
            collections,
            provenances,
          }"
        /> -->
      </div>


      <!-- Liste des références à valider (uniquement pour l'onglet Formulaire) -->
      <MelReferencesTable
        class="mel-references"
        ref="referencesTableRef"
        :references="references"
        source="form"
        :lookupData="referencesTableLookupData"
        :loading="isSubmitting"
        :primary-action-label="melOptions.button1Label"
        :show-primary-action="melOptions.button1Available"
        :shooting-only="isShootingRequest"
        @remove="confirmRemoveReference"
        @confirm="confirmReferencesUpload"
        @duplicate="prefillFromReference"
        @clearAll="clearAllDrafts"
        @edit="handleEditReference"
      />
    </div>

    <!-- Modal Stock en paquet (couleurs / tailles) -->
    <ModalComp
      :isVisible="showStockModal"
      :title="formData.venduPar === 'tailles' ? 'Stock par taille' : 'Stock en paquet (optionnel)'"
      size="lg"
      hideFooter
      @update:isVisible="showStockModal = $event"
    >
      <template #body>
        <div
          v-if="formCouleurs.length > 0"
          class="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3"
        >
          <label
            for="stock-rapide"
            class="mb-2 block text-sm font-semibold text-black"
          >
            Stock rapide — appliquer à toutes les cases
          </label>
          <div class="flex flex-wrap items-center gap-2">
            <input
              id="stock-rapide"
              ref="stockBulkInputRef"
              v-model.number="stockBulkValue"
              type="number"
              min="0"
              class="w-28 rounded-md border border-gray-300 px-3 py-2 text-center text-sm text-black focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              aria-label="Stock à appliquer à toutes les cases"
              @keydown.enter.prevent="applyStockToAll"
            />
            <button
              type="button"
              class="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              @click="applyStockToAll"
            >
              Appliquer partout
            </button>
            <span class="text-xs text-blue-700">
              Remplit toutes les couleurs<span v-if="formData.venduPar === 'tailles'"> et toutes les tailles</span>.
            </span>
          </div>
        </div>

        <!-- Pour couleurs : stock par couleur -->
        <div v-if="formData.venduPar === 'couleurs' && formCouleurs.length > 0" class="space-y-2">
          <div
            v-for="c in formCouleurs"
            :key="c.id"
            class="p-3 border border-gray-200 rounded-md bg-gray-50 flex items-center gap-3"
          >
            <span
              class="inline-block w-3 h-3 rounded-full border border-gray-300 flex-shrink-0"
              :style="{ backgroundColor: (c as any).hexColor || '#CCCCCC' }"
            ></span>
            <span class="text-sm font-medium text-black w-28 truncate">{{ c.nom }}</span>
            <input
              :value="(c as any).stock != null ? String((c as any).stock) : ''"
              @input="(e) => { (c as any).stock = (e.target as HTMLInputElement).value === '' ? null : Number((e.target as HTMLInputElement).value) }"
              type="number"
              min="0"
              class="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-center text-black"
              placeholder="0"
            />
          </div>
        </div>

        <!-- Pour couleurs mélangées : stock global en paquet -->
        <div v-else-if="formData.venduPar === 'melangees' && formCouleurs.length > 0" class="space-y-3">
          <p class="text-sm text-gray-600 mb-2">Stock total du paquet (toutes couleurs confondues)</p>
          <div class="p-3 border border-gray-200 rounded-md bg-gray-50 flex items-center gap-3">
            <span class="text-sm font-medium text-black">Stock en paquet</span>
            <input
              :value="formData.stock ? String(formData.stock) : ''"
              @input="(e) => { formData.stock = (e.target as HTMLInputElement).value === '' ? null : Number((e.target as HTMLInputElement).value) }"
              type="number"
              min="0"
              class="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-center text-black"
              placeholder="0"
            />
          </div>
        </div>

        <!-- Pour tailles : stock par couleur × taille -->
        <div v-else-if="formData.venduPar === 'tailles' && formCouleurs.length > 0" class="space-y-3">
          <div
            v-for="c in formCouleurs"
            :key="c.id"
            class="flex items-center gap-3 px-3 py-2 border border-gray-200 rounded-md bg-gray-50"
          >
            <div class="flex items-center gap-2 flex-shrink-0 w-28">
              <span
                class="inline-block w-3 h-3 rounded-full border border-gray-300 flex-shrink-0"
                :style="{ backgroundColor: (c as any).hexColor || '#CCCCCC' }"
              ></span>
              <span class="text-sm font-medium text-black truncate">{{ c.nom }}</span>
            </div>
            <div class="flex flex-wrap gap-2">
              <div
                v-for="(ts, idx) in (c as any).tailleStocks"
                :key="ts.taille"
                class="flex items-center gap-1"
              >
                <span class="text-xs text-gray-600">{{ ts.taille }}</span>
                <input
                  :value="ts.quantity != null ? String(ts.quantity) : ''"
                  @input="(e) => { (c as any).tailleStocks[idx].quantity = (e.target as HTMLInputElement).value === '' ? null : Number((e.target as HTMLInputElement).value) }"
                  type="number"
                  min="0"
                  class="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-center text-black"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </div>

        <div v-else class="text-sm text-gray-500 text-center py-4">
          Aucune couleur sélectionnée.
        </div>

        <div class="flex justify-end mt-6">
          <button
            type="button"
            @click="showStockModal = false"
            class="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
          >
            Confirmer
          </button>
        </div>
      </template>
    </ModalComp>

    <!-- Choix de la prestation de shooting -->
    <ModalComp
      :isVisible="showMelConfirmModal"
      title="Choisir le type de shooting"
      size="lg"
      hideFooter
      @update:isVisible="showMelConfirmModal = $event"
    >
      <template #body>
        <div class="space-y-4 mb-10 pr-2 pl-2">
          <div v-if="showPrestationChoice">
            <h5 class="font-semibold text-black mb-3">
              Sélectionner une option
            </h5>

            <div class="space-y-3">
              <!-- Cintre gratuit -->
              <label
                class="flex items-start p-3 border rounded-lg"
                :class="{
                  'bg-blue-50 border-blue-500 cursor-pointer hover:bg-gray-50':
                    selectedPrestation === 'cintre' && effectiveReferenceCount <= 60,
                  'bg-gray-100 border-gray-400 opacity-60 cursor-not-allowed':
                    effectiveReferenceCount > 60,
                  'cursor-pointer hover:bg-gray-50': effectiveReferenceCount <= 60,
                }"
              >
                <input
                  type="radio"
                  name="prestation"
                  value="cintre"
                  v-model="selectedPrestation"
                  :disabled="effectiveReferenceCount > 60"
                  class="mt-1 mr-3"
                />
                <div class="flex-1">
                  <div class="font-semibold text-black mb-1">
                    Cintre : GRATUIT
                    <span
                      v-if="effectiveReferenceCount > 60"
                      class="ml-2 text-xs bg-red-500 text-white px-2 py-0.5 rounded"
                    >
                      Non disponible
                    </span>
                  </div>
                  <p class="text-sm text-black">
                    Vos produits seront shootés par notre équipe dans nos
                    studios. Les photos seront mises en ligne le lendemain du
                    shooting.
                  </p>
                  <p
                    v-if="effectiveReferenceCount > 60"
                    class="text-xs text-red-600 mt-2"
                  >
                    ⚠️ Cette option gratuite est limitée à 60 références maximum
                    par demande. Vous avez {{ effectiveReferenceCount }} références.
                    Veuillez choisir une prestation payante ou réduire le nombre
                    de références.
                  </p>
                </div>
              </label>

              <!-- Shooting exclusif Efashion -->
              <label
                class="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                :class="{
                  'bg-blue-50 border-blue-500':
                    selectedPrestation === 'efashion',
                }"
              >
                <input
                  type="radio"
                  name="prestation"
                  value="efashion"
                  v-model="selectedPrestation"
                  class="mt-1 mr-3"
                />
                <div class="flex-1">
                  <div class="font-semibold text-black mb-1">
                    Exclusive Efashion : 4.00€
                  </div>
                  <p class="text-sm text-black">
                    *4.00€ HT la couleur par référence photos, prise et mise en
                    ligne dans la semaine suivant la demande avec droit de
                    diffusion sur Efashion uniquement (site Ecommerce b2b)
                  </p>
                </div>
              </label>

              <!-- Shooting libre utilisation -->
              <label
                class="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                :class="{
                  'bg-blue-50 border-blue-500':
                    selectedPrestation === 'libredroit',
                }"
              >
                <input
                  type="radio"
                  name="prestation"
                  value="libredroit"
                  v-model="selectedPrestation"
                  class="mt-1 mr-3"
                />
                <div class="flex-1">
                  <div class="font-semibold text-black mb-1">
                    Libre utilisation : 6.00€
                  </div>
                  <p class="text-sm text-black mb-2">
                    *6.00€ HT la couleur par référence photos, prise et mise en
                    ligne dans la semaine suivant la demande. Après une semaine
                    d'exclusivité sur le site Efashion, un lien de
                    téléchargement sera disponible sur la ligne de la demande.
                    <strong
                      >Pendant cette semaine, il n'est pas possible d'utiliser
                      les photos sur d'autres plateformes. Si cela arrive, des
                      sanctions pourront être appliquées.</strong
                    >
                  </p>
                </div>
              </label>
            </div>

            <!-- Checkbox toutes les couleurs -->
            <div class="mt-4 p-3 bg-gray-50 rounded-lg">
              <label class="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  v-model="shootAllColors"
                  class="mt-1 mr-3 rounded"
                />
                <div class="flex-1">
                  <span class="text-sm text-black">
                    <strong>Toutes les couleurs</strong> (si coché, chaque
                    couleur sera photographiée séparément. Sinon, seule la
                    couleur principale sera photographiée)
                  </span>
                  <p class="text-xs text-black mt-1 italic">
                    💰 Attention : Pour les prestations payantes (Exclusive
                    Efashion / Libre utilisation), le prix est multiplié par le
                    nombre de couleurs shootées.
                  </p>
                </div>
              </label>
            </div>

            <!-- Message droits d'image -->
            <div
              class="mt-4 text-xs text-black italic p-3 bg-yellow-50 border border-yellow-200 rounded"
            >
              <strong>Concernant les droits d'image des mannequins</strong>,
              nous vous informons que les photos sont uniquement destinées à des
              fins commerciales. Efashion se réserve le droit d'interdire toute
              utilisation ne respectant pas l'intégrité de la mannequin.
            </div>
          </div>

          <!-- Boutons d'action -->
          <div class="mt-6 pt-4 border-t">
            <div class="flex justify-end">
              <ActionButton
                label="Confirmer"
                variant="primary"
                :disabled="
                  isSubmitting ||
                  !selectedPrestation ||
                  (selectedPrestation === 'cintre' && effectiveReferenceCount > 60)
                "
                :loading="isSubmitting"
                customClass="w-full sm:w-auto"
                @click="confirmMelChoice"
              />
            </div>
          </div>
        </div>
      </template>
    </ModalComp>

    <!-- Modal Champs manquants -->
    <ModalComp
      :isVisible="showMissingFieldsModal"
      title="Champs obligatoires manquants"
      size="md"
      @update:isVisible="showMissingFieldsModal = $event"
    >
      <template #body>
        <div class="space-y-3">
          <p class="text-sm text-black">
            Veuillez compléter les champs suivants avant d'ajouter la référence
            :
          </p>
          <ul class="list-disc pl-5 text-sm text-red-700">
            <li v-for="item in missingFields" :key="item">{{ item }}</li>
          </ul>
        </div>
      </template>
      <template #footer>
        <button
          @click="showMissingFieldsModal = false"
          class="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          type="button"
        >
          Confirmer
        </button>
      </template>
    </ModalComp>

    <!-- Modal Confirmation suppression référence -->
    <ModalComp
      :isVisible="showDeleteRefModal"
      title="Confirmer la suppression"
      size="sm"
      @update:isVisible="showDeleteRefModal = $event"
    >
      <template #body>
        <div class="space-y-3">
          <p class="text-sm text-black">
            Êtes-vous sûr de vouloir supprimer cette référence ?
          </p>
          <p class="text-xs text-black">Cette action est irréversible.</p>
        </div>
      </template>
      <template #footer>
        <div class="flex justify-end gap-3">
          <ActionButton
            label="Annuler"
            variant="secondary"
            @click="cancelRemoveReference"
          />
          <ActionButton
            label="Supprimer"
            variant="danger"
            @click="removeReference"
          />
        </div>
      </template>
    </ModalComp>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  reactive,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from "vue";
import { storeToRefs } from "pinia";
import { useRoute, useRouter } from "vue-router";
import {
  ActionButton,
  BaseInput,
  ModalComp,
  CouleursSelector,
  CompositionForm,
  MelReferencesTable,
  MelExcelUploadZone,
  MelExcelAnalysisResults,
  CategoryTreeAutocomplete,
} from "@/components";
import type { CategoryPathSelection } from "@/types/categorie";
import type {
  MelFormData,
  MelReference,
  ExcelAnalysisRow,
  Caracteristique,
} from "@/types/mel";
import { useNotification } from "@/composables/useNotification";
import {
  analyzeExcelFile as analyzeExcelAPI,
  getCaracteristiques,
  getReferenceData,
  getCompositionAutocomplete,
  saveMelChoice,
  saveMelDraft,
  getMelDraft,
  updateMelDraft,
  deleteMelDraft,
  deleteMelDraftByReference,
  deleteAllMelDrafts,
  checkReferencesExistsBatch,
} from "@/api/shooting";
import { isValidProductDimensions } from "@/utils/productDimensions";
import { getPackLength } from "@/utils/packLength";
import {
  formatDeclinaisonLabel,
  getDeclinaisonLength,
} from "@/utils/declinaisonLabel";
import {
  getRecentCompositions,
  rememberComposition,
} from "@/utils/melCompositionHistory";
import {
  MAX_MEL_UNIT_WEIGHT_KG,
  MAX_MEL_UNIT_WEIGHT_ERROR,
  WARN_MEL_UNIT_WEIGHT_KG,
  getMelUnitWeightError,
  isMelUnitWeightAllowed,
} from "@/utils/melCompositions";
import { getCouleursOfficielles } from "@/api/couleurs";
import { getCouleursByVendeur, getProduitCouleurs } from "@/api/produits";
import { getCategoriesTree } from "@/api/categories";
import {
  staticCollectionsLibelles,
  staticCompositionLibelles,
  staticCouleursDefaut,
  staticMemoCaracDetailedRows,
  staticMemoCaracFlatLibelles,
} from "@/data/melTemplateStaticData";
import { getMelTemplateData, buildMelDataFromPayload } from "@/api/melTemplate";
import type { CategorieNode } from "@/types/categorie";
import { useMelStore } from "@/stores/melStore";
import { useMelUiStore } from "@/stores/melUi";
import { useAuthStore } from "@/stores/auth";
import type { ProduitCatalogue } from "@/types";
import {
  getProduitDescription,
  getProduitCaracteristiqueIds,
} from "@/api/produits";
import * as XLSX from "xlsx-js-style";
import { fetchRest, getApiBaseUrl } from "@/config/api";

// Notifications
const { success, error, warning, modifier } = useNotification();

// Router
const router = useRouter();
const route = useRoute();
const isShootingRequest = computed(
  () => route.name === "catalog/shooting-request",
);

// Date d'aujourd'hui pour la validation des dates
const todayDate = new Date().toISOString().split("T")[0];

// Stores
const melStore = useMelStore();
const authStore = useAuthStore();
const vendeurId = authStore.user?.id;

// État de chargement des données
const isLoadingReferenceData = ref(false);

const showErrors    = ref(false)
const showWarnings  = ref(false)

// ===== COMPOSANT VUE =====
// UI State (persisted via Pinia + localStorage)
const melUiStore = useMelUiStore();
const { activeTab, melDataSource, selectedMelOption, selectedPrestation } =
  storeToRefs(melUiStore);
const isSubmitting = ref(false);
const showStockModal = ref(false);
const stockBulkValue = ref<number | string>(100);
const stockBulkInputRef = ref<HTMLInputElement | null>(null);

const showMelConfirmModal = ref(false);
const showPrestationChoice = ref(false);
const referencesTableRef = ref<HTMLElement | null>(null);
const formTopAnchorRef = ref<HTMLElement | null>(null);
const descriptionInputMelRef = ref<HTMLTextAreaElement | null>(null);
const couleursSelectorRef = ref<{
  focusFirstQuantity: () => boolean;
} | null>(null);
const shootAllColors = ref(false);

const forceFormMethodOnCompactViewport = () => {
  if (window.innerWidth <= 1024 && activeTab.value !== "form") {
    activeTab.value = "form";
  }
};

const selectedSousCategorie = ref("");
const selectedSousSousCategorie = ref("");
// Modal pour lister les champs manquants
const showMissingFieldsModal = ref(false);
const showValidationErrors = ref(false);
const melFieldTouched = reactive({
  categorie: false,
  sousCategorie: false,
  sousSousCategorie: false,
  couleurs: false,
});
// Modal pour confirmer la suppression d'une référence
const showDeleteRefModal = ref(false);
const refToDelete = ref<string | null>(null);
const missingFields = ref<string[]>([]);

// ID de la référence en cours d'édition (mode édition)
const editingReferenceId = ref<string | null>(null);

// Caractéristiques pour le formulaire en ligne
const formCaracteristiques = ref<
  Array<{ id_caracteristique: number; libelle: string }>
>([]);
const formSelectedCaracteristiques = ref<number[]>([]);
const isLoadingFormCaracteristiques = ref(false);
const isFormCaracteristiquesOpen = ref(false);

// Composition pour le formulaire en ligne
const formCompositionType = ref<"autres" | "chaussures" | null>(null);
const formLocalisations = ref<
  Array<{ id_composition_localisation: number; libelle: string }>
>([]);
const formCompositionsList = ref<
  Array<{ id_composition: number; libelle: string }>
>([]);
const formCompositions = ref<
  Array<{
    localisationId: number;
    compositionId: number;
    compositionName: string;
    percentage: number | "";
  }>
>([]);
const selectedLocalisation = ref("");
const newCompositionText = ref("");

// Variables pour l'autocomplete des compositions
const compositionAutocompleteOptions = ref<
  Array<{ id: number; label: string }>
>([]);
const showCompositionDropdown = ref(false);
const isLoadingCompositionAutocomplete = ref(false);
const isPrefillingFromProduct = ref(false);

// Persist UI changes to localStorage when these refs change programmatically
watch(activeTab, () => melUiStore.persistNow());
watch(melDataSource, () => melUiStore.persistNow());
watch(selectedMelOption, () => melUiStore.persistNow());
watch(selectedPrestation, () => melUiStore.persistNow());

// Form data
const formData = reactive<MelFormData>({
  reference: "",
  marque: "",
  poids: "",
  categorie: "",
  venduPar: "couleurs",
  collection: "",
  paysOrigine: "",
  taillePaquet: "", // ID de la déclinaison (gamme de tailles)
  quantitePaquet: "", // ID du pack de quantités
  stock: "",
  prix: "",
  prixReduit: "",
  dateRemise: "",
  pourcentageRemise: "",
  dimensions: "",
  minimumCommande: "",
  descriptionFr: "",
  descriptionEn: "",
  descriptionIt: "",
  descriptionEs: "",
  descriptionDe: "",
  descriptionZh: "",
});

const unitWeightValue = computed(() =>
  Number(String(formData.poids ?? "").replace(",", ".")),
);
const isUnitWeightOverMax = computed(
  () =>
    Number.isFinite(unitWeightValue.value) &&
    unitWeightValue.value > MAX_MEL_UNIT_WEIGHT_KG,
);
const isUnitWeightOver2kg = computed(
  () =>
    Number.isFinite(unitWeightValue.value) &&
    unitWeightValue.value > WARN_MEL_UNIT_WEIGHT_KG &&
    unitWeightValue.value <= MAX_MEL_UNIT_WEIGHT_KG,
);

const isTranslatingMel = ref(false);
const descriptionInputMel = ref("");

// Détecte la présence d'emojis / pictogrammes dans un texte
const EMOJI_REGEX =
  /([\u{1F000}-\u{1FAFF}]|[\u{2600}-\u{27BF}]|[\u{2300}-\u{23FF}]|[\u{2B00}-\u{2BFF}]|\u200D|\uFE0F)/u;

const containsEmoji = (text?: string | null): boolean =>
  !!text && EMOJI_REGEX.test(text);

const MEL_LANG_MAP: { key: string; lang: string }[] = [
  { key: "descriptionFr", lang: "francais" },
  { key: "descriptionEn", lang: "anglais" },
  { key: "descriptionIt", lang: "italien" },
  { key: "descriptionEs", lang: "espagnol" },
  { key: "descriptionDe", lang: "allemand" },
];

async function autoTranslateMel() {
  const text = descriptionInputMel.value?.trim();
  if (!text) {
    error("Veuillez saisir une description.");
    return;
  }
  if (descriptionInputMel.value.length > 3000) {
    error("La description dépasse 3000 caractères. Veuillez raccourcir votre texte.");
    return;
  }
  if (containsEmoji(text)) {
    error(
      "La description ne doit pas contenir d'emojis : ils ne s'affichent pas correctement sur la fiche produit et perturbent la traduction automatique.",
    );
    return;
  }
  isTranslatingMel.value = true;
  try {
    const API_URL = getApiBaseUrl();
    const detectRes = await fetchRest(`${API_URL}/translate/detect`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
    });
    const detectData = await detectRes.json();
    const detectedLang = detectData.language || "francais";

    const targetLangs = MEL_LANG_MAP
      .filter((e) => e.lang !== detectedLang)
      .map((e) => e.lang);

    const response = await fetchRest(`${API_URL}/translate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, sourceLang: detectedLang, targetLangs }),
    });
    if (!response.ok) throw new Error("Translation failed");
    const data = await response.json();

    for (const entry of MEL_LANG_MAP) {
      if (entry.lang === detectedLang) {
        (formData as any)[entry.key] = text;
      } else if (data[entry.lang]) {
        (formData as any)[entry.key] = data[entry.lang];
      }
    }
  } catch (err) {
    console.error("Translation error:", err);
    error("Erreur lors de la traduction. Veuillez réessayer.");
  } finally {
    isTranslatingMel.value = false;
  }
}

// Data - Les références sont maintenant gérées par le store
const localReferences = computed(() => melStore.references);

// Produits en brouillon chargés depuis la base de données (ajoutés via l'ancienne interface PHP)
const draftProducts = computed(() => melStore.draftProducts);

// Transformer les draftProducts (format DB) en MelReference (format UI)
// Pour venduPar="couleurs" ou "tailles", les produits sont groupés par reference_base ou id_couleur_liee
const draftProductsAsReferences = computed((): MelReference[] => {
  // Grouper les produits par reference_base ou id_couleur_liee pour venduPar="couleurs" ou "tailles"
  const groupedByRefBase: Record<string, any[]> = {};
  const standaloneProducts: any[] = [];

  for (const p of draftProducts.value) {
    const venduPar = p.vendu_par || "couleurs";
    // Pour venduPar="couleurs" ou "tailles", grouper par reference_base ou id_couleur_liee
    if (venduPar === "couleurs" || venduPar === "tailles") {
      // Utiliser reference_base si disponible, sinon id_couleur_liee comme clé de groupement
      let groupKey: string | null = null;
      if (p.reference_base) {
        const decliKey = p.id_declinaison ? `_decli${p.id_declinaison}` : '';
        groupKey = `ref_${p.reference_base.trim().toLowerCase()}_${venduPar}${decliKey}`;
      } else if (p.id_couleur_liee) {
        const decliKey = p.id_declinaison ? `_decli${p.id_declinaison}` : '';
        groupKey = `liee_${p.id_couleur_liee}_${venduPar}${decliKey}`;
      }

      if (groupKey) {
        if (!groupedByRefBase[groupKey]) {
          groupedByRefBase[groupKey] = [];
        }
        groupedByRefBase[groupKey].push(p);
      } else {
        // Pas de clé de groupement, traiter individuellement
        standaloneProducts.push(p);
      }
    } else {
      // Pour les autres modes (melangees), traiter individuellement
      standaloneProducts.push(p);
    }
  }

  const result: MelReference[] = [];

  // Traiter les produits groupés par reference_base (venduPar="couleurs")
  for (const refBase of Object.keys(groupedByRefBase)) {
    const products = groupedByRefBase[refBase];
    // Utiliser le premier produit (main) comme base
    const mainProduct = products.find((p: any) => p.main) || products[0];
    const remiseSource =
      products.find((p: any) =>
        !!normalizeDateForDateInput(
          p?.dateDebutRemise ?? p?.date_debut_remise ?? p?.dateRemise ?? p?.date_remise ?? "",
        ),
      ) || mainProduct;

    // Collecter toutes les couleurs de tous les produits du groupe
    const allCouleurs: any[] = [];
    for (const p of products) {
      if (p.couleurs && Array.isArray(p.couleurs)) {
        for (const c of p.couleurs) {
          // Éviter les doublons de couleurs
          if (!allCouleurs.some((existing) => existing.id === c.id)) {
            allCouleurs.push({
              id: c.id,
              nom: c.nom,
              isMain: p.main || false,
              description_paquet: c.description_paquet,
              stock: c.stock ?? null,
              tailleStocks: Array.isArray(c.tailleStocks) ? c.tailleStocks : [],
            });
          }
        }
      }
    }

    result.push({
      id: `db-${mainProduct.id_produit}`,
      reference: mainProduct.reference_base || mainProduct.reference || "",
      marque: mainProduct.id_vendeur_marque?.toString() || "",
      marqueLibelle: mainProduct.marque || "",
      poids: mainProduct.poids?.toString() || "",
      categorie: mainProduct.id_categorie?.toString() || "",
      venduPar: mainProduct.vendu_par || "",
      collection: mainProduct.id_collection?.toString() || "",
      paysOrigine: mainProduct.id_provenance?.toString() || "",
      taillePaquet: mainProduct.id_declinaison?.toString() || "",
      quantitePaquet: (mainProduct.vendu_par === 'tailles')
        ? (() => {
            const decl = declinaisons.value.find(d => Number(d.id) === Number(mainProduct.id_declinaison));
            if (!decl?.tailles) return "";
            const parts = decl.tailles.split(',').filter(Boolean);
            if (parts.length === 1) {
              const subParts = parts[0].split('/').filter(Boolean);
              return String(subParts.length);
            }
            return String(parts.length);
          })()
        : (mainProduct.id_pack?.toString() || ""),
      stock: mainProduct.stock?.toString() || "",
      prix: mainProduct.prix?.toString() || "",
      prixReduit: mainProduct.prixReduit?.toString() || "",
      dateRemise: normalizeDateForDateInput(
        (remiseSource as any).dateDebutRemise ??
          (remiseSource as any).date_debut_remise ??
          (remiseSource as any).dateRemise ??
          (remiseSource as any).date_remise ??
          "",
      ),
      pourcentageRemise: String(
        (remiseSource as any).pourcentageRemise ??
          (remiseSource as any).pourcentage_remise ??
          (remiseSource as any).remisePourcentage ??
          "",
      ),
      dimensions: mainProduct.dimension || "",
      minimumCommande: mainProduct.qteMini?.toString() || "",
      descriptionFr: mainProduct.descriptionFr || "",
      descriptionEn: mainProduct.descriptionEn || "",
      descriptionIt: mainProduct.descriptionIt || "",
      descriptionEs: mainProduct.descriptionEs || "",
      descriptionDe: mainProduct.descriptionDe || "",
      descriptionZh: mainProduct.descriptionZh || "",
      dateCreation: mainProduct.dateCreation
        ? formatDateToFrench(new Date(mainProduct.dateCreation))
        : "",
      couleurs: allCouleurs.map((c: any) => {
        const couleur: any = {
          id: c.id,
          nom: c.nom,
          isMain: c.isMain || false,
        };
        if (c.description_paquet) {
          try {
            if (typeof c.description_paquet === "string") {
              if (c.description_paquet.startsWith("[")) {
                couleur.quantitesParTaille = JSON.parse(c.description_paquet);
              } else {
                couleur.quantitesParTaille = c.description_paquet
                  .split(/[,-]/)
                  .map((q: string) => parseInt(q.trim(), 10) || 0);
              }
            } else if (Array.isArray(c.description_paquet)) {
              couleur.quantitesParTaille = c.description_paquet;
            }
          } catch (e) {
            // Ignorer les erreurs de parsing
          }
        }
        if (c.stock != null) couleur.stock = c.stock;
        if (Array.isArray(c.tailleStocks)) couleur.tailleStocks = c.tailleStocks.map((ts: any) => ({ ...ts }));
        return couleur;
      }),
      compositions:
        mainProduct.compositions?.map((comp: any) => {
          const rawCompId =
            comp.compositionId ??
            comp.id_composition ??
            comp.idComposition ??
            comp.id;
          const normalizedCompId = Number(rawCompId) || 0;
          const rawPct =
            comp.percentage ??
            comp.pourcentage ??
            comp.percent ??
            comp.taux ??
            comp.value;
          const normalizedPct =
            typeof rawPct === "string"
              ? Number(rawPct.replace(",", ".").replace("%", "").trim())
              : Number(rawPct);
          const rawName =
            comp.compositionName ??
            comp.materiau ??
            comp.composition ??
            comp.libelle ??
            comp.name ??
            "";
          return {
            id_composition: normalizedCompId,
            compositionId: normalizedCompId,
            materiau: rawName,
            compositionName: rawName,
            pourcentage: Number.isFinite(normalizedPct) ? normalizedPct : 0,
            percentage: Number.isFinite(normalizedPct) ? normalizedPct : 0,
            localisation:
              (comp.localisationId ?? comp.id_composition_localisation ?? comp.localisation)?.toString() || "0",
            localisationId:
              Number(comp.localisationId ?? comp.id_composition_localisation ?? 0) || 0,
            localisationName:
              typeof comp.localisationName === "string" &&
              comp.localisationName.trim() !== ""
                ? comp.localisationName.trim()
                : undefined,
          };
        }) || [],
      caracteristiques:
        mainProduct.caracteristiques?.map((carac: any) => ({
          id: carac.id,
          libelle: carac.libelle,
        })) || [],
    });
  }

  // Traiter les produits standalone (melangees, tailles)
  for (const p of standaloneProducts) {
    result.push({
      id: `db-${p.id_produit}`,
      reference: p.reference || "",
      marque: p.id_vendeur_marque?.toString() || "",
      marqueLibelle: p.marque || "",
      poids: p.poids?.toString() || "",
      categorie: p.id_categorie?.toString() || "",
      venduPar: p.vendu_par || "",
      collection: p.id_collection?.toString() || "",
      paysOrigine: p.id_provenance?.toString() || "",
      taillePaquet: p.id_declinaison?.toString() || "",
      quantitePaquet: (p.vendu_par === 'tailles')
        ? (() => {
            const decl = declinaisons.value.find(d => Number(d.id) === Number(p.id_declinaison));
            if (!decl?.tailles) return "";
            const parts = decl.tailles.split(',').filter(Boolean);
            if (parts.length === 1) {
              const subParts = parts[0].split('/').filter(Boolean);
              return String(subParts.length);
            }
            return String(parts.length);
          })()
        : (p.id_pack?.toString() || ""),
      stock: p.stock?.toString() || "",
      prix: p.prix?.toString() || "",
      prixReduit: p.prixReduit?.toString() || "",
      dateRemise: normalizeDateForDateInput(
        (p as any).dateDebutRemise ??
          (p as any).date_debut_remise ??
          (p as any).dateRemise ??
          (p as any).date_remise ??
          "",
      ),
      pourcentageRemise: String(
        (p as any).pourcentageRemise ??
          (p as any).pourcentage_remise ??
          (p as any).remisePourcentage ??
          "",
      ),
      dimensions: p.dimension || "",
      minimumCommande: p.qteMini?.toString() || "",
      descriptionFr: p.descriptionFr || "",
      descriptionEn: p.descriptionEn || "",
      descriptionIt: p.descriptionIt || "",
      descriptionEs: p.descriptionEs || "",
      descriptionDe: p.descriptionDe || "",
      descriptionZh: p.descriptionZh || "",
      dateCreation: p.dateCreation
        ? formatDateToFrench(new Date(p.dateCreation))
        : "",
      couleurs:
        p.couleurs?.map((c: any) => {
          const hexFromList = couleursList.value.find(
            (cl) => cl.id_couleur === c.id,
          )?.hexColor;
          const couleur: any = {
            id: c.id,
            nom: c.nom,
            isMain: c.isMain || false,
            hexColor: c.hexColor || hexFromList || null,
          };
          if (c.description_paquet) {
            try {
              if (typeof c.description_paquet === "string") {
                if (c.description_paquet.startsWith("[")) {
                  couleur.quantitesParTaille = JSON.parse(c.description_paquet);
                } else {
                  couleur.quantitesParTaille = c.description_paquet
                    .split(/[,-]/)
                    .map((q: string) => parseInt(q.trim(), 10) || 0);
                }
              } else if (Array.isArray(c.description_paquet)) {
                couleur.quantitesParTaille = c.description_paquet;
              }
            } catch (e) {
              // Ignorer les erreurs de parsing
            }
          }
          if (c.stock != null) couleur.stock = c.stock;
          if (Array.isArray(c.tailleStocks)) couleur.tailleStocks = c.tailleStocks.map((ts: any) => ({ ...ts }));
          return couleur;
        }) || [],
      compositions:
        p.compositions?.map((comp: any) => {
          const rawCompId =
            comp.compositionId ??
            comp.id_composition ??
            comp.idComposition ??
            comp.id;
          const normalizedCompId = Number(rawCompId) || 0;
          const rawPct =
            comp.percentage ??
            comp.pourcentage ??
            comp.percent ??
            comp.taux ??
            comp.value;
          const normalizedPct =
            typeof rawPct === "string"
              ? Number(rawPct.replace(",", ".").replace("%", "").trim())
              : Number(rawPct);
          const rawName =
            comp.compositionName ??
            comp.materiau ??
            comp.composition ??
            comp.libelle ??
            comp.name ??
            "";
          return {
            id_composition: normalizedCompId,
            compositionId: normalizedCompId,
            materiau: rawName,
            compositionName: rawName,
            pourcentage: Number.isFinite(normalizedPct) ? normalizedPct : 0,
            percentage: Number.isFinite(normalizedPct) ? normalizedPct : 0,
            localisation:
              (comp.localisationId ?? comp.id_composition_localisation ?? comp.localisation)?.toString() || "0",
            localisationId:
              Number(comp.localisationId ?? comp.id_composition_localisation ?? 0) || 0,
            localisationName:
              typeof comp.localisationName === "string" &&
              comp.localisationName.trim() !== ""
                ? comp.localisationName.trim()
                : undefined,
          };
        }) || [],
      caracteristiques:
        p.caracteristiques?.map((carac: any) => ({
          id: carac.id,
          libelle: carac.libelle,
        })) || [],
    });
  }

  return result;
});

// Combiner les références locales (localStorage) et les produits de la base de données
// Déduplication: les produits de la base de données ont priorité sur les références locales
const normalizeReferenceBase = (value: unknown): string =>
  String(value ?? "").trim().toLowerCase();

const references = computed(() => {
  const dbRefs = draftProductsAsReferences.value;
  const dbRefSet = new Set(
    dbRefs.map((r) => normalizeReferenceBase(r.reference)).filter(Boolean),
  );
  // Filtrer les références locales qui existent déjà dans la base de données
  const filteredLocalRefs = localReferences.value.filter(
    (r) => !dbRefSet.has(normalizeReferenceBase(r.reference)),
  );
  return [...dbRefs, ...filteredLocalRefs];
});

const normalizeDateForDateInput = (value: unknown): string => {
  if (value == null) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  if (
    raw === "0000-00-00" ||
    raw === "0000-00-00 00:00:00" ||
    raw === "0000-00-00T00:00:00.000Z"
  ) {
    return "";
  }
  // Déjà au format attendu par <input type="date">
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  // Date ISO
  if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) return raw.slice(0, 10);
  // Date MySQL "YYYY-MM-DD HH:mm:ss" (souvent non parsée par Safari via new Date)
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}$/.test(raw))
    return raw.slice(0, 10);
  // Format FR/DD/MM/YYYY -> YYYY-MM-DD
  const fr = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (fr) return `${fr[3]}-${fr[2]}-${fr[1]}`;
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  return "";
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getMarqueName = (marqueId: string, marqueLibelle?: string) => {
  // Si le libellé est fourni directement (depuis la base de données), l'utiliser
  if (marqueLibelle) {
    return marqueLibelle;
  }

  if (
    !marqueId ||
    !isReferenceDataLoaded.value ||
    !marques.value ||
    marques.value.length === 0
  ) {
    return marqueId || "-";
  }

  const marque = marques.value.find((m) => m.value === marqueId);
  return marque ? marque.label : marqueId;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getCategorieName = (categorieId: string) => {
  if (
    !categorieId ||
    !isReferenceDataLoaded.value ||
    !categories.value ||
    categories.value.length === 0
  ) {
    return categorieId || "-";
  }

  const categorieIdNum = parseInt(categorieId, 10);
  const categorie = categories.value.find((c) => c.id === categorieIdNum);
  return categorie ? categorie.label : categorieId;
};

// Validation automatique du pourcentage de remise
watch(
  () => formData.pourcentageRemise,
  (newValue) => {
    const percentage = Number(newValue);
    if (!newValue || !Number.isFinite(percentage)) {
      formData.pourcentageRemise = "";
      formData.dateRemise = "";
      return;
    }

    const normalized = Math.min(100, Math.max(0, percentage));
    formData.pourcentageRemise = String(normalized);
    formData.dateRemise = normalized > 0 ? todayDate : "";
  },
);

// Validation automatique du prix réduit (doit être > 0 et < prix)
watch(
  () => [formData.prixReduit, formData.prix],
  ([prixReduit, prix]) => {
    if (!prixReduit || prixReduit === "") {
      return;
    }

    const prixReduitNum = parseFloat(prixReduit);
    const prixNum = parseFloat(prix);

    if (isNaN(prixReduitNum)) {
      formData.prixReduit = "";
      return;
    }

    // Si le prix est valide, vérifier que le prix réduit est < prix
    if (!isNaN(prixNum) && prixNum > 0) {
      if (prixReduitNum <= 0) {
        // Si prix réduit <= 0, corriger à 0.01
        formData.prixReduit = "0.01";
      } else if (prixReduitNum >= prixNum) {
        // Si prix réduit >= prix, bloquer en mettant le maximum autorisé (prix - 0.01)
        const prixReduitMax = Math.max(0.01, prixNum - 0.01);
        formData.prixReduit = prixReduitMax.toFixed(2);
      }
    }
  },
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getCollectionName = (collectionId: string | number) => {
  if (
    !collectionId ||
    !isReferenceDataLoaded.value ||
    !collections.value ||
    collections.value.length === 0
  ) {
    return collectionId?.toString() || "-";
  }

  // Convertir en string pour la comparaison (les IDs sont des strings dans les données)
  const collectionIdStr = collectionId.toString();
  const collection = collections.value.find(
    (c) => c.id.toString() === collectionIdStr,
  );
  return collection ? collection.label : collectionIdStr;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getPaysName = (paysId: string | number) => {
  if (
    !paysId ||
    !isReferenceDataLoaded.value ||
    !provenances.value ||
    provenances.value.length === 0
  ) {
    return paysId?.toString() || "-";
  }

  // Convertir en string pour la comparaison (les IDs sont des strings dans les données)
  const paysIdStr = paysId.toString();
  const pays = provenances.value.find((p) => p.id.toString() === paysIdStr);
  return pays ? pays.label : paysIdStr;
};

// Données de référence (chargées depuis l'API)
const marques = ref<
  Array<{ id: number; label: string; value: string; defaut?: boolean }>
>([]);
const categories = ref<Array<{ id: number; label: string }>>([]);
const isReferenceDataLoaded = ref(false);
const sousCategories = ref<
  Array<{ id: string; label: string; parentId: string }>
>([]);
const sousSousCategories = ref<
  Array<{ id: string; label: string; parentId: string }>
>([]);
const collections = ref<Array<{ id: number; label: string }>>([]);
const provenances = ref<Array<{ id: number; label: string }>>([]);
const packs = ref<Array<{ id: number; label: string; value: string }>>([]);
const declinaisons = ref<
  Array<{ id: number; label: string; value: number; tailles: string }>
>([]);
const compositions = ref<
  Array<{
    id: number;
    libelle: string;
    id_categorie: string | null;
    type: "composition" | "localisation";
  }>
>([]);
// Arbre des catégories (structure identique à ProductSheetModal)
const categoriesTree = ref<CategorieNode[]>([]);

// Couleurs chargées via GraphQL
const couleursList = ref<
  Array<{
    id_couleur: number;
    couleur_FR: string;
    couleur_EN: string;
    hexColor?: string | null;
  }>
>([]);

// ====== PERSISTENCE DES CHAMPS PRÉFÉRÉS (localStorage) ======
const PREFERRED_FIELDS_LS_KEY = "mel.preferredFields.v1";
const preferredFieldsLoaded = ref(false);
// Indicateur pour éviter d'exécuter certains watchers pendant la restauration
const isRestoringPreferred = ref(false);
// Indicateur lors du pré-remplissage depuis une référence (Dupliquer)
const isPrefillingFromReference = ref(false);
// Indicateur pour mettre en pause la sauvegarde automatique (évite d'écraser le cache avec des valeurs vides)
const isPreferredSavePaused = ref(false);

type PreferredFields = {
  categorie: string;
  sousCategorie: string;
  sousSousCategorie: string;
  venduPar: string;
  collection: string;
  paysOrigine: string;
  // Champs supplémentaires demandés
  reference: string;
  poids: string;
  // Descriptions
  descriptionFr?: string;
  descriptionEn?: string;
  descriptionIt?: string;
  descriptionEs?: string;
  descriptionDe?: string;
  descriptionZh?: string;
  // Prix & remises
  prix: string;
  dateRemise: string;
  prixReduit: string;
  pourcentageRemise: string;
  // Préférences MEL spécifiques au paquet
  taillePaquet?: string;
  quantitePaquet?: string;
  // Composition - tout ce qui concerne la section Composition
  compositionType?: "autres" | "chaussures" | null;
  compositions?: Array<{
    localisationId: number;
    compositionId: number;
    compositionName: string;
    percentage: number | "";
  }>;
  formLocalisations?: Array<{
    id_composition_localisation: number;
    libelle: string;
  }>;
  formCompositionsList?: Array<{ id_composition: number; libelle: string }>;
  selectedLocalisation?: string;
  newCompositionText?: string;
  compositionAutocompleteOptions?: Array<{ id: number; label: string }>;
  couleurs?: Array<{
    id: number;
    nom: string;
    isMain: boolean;
    hexColor?: string | null;
    quantitesParTaille?: number[];
    tailleStocks?: { taille: string; quantity: number | null }[];
  }>;
  // Marque et caractéristiques
  marque?: string;
  caracteristiques?: number[];
};

const savePreferredFields = () => {
  try {
    // Ne rien faire si la sauvegarde est en pause (ex: reset ou restauration initiale)
    if (isPreferredSavePaused.value) return;
    const payload: PreferredFields = {
      categorie: formData.categorie || "",
      sousCategorie: selectedSousCategorie.value || "",
      sousSousCategorie: selectedSousSousCategorie.value || "",
      venduPar: formData.venduPar || "",
      collection: formData.collection || "",
      paysOrigine: formData.paysOrigine || "",
      reference: formData.reference || "",
      poids: formData.poids || "",
      descriptionFr: formData.descriptionFr || "",
      descriptionEn: formData.descriptionEn || "",
      descriptionIt: formData.descriptionIt || "",
      descriptionEs: formData.descriptionEs || "",
      descriptionDe: formData.descriptionDe || "",
      descriptionZh: formData.descriptionZh || "",
      prix: formData.prix || "",
      dateRemise: formData.dateRemise || "",
      prixReduit: formData.prixReduit || "",
      pourcentageRemise: formData.pourcentageRemise || "",
      // Sauvegarder la déclinaison et la quantité du paquet
      taillePaquet: formData.taillePaquet || "",
      quantitePaquet: formData.quantitePaquet || "",
      // Composition: sauvegarder toutes les données utiles de la section
      compositionType: formCompositionType.value,
      compositions: Array.isArray(formCompositions.value)
        ? formCompositions.value
        : [],
      formLocalisations: Array.isArray(formLocalisations.value)
        ? formLocalisations.value.map((l) => ({
            id_composition_localisation:
              Number(l.id_composition_localisation) || 0,
            libelle: String(l.libelle || ""),
          }))
        : [],
      formCompositionsList: Array.isArray(formCompositionsList.value)
        ? formCompositionsList.value.map((c) => ({
            id_composition: Number(c.id_composition) || 0,
            libelle: String(c.libelle || ""),
          }))
        : [],
      selectedLocalisation: selectedLocalisation.value || "",
      newCompositionText: newCompositionText.value || "",
      compositionAutocompleteOptions: Array.isArray(
        compositionAutocompleteOptions.value,
      )
        ? compositionAutocompleteOptions.value.map((o) => ({
            id: Number(o.id) || 0,
            label: String(o.label || ""),
          }))
        : [],
      couleurs: Array.isArray(formCouleurs.value)
        ? formCouleurs.value.map((c: any) => ({
            id: Number(c.id) || 0,
            nom: String(c.nom || ""),
            isMain: !!c.isMain,
            hexColor: c.hexColor || null,
            quantitesParTaille: Array.isArray(c.quantitesParTaille)
              ? c.quantitesParTaille.map((n: any) => Number(n) || 0)
              : undefined,
            tailleStocks: Array.isArray(c.tailleStocks)
              ? c.tailleStocks.map((ts: any) => ({
                  taille: String(ts.taille || ""),
                  quantity: ts.quantity != null ? Number(ts.quantity) : null,
                }))
              : undefined,
          }))
        : [],
      // Marque et caractéristiques
      marque: formData.marque || "",
      caracteristiques: Array.isArray(formSelectedCaracteristiques.value)
        ? [...formSelectedCaracteristiques.value]
        : [],
    };
    localStorage.setItem(PREFERRED_FIELDS_LS_KEY, JSON.stringify(payload));
  } catch (e) {
    // ignore storage errors (e.g., private mode)
  }
};

const loadPreferredFields = () => {
  try {
    const raw = localStorage.getItem(PREFERRED_FIELDS_LS_KEY);
    if (!raw) {
      preferredFieldsLoaded.value = true;
      return;
    }
    const data = JSON.parse(raw) as Partial<PreferredFields>;

    // Suspendre les effets des watchers pendant la restauration
    isRestoringPreferred.value = true;

    if (data.categorie !== undefined) formData.categorie = data.categorie || "";
    if (data.sousCategorie !== undefined)
      selectedSousCategorie.value = data.sousCategorie || "";
    if (data.sousSousCategorie !== undefined)
      selectedSousSousCategorie.value = data.sousSousCategorie || "";
    if (data.venduPar !== undefined) formData.venduPar = data.venduPar || "";
    if (data.collection !== undefined)
      formData.collection = data.collection || "";
    if (data.paysOrigine !== undefined)
      formData.paysOrigine = data.paysOrigine || "";
    if (data.reference !== undefined) formData.reference = data.reference || "";
    if (data.poids !== undefined) formData.poids = data.poids || "";
    if ((data as any).descriptionFr !== undefined)
      formData.descriptionFr = String((data as any).descriptionFr || "");
    if ((data as any).descriptionEn !== undefined)
      formData.descriptionEn = String((data as any).descriptionEn || "");
    if ((data as any).descriptionIt !== undefined)
      formData.descriptionIt = String((data as any).descriptionIt || "");
    if ((data as any).descriptionEs !== undefined)
      formData.descriptionEs = String((data as any).descriptionEs || "");
    if ((data as any).descriptionDe !== undefined)
      formData.descriptionDe = String((data as any).descriptionDe || "");
    if ((data as any).descriptionZh !== undefined)
      formData.descriptionZh = String((data as any).descriptionZh || "");
    if (data.taillePaquet !== undefined)
      formData.taillePaquet = data.taillePaquet || "";
    if (data.quantitePaquet !== undefined)
      formData.quantitePaquet = data.quantitePaquet || "";
    if (data.prix !== undefined) formData.prix = data.prix || "";
    if (data.dateRemise !== undefined)
      formData.dateRemise = data.dateRemise || "";
    if (data.prixReduit !== undefined)
      formData.prixReduit = data.prixReduit || "";
    if (data.pourcentageRemise !== undefined)
      formData.pourcentageRemise = data.pourcentageRemise || "";
    // Composition: restauration complète
    if (data.compositionType !== undefined)
      formCompositionType.value = (data.compositionType as any) ?? null;
    if (Array.isArray((data as any).compositions)) {
      formCompositions.value = (data as any).compositions.map((c: any) => ({
        localisationId: Number(c?.localisationId) || 0,
        compositionId: Number(c?.compositionId) || 0,
        compositionName:
          typeof c?.compositionName === "string" ? c.compositionName : "",
        percentage: Number(c?.percentage) || 0,
      }));
      // Amorcer l’historique si vide (première visite après déploiement)
      if (getRecentCompositions().length === 0) {
        for (const c of [...formCompositions.value].reverse()) {
          if (c.compositionId > 0 && c.compositionName.trim()) {
            rememberComposition({
              id: c.compositionId,
              label: c.compositionName.trim(),
            });
          }
        }
      }
    }
    if (Array.isArray((data as any).formLocalisations)) {
      formLocalisations.value = (data as any).formLocalisations.map(
        (l: any) => ({
          id_composition_localisation:
            Number(l?.id_composition_localisation) || 0,
          libelle: typeof l?.libelle === "string" ? l.libelle : "",
        }),
      );
    }
    if (Array.isArray((data as any).formCompositionsList)) {
      formCompositionsList.value = (data as any).formCompositionsList.map(
        (c: any) => ({
          id_composition: Number(c?.id_composition) || 0,
          libelle: typeof c?.libelle === "string" ? c.libelle : "",
        }),
      );
    }
    if ((data as any).selectedLocalisation !== undefined)
      selectedLocalisation.value = String(
        (data as any).selectedLocalisation || "",
      );
    if ((data as any).newCompositionText !== undefined)
      newCompositionText.value = String((data as any).newCompositionText || "");
    if (Array.isArray((data as any).compositionAutocompleteOptions)) {
      compositionAutocompleteOptions.value = (
        data as any
      ).compositionAutocompleteOptions.map((o: any) => ({
        id: Number(o?.id) || 0,
        label: typeof o?.label === "string" ? o.label : "",
      }));
    }
    if (Array.isArray((data as any).couleurs)) {
      formCouleurs.value = (data as any).couleurs.map((c: any) => {
        const id = Number(c?.id) || 0;
        // Chercher le hexColor depuis couleursList si absent dans le cache
        const hexFromList = couleursList.value.find(
          (cl) => cl.id_couleur === id,
        )?.hexColor;
        return {
          id,
          nom: typeof c?.nom === "string" ? c.nom : "",
          isMain: !!c?.isMain,
          hexColor: c?.hexColor || hexFromList || null,
          ...(Array.isArray(c?.quantitesParTaille)
            ? {
                quantitesParTaille: c.quantitesParTaille.map(
                  (n: any) => Number(n) || 0,
                ),
              }
            : {}),
          ...(Array.isArray(c?.tailleStocks)
            ? {
                tailleStocks: c.tailleStocks.map((ts: any) => ({
                  taille: String(ts.taille || ""),
                  quantity: ts.quantity != null ? Number(ts.quantity) : null,
                })),
              }
            : {}),
        };
      }) as any;
    }
    // Marque et caractéristiques
    // Ne restaurer la marque que si elle n'est pas vide (pour ne pas écraser la marque par défaut)
    if ((data as any).marque !== undefined && (data as any).marque)
      formData.marque = String((data as any).marque || "");
    if (Array.isArray((data as any).caracteristiques)) {
      formSelectedCaracteristiques.value = (data as any).caracteristiques.map(
        (id: any) => Number(id) || 0,
      );
    }
  } catch (e) {
    // ignore parse errors
  } finally {
    preferredFieldsLoaded.value = true;
  }
};

// Sauvegarder dès que l'un des champs préférés change (une fois le chargement terminé)
watch(
  () => [
    formData.categorie,
    selectedSousCategorie.value,
    selectedSousSousCategorie.value,
    formData.venduPar,
    formData.collection,
    formData.paysOrigine,
    formData.reference,
    formData.poids,
    formData.descriptionFr,
    formData.descriptionEn,
    formData.descriptionIt,
    formData.descriptionEs,
    formData.descriptionDe,
    formData.descriptionZh,
    formData.taillePaquet,
    formData.quantitePaquet,
    formData.prix,
    formData.dateRemise,
    formData.prixReduit,
    formData.pourcentageRemise,
    formData.marque,
  ],
  () => {
    if (!preferredFieldsLoaded.value || isPreferredSavePaused.value) return;
    savePreferredFields();
  },
);

// Sauvegarde spécifique pour les caractéristiques
watch(
  () => formSelectedCaracteristiques.value,
  () => {
    if (preferredFieldsLoaded.value && !isPreferredSavePaused.value)
      savePreferredFields();
  },
  { deep: true },
);

// Sauvegarde spécifique pour tous les éléments de la section Composition
watch(
  () => formCompositions.value,
  () => {
    if (preferredFieldsLoaded.value && !isPreferredSavePaused.value)
      savePreferredFields();
  },
  { deep: true },
);
watch(formCompositionType, () => {
  if (preferredFieldsLoaded.value && !isPreferredSavePaused.value)
    savePreferredFields();
});
watch(
  formLocalisations,
  () => {
    if (preferredFieldsLoaded.value && !isPreferredSavePaused.value)
      savePreferredFields();
  },
  { deep: true },
);
watch(
  formCompositionsList,
  () => {
    if (preferredFieldsLoaded.value && !isPreferredSavePaused.value)
      savePreferredFields();
  },
  { deep: true },
);
watch(selectedLocalisation, () => {
  if (preferredFieldsLoaded.value && !isPreferredSavePaused.value)
    savePreferredFields();
});
watch(newCompositionText, () => {
  if (preferredFieldsLoaded.value && !isPreferredSavePaused.value)
    savePreferredFields();
});
watch(
  compositionAutocompleteOptions,
  () => {
    if (preferredFieldsLoaded.value && !isPreferredSavePaused.value)
      savePreferredFields();
  },
  { deep: true },
);

// Computed properties pour les options des selects
const marqueOptions = computed(() => {
  const options = marques.value.map((m) => ({
    label: m.label,
    value: m.value,
  }));
  return options;
});
const categorieOptions = computed(() =>
  categories.value.map((c) => ({ label: c.label, value: c.id.toString() })),
);
const collectionOptions = computed(() =>
  collections.value.map((c) => ({ label: c.label, value: c.id.toString() })),
);
const paysOptions = computed(() =>
  provenances.value.map((p) => ({ label: p.label, value: p.id.toString() })),
);

// Toutes les catégories (générales + sous + sous-sous) pour le lookup dans le tableau
const allCategories = computed(() => {
  const all: Array<{ id: number; label: string }> = [];

  // Ajouter les catégories générales
  categories.value.forEach((cat) => {
    all.push({ id: cat.id, label: cat.label });
  });

  // Ajouter les sous-catégories
  sousCategories.value.forEach((sousCat) => {
    all.push({ id: parseInt(sousCat.id), label: sousCat.label });
  });

  // Ajouter les sous-sous-catégories
  sousSousCategories.value.forEach((ssousCat) => {
    all.push({ id: parseInt(ssousCat.id), label: ssousCat.label });
  });

  return all;
});

// Lookup tableau « références à valider » : résoudre ids compositions / zones
const referencesTableLookupData = computed(() => ({
  marques: marques.value,
  categories: allCategories.value,
  collections: collections.value,
  provenances: provenances.value,
  declinaisons: declinaisons.value,
  packs: packs.value,
  compositionMateriaux: compositions.value
    .filter((c) => c.type === "composition")
    .map((c) => ({ id: Number(c.id), label: c.libelle })),
  compositionLocalisations: compositions.value
    .filter((c) => c.type === "localisation")
    .map((c) => ({ id: Number(c.id), label: c.libelle })),
}));

// Options pour les quantités par paquet (basé sur la déclinaison sélectionnée)
const quantiteOptions = computed(() => {
  try {
    // Si aucune déclinaison n'est sélectionnée, afficher une liste vide
    if (!formData.taillePaquet) {
      return [];
    }

    // Vérifier que les données sont chargées
    if (!declinaisons.value || declinaisons.value.length === 0) {
      return [];
    }

    if (!packs.value || packs.value.length === 0) {
      return [];
    }

    // Trouver la déclinaison sélectionnée pour connaître le nombre de tailles
    const selectedDeclinaison = declinaisons.value.find(
      (d) => d && d.id && d.id.toString() === formData.taillePaquet,
    );
    if (!selectedDeclinaison || !selectedDeclinaison.tailles) {
      return [];
    }

    // La longueur correspond à la dernière colonne renseignée : "S,,L" = 3.
    const tailleCount = getDeclinaisonLength(selectedDeclinaison.tailles);

    // Filtrer les packs qui ont le même nombre d'éléments
    const filteredPacks = packs.value.filter((pack) => {
      return (
        pack &&
        getPackLength(pack.value) === tailleCount
      );
    });

    return filteredPacks.map((p) => ({
      label: `${p.label} (${p.value})`,
      value: p.id.toString(), // ← Envoyer l'ID du pack !
    }));
  } catch (error) {
    return [];
  }
});
// Options pour les packs (taille du paquet) - Utilise les déclinaisons
const tailleOptions = computed(() => {
  if (!declinaisons.value || declinaisons.value.length === 0) {
    return [];
  }
  return declinaisons.value.map((d) => ({
    label: formatDeclinaisonLabel(d.label, d.tailles), // Ex: "par 5 (S-M-L)"
    value: d.id.toString(), // ← Envoyer l'ID de la déclinaison !
  }));
});

// Liste des tailles pour les couleurs mélangées (extrait de la déclinaison sélectionnée)
const taillesListForMelangees = computed(() => {
  if ((formData.venduPar !== "melangees" && formData.venduPar !== "tailles") || !formData.taillePaquet) {
    return [];
  }

  const selectedDeclinaison = declinaisons.value.find(
    (d) => d && d.id && d.id.toString() === formData.taillePaquet,
  );
  if (!selectedDeclinaison || !selectedDeclinaison.tailles) {
    return [];
  }

  // Extraire les tailles depuis la chaîne (ex: "S,M,L" -> ['S', 'M', 'L'])
  return selectedDeclinaison.tailles
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
});

// Structure hiérarchique des catégories (construite dynamiquement)
const categoriesStructure = computed(() => {
  const structure: any = {};

  // Regrouper par catégorie principale
  categories.value.forEach((cat) => {
    structure[cat.id] = {
      label: cat.label,
      sousCategories: {},
    };
  });

  // Ajouter les sous-catégories
  sousCategories.value.forEach((subCat) => {
    if (structure[subCat.parentId]) {
      structure[subCat.parentId].sousCategories[subCat.id] = {
        label: subCat.label,
        sousSousCategories: {},
      };
    }
  });

  // Ajouter les sous-sous-catégories
  sousSousCategories.value.forEach((subSubCat) => {
    const parentCategory = Object.keys(structure).find(
      (catId) => structure[catId].sousCategories[subSubCat.parentId],
    );
    if (
      parentCategory &&
      structure[parentCategory].sousCategories[subSubCat.parentId]
    ) {
      structure[parentCategory].sousCategories[
        subSubCat.parentId
      ].sousSousCategories[subSubCat.id] = subSubCat.label;
    }
  });

  return structure;
});

// Options fixes (ne changent jamais)
const venduParOptions = [
  { label: "À la couleur", value: "couleurs" },
  { label: "Couleurs mélangées", value: "melangees" },
  { label: "Tailles aux choix", value: "tailles" },
];

// Computed properties pour les sélections en cascade
const sousCategorieOptions = computed(
  (): Array<{ label: string; value: string }> => {
    // Utiliser categoriesTree (structure identique à ProductSheetModal)
    const id1 = formData.categorie;
    if (!id1) return [];
    const found = categoriesTree.value.find(
      (c: any) => String(c.id_categorie) === String(id1),
    );
    if (!found || !found.children) return [];
    return found.children.map((c: any) => ({
      label: c.label,
      value: String(c.id_categorie),
    }));
  },
);

const sousSousCategorieOptions = computed(
  (): Array<{ label: string; value: string }> => {
    // Utiliser categoriesTree (structure identique à ProductSheetModal)
    const id1 = formData.categorie;
    const id2 = selectedSousCategorie.value;
    if (!id1 || !id2) return [];
    const level1 = categoriesTree.value.find(
      (c: any) => String(c.id_categorie) === String(id1),
    );
    if (!level1 || !level1.children) return [];
    const level2 = level1.children.find(
      (c: any) => String(c.id_categorie) === String(id2),
    );
    if (!level2 || !level2.children) return [];
    return level2.children.map((c: any) => ({
      label: c.label,
      value: String(c.id_categorie),
    }));
  },
);

// ===== MÉTHODES UI =====
const handleSubmit = async () => {
  // Empêcher les doubles soumissions si une requête est en cours
  if (isSubmitting.value) return;
  // Vérifier la longueur de la description
  if (descriptionInputMel.value.length > 3000) {
    error("La description dépasse 3000 caractères. Veuillez raccourcir votre texte.");
    return;
  }
  // Si des champs sont manquants, afficher la modale et ne pas soumettre
  const missing = getMissingRequiredFields();
  if (missing.length > 0) {
    showValidationErrors.value = true;
    melFieldTouched.categorie = true;
    melFieldTouched.sousCategorie = true;
    melFieldTouched.sousSousCategorie = true;
    melFieldTouched.couleurs = true;
    missingFields.value = missing;
    showMissingFieldsModal.value = true;
    return;
  }

  // Normaliser la référence (trim/casse)
  const refName = String(formData.reference || "").trim();
  if (!refName) {
    error("La référence est requise.");
    return;
  }
  if (formSelectedCaracteristiques.value.length > 5) {
    error("Maximum 5 caractéristiques par produit.");
    return;
  }
  const isEditMode = editingReferenceId.value !== null;
  const refNormalized = normalizeReferenceBase(refName);
  const hasDuplicateDraftRef = references.value.some((r) => {
    if (!r) return false;
    if (isEditMode && r.id === editingReferenceId.value) return false;
    return normalizeReferenceBase(r.reference) === refNormalized;
  });
  if (hasDuplicateDraftRef) {
    error(
      `Référence "${refName}" déjà existante dans vos brouillons. Veuillez changer le nom de référence.`,
    );
    return;
  }

  isSubmitting.value = true;

  // Vérifier que la référence n'existe pas déjà en base de données
  try {
    const checkResult = await checkReferencesExistsBatch([
      { reference: refName },
    ]);
    if (checkResult?.results?.some((r: any) => r.exists)) {
      error(`La référence "${refName}" existe déjà dans votre liste de produits.`);
      isSubmitting.value = false;
      return;
    }
  } catch (err) {
    console.warn("Erreur lors de la vérification de la référence:", err);
    error("Erreur lors de la vérification de la référence. Veuillez réessayer.");
    isSubmitting.value = false;
    return;
  }

  try {
    // Règle métier: sur les écrans MEL, si une Déclinaison est sélectionnée
    // alors un Pack doit obligatoirement être choisi, sauf lorsque
    // « Vendu par » est « Tailles aux choix ».
    // Ici: taillePaquet = id_declinaison, quantitePaquet = id_pack
    if (
      formData.taillePaquet &&
      !formData.quantitePaquet &&
      formData.venduPar !== "tailles"
    ) {
      error(
        "Veuillez sélectionner un Pack lorsque vous choisissez une Déclinaison.",
      );
      isSubmitting.value = false;
      return;
    }

    // Simulation de soumission
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Auto-traduction de secours : si une description a été saisie mais que
    // l'utilisateur n'a pas cliqué sur "AI Traduction" (champs traduits vides),
    // on lance la traduction automatiquement avant l'enregistrement.
    // On accepte aussi le cas où l'utilisateur a saisi le texte directement
    // dans l'un des champs de langue (sans passer par le champ principal).
    {
      let srcText = descriptionInputMel.value?.trim();
      if (!srcText) {
        srcText = (
          formData.descriptionFr ||
          formData.descriptionEn ||
          formData.descriptionIt ||
          formData.descriptionEs ||
          formData.descriptionDe ||
          ""
        ).trim();
      }
      const filledCount = [
        formData.descriptionFr,
        formData.descriptionEn,
        formData.descriptionIt,
        formData.descriptionEs,
        formData.descriptionDe,
      ].filter((v) => (v || "").trim()).length;
      if (srcText && filledCount < MEL_LANG_MAP.length) {
        if (containsEmoji(srcText)) {
          error(
            "La description ne doit pas contenir d'emojis : ils ne s'affichent pas correctement sur la fiche produit et perturbent la traduction automatique.",
          );
          return;
        }
        descriptionInputMel.value = srcText;
        await autoTranslateMel();
      }
    }

    // Créer la nouvelle référence à ajouter au tableau
    const newReference: MelReference = {
      id: isEditMode ? editingReferenceId.value! : Date.now().toString(), // Conserver l'ID en mode édition
      reference: refName,
      marque: formData.marque,
      poids: formData.poids,
      // Envoyer la catégorie la plus spécifique (sous-sous > sous > générale)
      categorie:
        selectedSousSousCategorie.value ||
        selectedSousCategorie.value ||
        formData.categorie,
      categorieLibelle: selectedCategorieLabel.value || "",
      sousCategorieLibelle: selectedSousCategorieLabel.value || "",
      sousSousCategorieLibelle: selectedSousSousCategorieLabel.value || "",
      venduPar: formData.venduPar,
      collection: formData.collection,
      paysOrigine: formData.paysOrigine,
      taillePaquet: formData.taillePaquet, // ID de la déclinaison (gamme de tailles)
      quantitePaquet:
        formData.venduPar === "tailles" ? "" : formData.quantitePaquet, // ID du pack (pas de pack pour vendu par tailles)
      stock: formData.stock,
      prix: formData.prix,
      prixReduit: formData.prixReduit,
      dateRemise: formData.dateRemise,
      pourcentageRemise: formData.pourcentageRemise,
      dimensions: formData.dimensions,
      minimumCommande: formData.minimumCommande,
      couleurs: formCouleurs.value.map((c: any) => {
        const couleurItem: any = {
          id: c.id,
          nom: c.nom,
          isMain: c.isMain,
        };
        // Pour les couleurs mélangées, ajouter les quantités par taille
        if (
          formData.venduPar === "melangees" &&
          c.quantitesParTaille &&
          Array.isArray(c.quantitesParTaille)
        ) {
          couleurItem.quantitesParTaille = c.quantitesParTaille;
          // Format pour descriptionCouleurs (ex: "1-2-1" pour les quantités)
          couleurItem.description = c.quantitesParTaille.join("-");
        }
        if (c.stock != null) {
          couleurItem.stock = c.stock;
        }
        // Pour tailles aux choix, sauvegarder le stock par taille
        if (
          formData.venduPar === "tailles" &&
          c.tailleStocks &&
          Array.isArray(c.tailleStocks)
        ) {
          couleurItem.tailleStocks = c.tailleStocks;
        }
        return couleurItem;
      }),
      descriptionFr: formData.descriptionFr,
      descriptionEn: formData.descriptionEn,
      descriptionIt: formData.descriptionIt,
      descriptionEs: formData.descriptionEs,
      descriptionDe: formData.descriptionDe,
      descriptionZh: formData.descriptionZh,
      dateCreation: new Date().toLocaleDateString("fr-FR"),
      // Inclure le libellé du matériau pour l'affichage dans la liste des références
      compositions:
        formCompositions.value.length > 0
          ? formCompositions.value.map((comp) => ({
              id_composition: comp.compositionId,
              materiau: comp.compositionName, // ← nécessaire pour l'affichage « Produit > Composition »
              localisation: comp.localisationId.toString(),
              pourcentage:
                comp.percentage === "" ? 0 : Number(comp.percentage) || 0,
            }))
          : undefined,
      caracteristiques:
        formSelectedCaracteristiques.value.length > 0
          ? formSelectedCaracteristiques.value
              .map((id) => {
                const carac = formCaracteristiques.value.find(
                  (c) => c.id_caracteristique === id,
                );
                return carac
                  ? {
                      id: carac.id_caracteristique,
                      libelle: carac.libelle,
                    }
                  : null;
              })
              .filter(
                (carac): carac is { id: number; libelle: string } =>
                  carac !== null,
              )
          : undefined,
    };

    if (isEditMode) {
      // Mode édition: mettre à jour la référence existante
      const oldId = editingReferenceId.value!;

      if (oldId.startsWith("db-")) {
        // Produit déjà en base de données: utiliser l'API update (pas de delete + insert)
        const productId = parseInt(oldId.replace("db-", ""), 10);
        try {
          // Vérifier que la nouvelle référence n'existe pas déjà
          const checkResult = await checkReferencesExistsBatch([
            { reference: newReference.reference },
          ]);
          if (checkResult?.results?.some((r: any) => r.exists)) {
            error(`La référence "${newReference.reference}" existe déjà.`);
            isSubmitting.value = false;
            return;
          }

          await updateMelDraft(productId, {
            dataSource: "form",
            reference: newReference,
          });
          await refreshDraftReferencesTable();
        } catch (err) {
          console.warn(
            "Erreur lors de la mise à jour du brouillon:",
            err,
          );
          error("Erreur lors de la mise à jour de la référence");
        }
      } else {
        // Produit non encore en base: supprimer du store local puis sauvegarder
        melStore.removeReference(oldId);
        try {
          const draftData = {
            dataSource: "form" as const,
            references: [newReference],
          };
          await saveMelDraft(draftData);
          await refreshDraftReferencesTable();
        } catch (draftErr) {
          console.warn("Erreur lors de la sauvegarde du brouillon:", draftErr);
        }
      }

      // Réinitialiser le mode édition
      editingReferenceId.value = null;
      resetForm();
      success("Référence mise à jour avec succès !");
    } else {
      // Sauvegarder en brouillon dans la base de données
      try {
        const draftData = {
          dataSource: "form" as const,
          references: [newReference],
        };
        await saveMelDraft(draftData);
        await refreshDraftReferencesTable();
      } catch (draftErr) {
        console.warn("Erreur lors de la sauvegarde du brouillon:", draftErr);
      }

      resetForm();
      success("Référence ajoutée avec succès !");
    }
  } catch (err) {
    error("Erreur lors de l'ajout de la référence");
  } finally {
    isSubmitting.value = false;
  }
};

// Fonction pour afficher la modal de confirmation de suppression
const confirmRemoveReference = (referenceId: string) => {
  refToDelete.value = referenceId;
  showDeleteRefModal.value = true;
};

// Fonction pour supprimer une référence (appelée depuis la modal)
const removeReference = async () => {
  if (refToDelete.value) {
    // Si c'est un produit de la base de données (id commence par "db-"), supprimer via l'API
    if (refToDelete.value.startsWith("db-")) {
      const productId = parseInt(refToDelete.value.replace("db-", ""), 10);
      try {
        await deleteMelDraft(productId);
        // Recharger les produits brouillon depuis la base
        const draftResponse = await getMelDraft();
        if (draftResponse.success && draftResponse.products) {
          melStore.setDraftProducts(draftResponse.products);
        } else {
          melStore.clearDraftProducts();
        }
        success("Référence supprimée avec succès !");
      } catch (err) {
        error("Erreur lors de la suppression de la référence");
        console.error(err);
      }
    } else {
      // Référence locale (formulaire/Excel): supprimer via l'API par nom de référence
      const ref = melStore.references.find((r) => r.id === refToDelete.value);
      if (ref && ref.reference) {
        try {
          await deleteMelDraftByReference(ref.reference);
          melStore.removeReference(refToDelete.value);
          success("Référence supprimée avec succès !");
        } catch (err) {
          error("Erreur lors de la suppression de la référence");
          console.error(err);
        }
      } else {
        // Fallback: supprimer uniquement du localStorage si pas de référence trouvée
        melStore.removeReference(refToDelete.value);
        success("Référence supprimée avec succès !");
      }
    }
    refToDelete.value = null;
  }
  showDeleteRefModal.value = false;
};

// Recharger les brouillons depuis la base et rafraîchir le tableau des références
const refreshDraftReferencesTable = async () => {
  try {
    const draftResponse = await getMelDraft();
    if (draftResponse.success && draftResponse.products) {
      melStore.setDraftProducts(draftResponse.products);
    } else {
      melStore.clearDraftProducts();
    }
  } catch (e) {
    console.warn("Erreur lors du refresh des brouillons MEL:", e);
  }
};

// Fonction pour annuler la suppression
const cancelRemoveReference = () => {
  refToDelete.value = null;
  showDeleteRefModal.value = false;
};

const scrollToFormTop = async () => {
  await nextTick();
  const anchor = formTopAnchorRef.value;
  if (!anchor) {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }

  const offset = 8;
  const findScrollableParent = (el: HTMLElement): HTMLElement | null => {
    let parent = el.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      const canScrollY =
        /(auto|scroll)/.test(style.overflowY) &&
        parent.scrollHeight > parent.clientHeight;
      if (canScrollY) return parent;
      parent = parent.parentElement;
    }
    return null;
  };

  // 1) Tentative native
  anchor.scrollIntoView({ behavior: "smooth", block: "start" });

  // 2) Ajustement robuste pour conteneur scrollable interne
  const scrollableParent = findScrollableParent(anchor);
  if (scrollableParent) {
    const parentRect = scrollableParent.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const topInParent =
      scrollableParent.scrollTop + (anchorRect.top - parentRect.top) - offset;
    scrollableParent.scrollTo({
      top: Math.max(0, topInParent),
      behavior: "smooth",
    });
  }

  // 3) Fallback fenêtre (si la page principale est scrollée)
  const absoluteTop = window.scrollY + anchor.getBoundingClientRect().top - offset;
  window.scrollTo({ top: Math.max(0, absoluteTop), behavior: "smooth" });
};

// Fonction pour vider tous les brouillons
const clearAllDrafts = async () => {
  try {
    await deleteAllMelDrafts();
    melStore.clearReferences();
    melStore.clearDraftProducts();
    success("Tous les brouillons ont été supprimés avec succès !");
  } catch (err) {
    error("Erreur lors de la suppression des brouillons");
    console.error(err);
  }
};

// Éditer une référence existante - remplir le formulaire et marquer comme mode édition
const handleEditReference = async (ref: any) => {
  if (!ref) return;

  // Enregistrer l'ID de la référence en cours d'édition
  editingReferenceId.value = ref.id;

  // Utiliser la logique existante de prefillFromReference pour remplir le formulaire
  await prefillFromReference(ref);

  // Faire défiler vers le haut du formulaire
  await scrollToFormTop();

  // Afficher un message indiquant le mode édition
  modifier(`✏️ Mode édition - Référence: ${ref.reference}`);
};

// Annuler le mode édition
const cancelEdit = () => {
  editingReferenceId.value = null;
  resetForm();
};

// Dupliquer une référence de la liste et pré-remplir le formulaire du haut
const prefillFromReference = async (ref: any) => {
  if (!ref) return;
  const normalizedRefKey = String(ref.reference || "")
    .trim()
    .toLowerCase();
  const dbRefWithRemise = draftProductsAsReferences.value.find((r) => {
    const sameRef =
      String(r.reference || "")
        .trim()
        .toLowerCase() === normalizedRefKey;
    if (!sameRef) return false;
    return !!normalizeDateForDateInput(
      (r as any).dateDebutRemise ??
        (r as any).date_debut_remise ??
        (r as any).dateRemise ??
        (r as any).date_remise ??
        "",
    );
  });
  const refSource = dbRefWithRemise || ref;
  // Aller sur l'onglet formulaire

  activeTab.value = "form";

  // Catégories: reconstruire le chemin (cat / sous / sous-sous) à partir de l'id le plus spécifique
  try {
    // Empêcher les watchers de vider les sélections pendant la restauration
    isRestoringPreferred.value = true;
    isPrefillingFromReference.value = true;
    const path = findMelCategoryPathFromLeafId(ref.categorie);
    if (path.catId) formData.categorie = String(path.catId);
    selectedSousCategorie.value = String(path.sousId || "");
    selectedSousSousCategorie.value = String(path.sousSousId || "");
    await nextTick();
  } catch (e) {
    formData.categorie = String(ref.categorie || "");
    selectedSousCategorie.value = "";
    selectedSousSousCategorie.value = "";
  } finally {
    isRestoringPreferred.value = false;
  }

  // Renseigner les autres champs principaux (forcer string pour les selects)
  formData.reference = ref.reference || "";
  formData.marque = ref.marque || "";
  formData.venduPar = ref.venduPar || formData.venduPar;
  formData.collection = String(ref.collection || "");
  formData.paysOrigine = String(ref.paysOrigine || "");
  formData.taillePaquet = String(ref.taillePaquet || "");
  formData.quantitePaquet = String(ref.quantitePaquet || "");
  formData.stock = String(ref.stock || "");
  formData.prix = String(ref.prix || "");
  formData.prixReduit = String(refSource.prixReduit || "");
  let resolvedDateRemise = normalizeDateForDateInput(
    refSource.dateDebutRemise ??
      refSource.date_debut_remise ??
      refSource.dateRemise ??
      refSource.date_remise ??
      "",
  );
  let resolvedPourcentageRemise = String(
    refSource.pourcentageRemise ??
      refSource.pourcentage_remise ??
      refSource.remisePourcentage ??
      "",
  );
  formData.dateRemise = resolvedDateRemise;
  formData.pourcentageRemise = resolvedPourcentageRemise;
  formData.poids = String(ref.poids || "");
  formData.dimensions = ref.dimensions || "";
  formData.minimumCommande = String(ref.minimumCommande || "");
  formData.descriptionFr = ref.descriptionFr || "";
  formData.descriptionEn = ref.descriptionEn || "";
  formData.descriptionIt = ref.descriptionIt || "";
  formData.descriptionEs = ref.descriptionEs || "";
  formData.descriptionDe = ref.descriptionDe || "";
  formData.descriptionZh = ref.descriptionZh || "";

  // Couleurs
  if (Array.isArray(ref.couleurs)) {
    formCouleurs.value = ref.couleurs.map((c: any) => {
      const hexFromList = couleursList.value.find(
        (cl) => cl.id_couleur === c.id,
      )?.hexColor;
      const base: any = {
        id: c.id,
        nom: c.nom,
        isMain: !!c.isMain,
        hexColor: c.hexColor || hexFromList || null,
        stock: c.stock != null ? c.stock : null,
      };
      // Restaurer les quantités par taille pour le mode "Couleurs mélangées"
      if (ref.venduPar === "melangees" && Array.isArray(c.quantitesParTaille)) {
        // Cloner le tableau pour éviter les références partagées
        base.quantitesParTaille = [...c.quantitesParTaille];
      }
      // Restaurer le stock par taille pour le mode "Tailles aux choix"
      if (ref.venduPar === "tailles" && Array.isArray(c.tailleStocks)) {
        base.tailleStocks = c.tailleStocks.map((ts: any) => ({ ...ts }));
      }
      return base;
    }) as any;
  } else {
    formCouleurs.value = [];
  }

  // Caractéristiques
  if (Array.isArray(ref.caracteristiques)) {
    formSelectedCaracteristiques.value = ref.caracteristiques.map(
      (c: any) => c.id,
    );
  } else {
    formSelectedCaracteristiques.value = [];
  }

  // S'assurer que compositionType est défini avant de charger les compositions
  ensureCompositionType();

  // S'assurer que les données de compositions sont prêtes
  await nextTick();
  try {
    loadFormCompositionsData();
  } catch (e) {
    void e;
  }
  await nextTick();

  // Compositions: tenter de restaurer si disponibles
  if (Array.isArray(ref.compositions) && ref.compositions.length > 0) {
    const list = formCompositionsList.value || [];
    const locs = formLocalisations.value || [];
    formCompositions.value = ref.compositions.map((c: any) => {
      let compositionId: number | null = null;
      let compositionName = "";
      // ID direct si présent (support both id_composition and compositionId)
      const rawCompId = c.id_composition ?? c.compositionId;
      const normalizedCompId = Number(rawCompId);
      if (Number.isFinite(normalizedCompId) && normalizedCompId > 0) {
        compositionId = normalizedCompId;
        const found = list.find((it) => it.id_composition === compositionId);
        const fallbackFromGlobal = compositions.value.find(
          (it) => it.type === "composition" && Number(it.id) === compositionId,
        )?.libelle;
        compositionName = found
          ? found.libelle
          : c.materiau ||
            c.compositionName ||
            c.libelle ||
            c.name ||
            fallbackFromGlobal ||
            "";
      } else if (c.materiau || c.compositionName || c.libelle || c.name) {
        // Fallback par libellé (support both materiau and compositionName)
        const matName = c.materiau || c.compositionName || c.libelle || c.name || "";
        const found = list.find(
          (it) => it.libelle?.toLowerCase() === String(matName).toLowerCase(),
        );
        if (found) {
          compositionId = found.id_composition;
          compositionName = found.libelle;
        } else {
          // Garder le nom même si pas trouvé dans la liste
          compositionName = matName;
        }
      }

      // Localisation: essayer d'extraire l'ID depuis la chaîne ou via libellé
      // Support both localisation and localisationId/localisationName
      let localisationId: number | null = null;
      const rawLocId = c.localisationId;
      const rawLocName = c.localisation || c.localisationName;
      const normalizedLocId = Number(rawLocId);
      if (Number.isFinite(normalizedLocId) && normalizedLocId > 0) {
        localisationId = normalizedLocId;
      } else if (rawLocName && /^\d+$/.test(String(rawLocName))) {
        localisationId = parseInt(String(rawLocName), 10);
      } else if (rawLocName) {
        const loc = locs.find(
          (l) => l.libelle?.toLowerCase() === String(rawLocName).toLowerCase(),
        );
        if (loc) localisationId = loc.id_composition_localisation;
      }

      // Support both pourcentage and percentage
      const pct =
        c.pourcentage ?? c.percentage ?? c.percent ?? c.taux ?? c.value;
      const normalizedPct =
        typeof pct === "string"
          ? Number(pct.replace(",", ".").replace("%", "").trim())
          : Number(pct);

      return {
        localisationId: localisationId ?? 0,
        compositionId: compositionId ?? 0,
        compositionName: compositionName,
        percentage: Number.isFinite(normalizedPct) ? normalizedPct : 0,
      };
    });
  } else {
    formCompositions.value = [];
  }

  success("Formulaire pré-rempli depuis la référence sélectionnée");
  await scrollToFormTop();
  // Fin de pré-remplissage
  isPrefillingFromReference.value = false;
};

const resetForm = () => {
  showValidationErrors.value = false;
  melFieldTouched.categorie = false;
  melFieldTouched.sousCategorie = false;
  melFieldTouched.sousSousCategorie = false;
  melFieldTouched.couleurs = false;

  // Conserver ces champs pour la prochaine référence
  const preserved = {
    categorie: formData.categorie,
    sousCategorie: selectedSousCategorie.value,
    sousSousCategorie: selectedSousSousCategorie.value,
    venduPar: formData.venduPar,
    collection: formData.collection,
    paysOrigine: formData.paysOrigine,
    marque: formData.marque,
  };

  // Réinitialiser tous les champs du formulaire
  Object.keys(formData).forEach((key) => {
    // Remettre à vide par défaut
    (formData as any)[key] = "";
  });

  // Ré-appliquer les valeurs préservées
  formData.categorie = preserved.categorie;
  selectedSousCategorie.value = preserved.sousCategorie;
  selectedSousSousCategorie.value = preserved.sousSousCategorie;
  formData.venduPar = preserved.venduPar;
  formData.collection = preserved.collection;
  formData.paysOrigine = preserved.paysOrigine;
  formData.marque = preserved.marque;

  // Réinitialiser les autres éléments du formulaire
  formCouleurs.value = []; // Réinitialiser les couleurs
  formCompositions.value = []; // Réinitialiser les compositions
  formSelectedCaracteristiques.value = []; // Réinitialiser les caractéristiques
  descriptionInputMel.value = ""; // Vider le champ source de traduction

  // S'assurer que compositionType est cohérent avec la catégorie préservée
  if (preserved.categorie && !formCompositionType.value) {
    formCompositionType.value = inferCompositionTypeFromCategorie(preserved.categorie);
    loadFormCompositionsData();
  }

  // Sauvegarder les champs préférés mis à jour
  savePreferredFields();
};

// Fonction pour réinitialiser les sous-catégories quand la catégorie change
const handleCategorieChange = () => {
  selectedSousCategorie.value = "";
  selectedSousSousCategorie.value = "";
};

// ===== MÉTHODES EXCEL =====
const selectedExcelFile = ref<File | null>(null);
const isAnalyzingExcel = ref(false);
const isDownloadingTemplate = ref(false);

// Excel Analysis depuis le store
const excelAnalysisResults = computed(
  () => melStore.excelAnalysis?.results || [],
);
const excelAnalysisErrors = computed(
  () => melStore.excelAnalysis?.errors || [],
);
const excelAnalysisWarnings = computed(
  () => melStore.excelAnalysis?.warnings || [],
);

const isCategorieOpen = ref(false);
const isSousCategorieOpen = ref(false);
const isSousSousCategorieOpen = ref(false);

const selectedCategorieLabel = computed(() => {
  const opt = categorieOptions.value.find(
    (o) => o.value === formData.categorie,
  );
  return opt ? opt.label : "";
});

const selectedSousCategorieLabel = computed(() => {
  const opt = sousCategorieOptions.value.find(
    (o) => o.value === selectedSousCategorie.value,
  );
  return opt ? opt.label : "";
});

const selectedSousSousCategorieLabel = computed(() => {
  const opt = sousSousCategorieOptions.value.find(
    (o) => o.value === selectedSousSousCategorie.value,
  );
  return opt ? opt.label : "";
});

const onCategorieSelect = (value: string) => {
  formData.categorie = value;
  isCategorieOpen.value = false;
  selectedSousCategorie.value = "";
  selectedSousSousCategorie.value = "";
};

const onSousCategorieSelect = (value: string) => {
  selectedSousCategorie.value = value;
  isSousCategorieOpen.value = false;
};

const onSousSousCategorieSelect = (value: string) => {
  selectedSousSousCategorie.value = value;
  isSousSousCategorieOpen.value = false;
};

const onCategoryPathSelect = async (selection: CategoryPathSelection) => {
  // Évite que les watchers cascade vident sous / sous-sous pendant l'affectation
  isRestoringPreferred.value = true;
  isCategorieOpen.value = false;
  isSousCategorieOpen.value = false;
  isSousSousCategorieOpen.value = false;
  try {
    formData.categorie = selection.catId;
    selectedSousCategorie.value = selection.sousId;
    selectedSousSousCategorie.value = selection.sousSousId;
    await nextTick();
  } finally {
    isRestoringPreferred.value = false;
  }

  if (selection.catId) {
    formCompositionType.value = inferCompositionTypeFromCategorie(selection.catId);
    loadFormCompositionsData();
  }
};

const onCategoryPathClear = async () => {
  isRestoringPreferred.value = true;
  try {
    formData.categorie = "";
    selectedSousCategorie.value = "";
    selectedSousSousCategorie.value = "";
    await nextTick();
  } finally {
    isRestoringPreferred.value = false;
  }
  formCompositionType.value = null;
  formLocalisations.value = [];
  formCompositionsList.value = [];
  formCompositions.value = [];
};

// État pour les couleurs multiples
const formCouleurs = ref<
  Array<{
    id: number;
    nom: string;
    isMain: boolean;
    quantitesParTaille?: number[];
    stock?: number | null;
    tailleStocks?: { taille: string; quantity: number | null }[];
  }>
>([]);

const handleColorSelectionComplete = async () => {
  await nextTick();
  if (
    formData.venduPar === "melangees" &&
    couleursSelectorRef.value?.focusFirstQuantity()
  ) {
    return;
  }
  descriptionInputMelRef.value?.focus();
};

const openStockModal = async () => {
  showStockModal.value = true;
  await nextTick();
  stockBulkInputRef.value?.focus();
  stockBulkInputRef.value?.select();
};

const applyStockToAll = () => {
  const parsedValue = Number(stockBulkValue.value);
  if (!Number.isFinite(parsedValue) || parsedValue < 0) return;
  const stockValue = Math.trunc(parsedValue);
  stockBulkValue.value = stockValue;

  if (formData.venduPar === "melangees") {
    formData.stock = stockValue;
    return;
  }

  if (formData.venduPar === "tailles") {
    formCouleurs.value = formCouleurs.value.map((couleur) => ({
      ...couleur,
      tailleStocks: taillesListForMelangees.value.map((taille) => ({
        taille,
        quantity: stockValue,
      })),
    }));
    return;
  }

  formCouleurs.value = formCouleurs.value.map((couleur) => ({
    ...couleur,
    stock: stockValue,
  }));
};

const handleExcelFileSelected = (file: File) => {
  selectedExcelFile.value = file;
  melStore.clearExcelAnalysis();
};

const analyzeExcelFile = async () => {
  if (!selectedExcelFile.value) return;

  isAnalyzingExcel.value = true;

  try {
    // Utilisation de la fonction importée
    const response = await analyzeExcelAPI(selectedExcelFile.value);

    // Mapper les résultats de l'API vers le format attendu par le composant
    const results: ExcelAnalysisRow[] = response.products.map(
      (product: any) => ({
        ligne: product.ligne || 0,
        marque: product.marque || "",
        marqueUtilisee: product.marqueUtilisee || product.marque || "",
        idMarque: product.idMarque ?? 0,
        reference: product.reference || "",
        categorie: product.categorie || "",
        sousCategorie: product.sousCategorie || "",
        sousSousCategorie: product.sousSousCategorie || "",
        sousSousCategorieId: product.sousSousCategorieId || "",
        provenances: product.provenances || "",
        idProvenance: product.idProvenance ?? 0,
        venduPar: product.venduPar || "",
        qteMini: product.qteMini || "",
        collection: product.collection || "",
        idCollection: product.idCollection ?? 0,
        tailles: product.tailles ?? 0,
        taillesLibelle: product.taillesLibelle || "",
        declinaison: product.declinaison ?? 0,
        couleurs: Array.isArray(product.couleurs) ? product.couleurs : [],
        couleursLibelle: product.couleursLibelle || "",
        prixHT: product.prixHT || "",
        prixReduit: product.prixReduit || "",
        poids: product.poids || "",
        compositions: product.compositions || "",
        compositionsString:
          product.compositionsString || product.compositions || "",
        caracteristiques: Array.isArray(product.caracteristiques)
          ? product.caracteristiques
          : [],
        caracteristiquesString: product.caracteristiquesString || "",
        descriptionFr: product.descriptionFr || "",
        descriptionEn: product.descriptionEn || "",
        descriptionIt: product.descriptionIt || "",
        descriptionEs: product.descriptionEs || "",
        descriptionDe: product.descriptionDe || "",
        descriptionZh: product.descriptionZh || "",
        tailleShoot: product.tailleShoot || "",
        instructionsShoot: product.instructionsShoot || "",
        dimensions: product.dimensions || "",
        stock: product.stock || "",
        status: product.status === "ok" ? "ok" : "error",
      }),
    );

    // Séparer les erreurs et les avertissements
    const errors = response.errors.map(
      (e: any) => `Ligne ${e.ligne}: ${e.message}`,
    );
    const warnings = response.warnings.map(
      (w: any) => `Ligne ${w.ligne}: ${w.message}`,
    );

    // Sauvegarder dans le store (écrase l'ancienne analyse)
    melStore.setExcelAnalysis(results, errors, warnings);

    // Garder les sections fermées par défaut (déjà initialisé à false plus haut)

    // Convertir les résultats Excel valides en références pour le formulaire en ligne
    // et sauvegarder les brouillons dans la base de données (même s'il y a des erreurs)
    const skippedWarnings = await convertExcelToReferences(results);
    const allWarnings = [...warnings, ...skippedWarnings];
    const addedCount = response.summary.validRows - skippedWarnings.length;

    // Mettre à jour le store avec tous les warnings
    if (allWarnings.length > 0 || errors.length > 0) {
      melStore.setExcelAnalysis(results, errors, allWarnings);
    }

    // Afficher un message de succès ou d'erreur
    if (
      response.success &&
      response.summary.canImport &&
      response.summary.errorRows === 0
    ) {
      // Pas d'erreurs - afficher succès
      if (skippedWarnings.length > 0) {
        success(
          `Analyse réussie ! ${addedCount} référence(s) ajoutée(s), ${skippedWarnings.length} référence(s) ignorée(s) car déjà existante(s).`,
        );
      } else {
        success(
          `Analyse réussie ! ${response.summary.validRows} produits valides sur ${response.summary.totalRows}`,
        );
        activeTab.value = "form";
        nextTick(() => {
          referencesTableRef.value?.$el?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
      }
    } else {
      // Il y a des erreurs - afficher les erreurs mais les données valides sont quand même sauvegardées
      if (addedCount > 0) {
        warning(
          `Analyse terminée avec ${response.summary.errorRows} erreur(s). ${addedCount} référence(s) valide(s) ajoutée(s) en brouillon. Veuillez corriger les erreurs dans votre fichier Excel pour les autres lignes.`,
        );
      } else {
        error(
          `Analyse terminée avec ${response.summary.errorRows} erreur(s) et ${response.summary.warningRows} avertissement(s). Veuillez corriger les erreurs dans votre fichier Excel.`,
        );
      }
    }
  } catch (err: any) {
    error(err.message || "Erreur lors de l'analyse du fichier Excel");
    melStore.clearExcelAnalysis();
  } finally {
    isAnalyzingExcel.value = false;
  }
};

// Formater une date au format jj/mm/yyyy
const formatDateToFrench = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Convertir les résultats Excel en références pour le formulaire en ligne
// Retourne les avertissements pour les références ignorées
const convertExcelToReferences = async (
  excelRows: ExcelAnalysisRow[],
): Promise<string[]> => {
  const validRows = excelRows.filter((row) => row.status === "ok");
  const newReferences: MelReference[] = [];
  const skippedWarnings: string[] = [];
  const existingRefBases = new Set(
    references.value
      .map((r) => normalizeReferenceBase(r.reference))
      .filter(Boolean),
  );
  const refsToCheck = Array.from(
    new Set(
      validRows
        .map((row) => String(row.reference ?? "").trim())
        .filter(Boolean),
    ),
  );
  const existingDbRefBases = new Set<string>();
  if (refsToCheck.length > 0) {
    const batchResult = await checkReferencesExistsBatch(
      refsToCheck.map((reference) => ({ reference })),
    );
    for (const result of batchResult.results) {
      if (result.exists) {
        existingDbRefBases.add(normalizeReferenceBase(result.reference));
      }
    }
  }

  for (const row of validRows) {
    // Excel peut renvoyer reference comme nombre → toujours convertir en string avant .trim()
    const refName = String(row.reference ?? "").trim();
    if (!refName) continue;
    const refKey = normalizeReferenceBase(refName);
    if (existingRefBases.has(refKey) || existingDbRefBases.has(refKey)) {
      row.status = "error";
      skippedWarnings.push(
        `Ligne ${row.ligne}: référence "${refName}" déjà existante.`,
      );
      continue;
    }
    existingRefBases.add(refKey);

    const newReference: MelReference = {
      id: `excel-${Date.now()}-${row.ligne}`,
      reference: String(row.reference ?? ""),
      marque:
        row.idMarque?.toString() ||
        row.id_vendeur_marque?.toString() ||
        row.marque?.toString() ||
        "",
      marqueLibelle: row.marqueUtilisee || row.marque || "",
      idMarque:
        row.idMarque ||
        row.id_vendeur_marque ||
        (typeof row.marque === "number"
          ? row.marque
          : parseInt(row.marque) || 0),
      poids: row.poids || "",
      categorie: row.sousSousCategorieId || "",
      categorieLibelle: row.categorie || "",
      sousCategorieLibelle: row.sousCategorie || "",
      sousSousCategorieLibelle: row.sousSousCategorie || "",
      sousSousCategorieId: row.sousSousCategorieId || "",
      venduPar: row.venduPar || "couleurs",
      collection: row.idCollection?.toString() || "",
      collectionLibelle: row.collection || "",
      idCollection: row.idCollection ?? 0,
      paysOrigine: row.idProvenance?.toString() || "",
      provenanceLibelle: row.provenances || "",
      idProvenance: row.idProvenance ?? 0,
      taillePaquet: row.declinaison?.toString() || "",
      quantitePaquet:
        row.venduPar === 'tailles' ? "" : (row.tailles?.toString() || ""),
      declinaison: row.declinaison ?? 0,
      tailles: row.tailles ?? 0,
      taillesLibelle: row.taillesLibelle || "",
      stock: row.stock || "",
      prix: row.prixHT || "",
      prixHT: row.prixHT || "",
      prixReduit: row.prixReduit || "",
      dateRemise: "",
      pourcentageRemise: "",
      dimensions: row.dimensions || "",
      minimumCommande: row.qteMini || "",
      descriptionFr: row.descriptionFr || "",
      descriptionEn: row.descriptionEn || "",
      descriptionIt: row.descriptionIt || "",
      descriptionEs: row.descriptionEs || "",
      descriptionDe: row.descriptionDe || "",
      descriptionZh: row.descriptionZh || "",
      dateCreation: formatDateToFrench(new Date()),
      couleurs:
        row.couleurs?.map(
          (
            color: number | { id: number; description?: string },
            index: number,
          ) => {
            const colorId = typeof color === "number" ? color : color.id;
            const hexFromList = couleursList.value.find(
              (cl) => cl.id_couleur === colorId,
            )?.hexColor;
            return {
              id: colorId,
              nom:
                row.couleursLibelle?.split(",")[index]?.trim()?.split("*")[0] ||
                `Couleur ${colorId}`,
              isMain: index === 0,
              hexColor: hexFromList || null,
              description:
                typeof color === "object" ? color.description : undefined,
            };
          },
        ) || [],

      compositions:
        Array.isArray(row.compositions) && row.compositions.length > 0
          ? row.compositions.map((comp: any, idx: number) => {
              // 从compositionsString解析出materiau名称
              const parsed = parseCompositionsFromString(
                row.compositionsString || "",
              );
              const matchedParsed = parsed[idx];
              return {
                ...comp,
                materiau: matchedParsed?.materiau || comp.materiau || "",
              };
            })
          : parseCompositionsFromString(row.compositionsString || ""),
      caracteristiques: [],
    };

    melStore.addReference(newReference);
    newReferences.push(newReference);
  }

  // Sauvegarder tous les brouillons Excel dans la base de données
  if (newReferences.length > 0) {
    try {
      const draftData = {
        dataSource: "excel" as const,
        references: newReferences,
      };
      await saveMelDraft(draftData);
    } catch (draftErr) {
      console.warn(
        "Erreur lors de la sauvegarde des brouillons Excel:",
        draftErr,
      );
      // Ne pas bloquer l'ajout local même si la sauvegarde échoue
    }
  }

  return skippedWarnings;
};

// Parser les compositions depuis une chaîne (format: "localisation*materiau*pourcentage,...")
const parseCompositionsFromString = (
  compositionsStr: string,
): Array<{ materiau: string; pourcentage: number; localisation: string }> => {
  if (!compositionsStr) return [];

  const compositions: Array<{
    materiau: string;
    pourcentage: number;
    localisation: string;
  }> = [];
  const parts = compositionsStr
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  for (const part of parts) {
    const segs = part
      .split("*")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!segs.length) continue;

    let localisation = "Produit";
    let materiau = "";
    let pourcentage = 0;

    if (segs.length === 2) {
      materiau = segs[0];
      pourcentage = Number(segs[1]) || 0;
    } else if (segs.length >= 3) {
      localisation = segs[0];
      materiau = segs[1];
      pourcentage = Number(segs[2]) || 0;
    }

    if (materiau) {
      compositions.push({ materiau, pourcentage, localisation });
    }
  }

  return compositions;
};

// ===== FONCTIONS POUR LE FORMULAIRE EN LIGNE =====

// Charger les caractéristiques pour le formulaire en ligne
const loadFormCaracteristiques = async (
  sousSousCategorieId: string | number | null | undefined,
) => {
  const idStr = String(sousSousCategorieId ?? "").trim();

  if (!idStr) {
    formCaracteristiques.value = [];
    formSelectedCaracteristiques.value = [];
    return;
  }

  const id = Number(idStr);
  if (!Number.isInteger(id) || id <= 0) {
    formCaracteristiques.value = [];
    formSelectedCaracteristiques.value = [];
    return;
  }

  try {
    // Charger les caractéristiques filtrées par catégorie avec héritage
    const caracs = await getCaracteristiques(String(sousSousCategorieId || ""));
    formCaracteristiques.value = caracs.caracteristiques || [];
    formSelectedCaracteristiques.value = [];
  } catch (err: any) {
    error(err.message || "Erreur lors du chargement des caractéristiques");
    formCaracteristiques.value = [];
    formSelectedCaracteristiques.value = [];
  }
};

// Toggle une caractéristique pour le formulaire en ligne
const toggleFormCaracteristique = (idCaracteristique: number) => {
  const index = formSelectedCaracteristiques.value.indexOf(idCaracteristique);

  if (index > -1) {
    // Retirer la caractéristique
    formSelectedCaracteristiques.value.splice(index, 1);
  } else {
    // Vérifier la limite de 5 caractéristiques maximum
    if (formSelectedCaracteristiques.value.length >= 5) {
      error(
        "Vous ne pouvez sélectionner que 5 caractéristiques maximum par produit.",
      );
      return;
    }
    // Ajouter la caractéristique
    formSelectedCaracteristiques.value.push(idCaracteristique);
  }
};

// ===== FONCTIONS POUR LA COMPOSITION DU FORMULAIRE EN LIGNE =====

// Validation de la composition (utilisé pour le formulaire)
const isCompositionValid = computed(() => {
  if (formCompositionType.value !== "autres") return true;

  // Grouper par localisation
  const localisationGroups: { [key: number]: number } = {};

  formCompositions.value.forEach((comp) => {
    const percentage = comp.percentage || 0;
    if (localisationGroups[comp.localisationId]) {
      localisationGroups[comp.localisationId] += percentage;
    } else {
      localisationGroups[comp.localisationId] = percentage;
    }
  });

  // Vérifier que chaque localisation fait 100%
  for (const localisationId in localisationGroups) {
    if (Math.round(localisationGroups[localisationId] * 10) !== 1000) {
      return false;
    }
  }

  return true;
});

const isShoeProduct = computed(
  () => formCompositionType.value === "chaussures",
);
const shouldShowDimensions = computed(
  () => formData.categorie === "15" || isShoeProduct.value,
);

const shouldShowMinimumCommande = computed(() => {
  // Masqué si : (déclinaisons multiples OU déclinaisons null) ET venduPar != "tailles"
  // Donc affiché si : venduPar == "tailles" OU (pas de déclinaisons multiples ET déclinaisons existent)
  const hasMultipleDeclinaisons =
    declinaisons.value && declinaisons.value.length > 1;
  const hasNoDeclinaisons =
    !declinaisons.value || declinaisons.value.length === 0;

  return (
    formData.venduPar === "tailles" ||
    (!hasMultipleDeclinaisons && !hasNoDeclinaisons)
  );
});

const isDimensionsFormatValid = computed(() => {
  if (!formData.dimensions || !shouldShowDimensions.value) return true;
  return isValidProductDimensions(formData.dimensions);
});

// Détection de conflit entre prix réduit manuel et remise en pourcentage
const hasPriceRemiseConflict = computed(() => {
  const prix = parseFloat(formData.prix);
  const prixReduit = parseFloat(formData.prixReduit);
  const pourcentageRemise = parseFloat(formData.pourcentageRemise);

  // Vérifier si les deux sont renseignés
  if (
    !isNaN(prix) &&
    !isNaN(prixReduit) &&
    prixReduit > 0 &&
    !isNaN(pourcentageRemise) &&
    pourcentageRemise > 0
  ) {
    // Calculer le prix réduit attendu avec la remise en pourcentage
    const prixReduitAttendu = (prix * (100 - pourcentageRemise)) / 100;

    // Si le prix réduit manuel est différent du prix réduit calculé, il y a un conflit
    // On tolère une petite différence (0.01€) pour les arrondis
    return Math.abs(prixReduit - prixReduitAttendu) > 0.01;
  }

  return false;
});

// Validation des champs obligatoires
const isFormValid = computed(() => {
  // Champs obligatoires de base
  const requiredFields = [
    formData.reference,
    formData.categorie,
    selectedSousCategorie.value, // Sous-catégorie obligatoire
    formData.collection, // Collection obligatoire
    formData.paysOrigine, // Pays d'origine obligatoire
    formData.taillePaquet, // Taille du paquet obligatoire
    formData.prix,
    formData.poids, // Poids obligatoire
  ];

  // Quantité du paquet : obligatoire SEULEMENT si vendu_par !== "tailles"
  if (formData.venduPar !== "tailles") {
    requiredFields.push(formData.quantitePaquet);
  }

  // Au moins une couleur doit être ajoutée
  const couleursValid = formCouleurs.value.length > 0;

  // Validation spécifique pour les couleurs mélangées : vérifier que les quantités par taille sont renseignées
  let quantitesMelangeesValid = true;
  if (formData.venduPar === "melangees") {
    if (formCouleurs.value.length === 0) {
      quantitesMelangeesValid = false;
    } else {
      // Vérifier que toutes les couleurs ont des quantités renseignées
      for (const couleur of formCouleurs.value) {
        const couleurWithQuantites = couleur as any;
        if (
          !couleurWithQuantites.quantitesParTaille ||
          !Array.isArray(couleurWithQuantites.quantitesParTaille)
        ) {
          quantitesMelangeesValid = false;
          break;
        }
        // Vérifier que toutes les quantités sont > 0 (au moins une quantité doit être renseignée)
        const hasQuantites = couleurWithQuantites.quantitesParTaille.some(
          (q: number) => q > 0,
        );
        if (!hasQuantites) {
          quantitesMelangeesValid = false;
          break;
        }
      }

      // Validation: vérifier que la somme des quantités des couleurs = quantités du pack
      if (quantitesMelangeesValid && formData.quantitePaquet) {
        // Obtenir les quantités du pack sélectionné
        const selectedPack = packs.value.find(
          (p) => p.id.toString() === formData.quantitePaquet,
        );

        if (selectedPack && selectedPack.value) {
          const packQuantites = selectedPack.value
            .split(",")
            .map((q: string) => parseInt(q.trim(), 10))
            .filter((q: number) => !isNaN(q));

          // Calculer la somme des quantités par position pour toutes les couleurs
          const sommeQuantites: number[] = new Array(packQuantites.length).fill(
            0,
          );

          for (const couleur of formCouleurs.value) {
            const couleurWithQuantites = couleur as any;
            if (couleurWithQuantites.quantitesParTaille) {
              couleurWithQuantites.quantitesParTaille.forEach(
                (qte: number, index: number) => {
                  if (index < sommeQuantites.length) {
                    sommeQuantites[index] += qte || 0;
                  }
                },
              );
            }
          }

          // Comparer avec les quantités du pack
          for (let i = 0; i < packQuantites.length; i++) {
            if (packQuantites[i] !== sommeQuantites[i]) {
              quantitesMelangeesValid = false;
              break;
            }
          }
        }
      }
    }
  }

  // Marque : obligatoire dès qu'au moins une marque est disponible
  if (marques.value?.length && !formData.marque) {
    return false;
  }

  const dimensionsValid =
    !isShoeProduct.value ||
    (!!formData.dimensions.trim() && isDimensionsFormatValid.value);

  // Sous-sous-catégorie : obligatoire seulement s'il y en a de disponibles
  if (
    sousSousCategorieOptions.value.length > 0 &&
    !selectedSousSousCategorie.value
  ) {
    return false;
  }

  // Vérifier que tous les champs obligatoires sont remplis
  const allRequiredFilled = requiredFields.every(
    (field) => field && field.toString().trim() !== "",
  );

  const poidsValid = isMelUnitWeightAllowed(
    Number(String(formData.poids ?? "").replace(",", ".")),
  );

  // Validation spéciale pour les chaussures : 3 zones obligatoires
  const chaussuresValid = validateChaussuresCompositions();

  // Vérifier que les compositions sont obligatoires et valides
  const compositionsValid = formCompositionType.value
    ? isCompositionValid.value
    : true;
  const compositionsRequired = formCompositionType.value
    ? formCompositions.value.length > 0
    : true;

  return (
    allRequiredFilled &&
    poidsValid &&
    couleursValid &&
    quantitesMelangeesValid &&
    compositionsValid &&
    compositionsRequired &&
    chaussuresValid &&
    dimensionsValid
  );
});

// Détail des champs manquants pour l'affichage dans une modale
const getMissingRequiredFields = (): string[] => {
  const items: string[] = [];

  // Champs de base
  if (!formData.reference || formData.reference.trim() === "")
    items.push("Référence");
  if (!formData.categorie) items.push("Catégorie");
  if (!selectedSousCategorie.value) items.push("Sous-catégorie");
  if (
    sousSousCategorieOptions.value.length > 0 &&
    !selectedSousSousCategorie.value
  )
    items.push("Sous-sous-catégorie");
  if (!formData.collection) items.push("Collection");
  if (!formData.paysOrigine) items.push("Pays d'origine");
  if (!formData.taillePaquet) items.push("Taille du paquet");
  if (!formData.prix) items.push("Prix");
  if (!formData.poids) items.push("Poids");
  else if (getMelUnitWeightError(formData.poids))
    items.push(MAX_MEL_UNIT_WEIGHT_ERROR);

  // Quantité du paquet si nécessaire
  if (formData.venduPar !== "tailles" && !formData.quantitePaquet)
    items.push("Quantité du paquet");

  // Couleurs
  if (formCouleurs.value.length === 0) items.push("Au moins une couleur");

  // Couleurs mélangées: quantités par taille
  if (formData.venduPar === "melangees") {
    if (formCouleurs.value.length === 0) {
      // déjà couvert par couleurs, mais on ajoute précision
      items.push("Quantités par taille pour les couleurs mélangées");
    } else {
      let ok = true;
      for (const c of formCouleurs.value as any[]) {
        if (!c.quantitesParTaille || !Array.isArray(c.quantitesParTaille)) {
          ok = false;
          break;
        }
        const hasQty = c.quantitesParTaille.some((q: number) => q > 0);
        if (!hasQty) {
          ok = false;
          break;
        }
      }
      if (!ok) items.push("Quantités par taille pour les couleurs mélangées");
    }

    // Validation: vérifier que la somme des quantités des couleurs = quantités du pack
    if (formData.quantitePaquet && formCouleurs.value.length > 0) {
      const selectedPack = packs.value.find(
        (p) => p.id.toString() === formData.quantitePaquet,
      );

      if (selectedPack && selectedPack.value) {
        const packQuantites = selectedPack.value
          .split(",")
          .map((q: string) => parseInt(q.trim(), 10))
          .filter((q: number) => !isNaN(q));

        const sommeQuantites: number[] = new Array(packQuantites.length).fill(
          0,
        );

        for (const c of formCouleurs.value as any[]) {
          if (c.quantitesParTaille) {
            c.quantitesParTaille.forEach((qte: number, index: number) => {
              if (index < sommeQuantites.length) {
                sommeQuantites[index] += qte || 0;
              }
            });
          }
        }

        let mismatch = false;
        for (let i = 0; i < packQuantites.length; i++) {
          if (packQuantites[i] !== sommeQuantites[i]) {
            mismatch = true;
            break;
          }
        }

        if (mismatch) {
          items.push(
            `La somme des quantités des couleurs (${sommeQuantites.join(
              "-",
            )}) ne correspond pas aux quantités du paquet (${packQuantites.join(
              "-",
            )})`,
          );
        }
      }
    }
  }

  // Marque si affichée
  if (marques.value?.length && !formData.marque) items.push("Marque");

  if (isShoeProduct.value && formData.dimensions.trim() && !isDimensionsFormatValid.value)
    items.push("Dimensions du colis (longueur × largeur × hauteur)");

  // Compositions requises selon type
  const chaussuresValid = validateChaussuresCompositions();
  const compositionsRequired = !!formCompositionType.value;
  const compositionsValid = formCompositionType.value
    ? isCompositionValid.value
    : true;
  if (compositionsRequired) {
    if (formCompositions.value.length === 0) items.push("Compositions");
    if (!compositionsValid)
      items.push(
        "Compositions invalides (les pourcentages doivent totaliser 100% par zone)",
      );
    if (!chaussuresValid)
      items.push("Compositions chaussures incomplètes (3 zones obligatoires)");
  }
  return Array.from(new Set(items));
};

// Inférer le type de composition à partir de la catégorie
const inferCompositionTypeFromCategorie = (categorie: string): "chaussures" | "autres" => {
  const categoryId = categorie.substring(0, 2);
  const isChaussures = categoryId === "14" ||
    categorie === "130106" ||
    categorie === "130306" ||
    categorie === "130405";
  return isChaussures ? "chaussures" : "autres";
};

// S'assurer que compositionType est défini si une catégorie est sélectionnée
const ensureCompositionType = () => {
  if (formData.categorie && !formCompositionType.value) {
    formCompositionType.value = inferCompositionTypeFromCategorie(formData.categorie);
  }
};

// Charger les données de composition (sans dépendre de la sous-sous-catégorie)
const loadFormCompositionsData = () => {
  if (!formCompositionType.value) {
    return;
  }

  try {
    // Charger les localisations et compositions depuis les données de référence
    if (formCompositionType.value === "autres") {
      // Pour "autres", utiliser les localisations générales (id_categorie IS NULL)
      formLocalisations.value = compositions.value
        .filter(
          (comp) => comp.id_categorie === null && comp.type === "localisation",
        )
        .map((comp) => ({
          id_composition_localisation: comp.id,
          libelle: comp.libelle,
        }));

      // Les compositions "autres" sont chargées via autocomplete
      formCompositionsList.value = [];
    } else {
      // Pour "chaussures", utiliser les données spécifiques (id_categorie = '14')
      formLocalisations.value = compositions.value
        .filter(
          (comp) => comp.id_categorie === "14" && comp.type === "localisation",
        )
        .map((comp) => ({
          id_composition_localisation: comp.id,
          libelle: comp.libelle,
        }));

      formCompositionsList.value = compositions.value
        .filter(
          (comp) => comp.id_categorie === "14" && comp.type === "composition",
        )
        .map((comp) => ({
          id_composition: comp.id,
          libelle: comp.libelle,
        }));
    }

    // Reset des sélections
    if (
      !isPrefillingFromProduct.value &&
      !isRestoringPreferred.value &&
      !isPrefillingFromReference.value
    ) {
      formCompositions.value = [];
      selectedLocalisation.value = "";
      newCompositionText.value = "";
    }
  } catch (err: any) {
    console.error("Erreur lors du chargement des données de composition:", err);
    error(
      err.message || "Erreur lors du chargement des données de composition",
    );
    // Ne pas réinitialiser formCompositionType pour garder le formulaire visible
    formLocalisations.value = [];
    formCompositionsList.value = [];
    formCompositions.value = [];
  }
};

// Gérer l'autocomplete des compositions
const handleCompositionInput = async (
  value: string,
  localisationId: string | null,
) => {
  newCompositionText.value = value;
  const term = value.trim();

  if (term.length < 1) {
    showCompositionDropdown.value = false;
    compositionAutocompleteOptions.value = [];
    return;
  }

  try {
    isLoadingCompositionAutocomplete.value = true;

    // Filtrer SEULEMENT les compositions de la MÊME localisation
    const selectedCompositionIds = formCompositions.value
      .filter((comp) =>
        localisationId
          ? comp.localisationId === parseInt(localisationId)
          : true,
      )
      .map((comp) => comp.compositionId)
      .filter((id) => id > 0)
      .join("#");

    const options = await getCompositionAutocomplete(
      term,
      selectedCompositionIds,
    );
    compositionAutocompleteOptions.value = options;
    showCompositionDropdown.value = options.length > 0;
  } catch (err) {
    console.error("Erreur lors de la récupération des compositions:", err);
    compositionAutocompleteOptions.value = [];
    showCompositionDropdown.value = false;
  } finally {
    isLoadingCompositionAutocomplete.value = false;
  }
};

// Sélectionner une composition depuis l'autocomplete
const selectComposition = (composition: { id: number; label: string }) => {
  newCompositionText.value = composition.label;
  showCompositionDropdown.value = false;
  compositionAutocompleteOptions.value = [];
};

// Masquer le dropdown
const hideCompositionDropdown = () => {
  setTimeout(() => {
    showCompositionDropdown.value = false;
  }, 200);
};

// Ajouter une composition pour "autres"
// Validation des 3 zones obligatoires pour les chaussures
const validateChaussuresCompositions = (): boolean => {
  if (formCompositionType.value !== "chaussures") return true;

  const localisationIds = formLocalisations.value.map(
    (loc) => loc.id_composition_localisation,
  );
  return localisationIds.every((localisationId) =>
    formCompositions.value.some(
      (comp) => comp.localisationId === localisationId,
    ),
  );
};

const downloadExcelTemplate = async () => {
  isDownloadingTemplate.value = true;
  try {
    // ── Données MEL : cache serveur en priorité, fallback sur le fichier statique compilé ──
    let melCollections = staticCollectionsLibelles;
    let melCompositions = staticCompositionLibelles;
    let melCouleurs = staticCouleursDefaut;
    let melCaracDetailedRows = staticMemoCaracDetailedRows;
    let melCaracFlatLibelles = staticMemoCaracFlatLibelles;
    try {
      const payload = await getMelTemplateData();
      const built = buildMelDataFromPayload(payload);
      melCollections = built.staticCollectionsLibelles;
      melCompositions = built.staticCompositionLibelles;
      melCouleurs = built.staticCouleursDefaut;
      melCaracDetailedRows = built.staticMemoCaracDetailedRows;
      melCaracFlatLibelles = built.staticMemoCaracFlatLibelles;
    } catch {
      // Utilise les imports statiques compilés comme fallback
    }

    // ── Palette beige ─────────────────────────────────────────────────────────
    const HDR_BG      = "C8A97A";  // en-tête : beige un peu plus foncé
    const HDR_TEXT    = "3E2000";  // texte en-tête : marron foncé
    const ROW_LIGHT   = "FEF3E2";  // ligne paire : beige clair
    const ROW_PALE    = "FFFBF5";  // ligne impaire : beige super clair
    const BORDER_CLR  = "D4B896";  // couleur des traits délimiteurs (beige moyen)

    // Trait fin beige sur les 4 côtés
    const THIN_BORDER = {
      top:    { style: "thin", color: { rgb: BORDER_CLR } },
      bottom: { style: "thin", color: { rgb: BORDER_CLR } },
      left:   { style: "thin", color: { rgb: BORDER_CLR } },
      right:  { style: "thin", color: { rgb: BORDER_CLR } },
    };

    const getColIdx = (cellKey: string): number => {
      const colStr = cellKey.replace(/\d+$/, "");
      let idx = 0;
      for (let i = 0; i < colStr.length; i++)
        idx = idx * 26 + (colStr.charCodeAt(i) - 64);
      return idx - 1;
    };

    // Applique le style beige foncé + gras + bordure sur la ligne 1 (en-têtes)
    const applyMemoHeaders = (sheet: Record<string, unknown>) => {
      Object.keys(sheet).forEach((key) => {
        if (!/^[A-Z]+1$/.test(key) || !sheet[key]) return;
        const cell = sheet[key] as Record<string, unknown>;
        if (!cell.s) cell.s = {};
        const s = cell.s as Record<string, unknown>;
        s.fill   = { fgColor: { rgb: HDR_BG } };
        s.font   = { bold: true, color: { rgb: HDR_TEXT } };
        s.border = THIN_BORDER;
        if (!s.alignment) s.alignment = {};
        const a = s.alignment as Record<string, unknown>;
        a.horizontal = "center";
        a.vertical   = "center";
        a.wrapText   = false;
      });
    };

    // Applique le fond beige en quinconce + bordure sur les lignes de données (row >= 2)
    const applyDataRows = (sheet: Record<string, unknown>) => {
      Object.keys(sheet).forEach((key) => {
        if (!/^[A-Z]+\d+$/.test(key) || !sheet[key]) return;
        const rowNum = parseInt(key.match(/(\d+)$/)?.[1] ?? "0");
        if (rowNum < 2) return;
        const cell = sheet[key] as Record<string, unknown>;
        if (!cell.s) cell.s = {};
        const s = cell.s as Record<string, unknown>;
        s.fill   = { fgColor: { rgb: rowNum % 2 === 0 ? ROW_LIGHT : ROW_PALE } };
        s.border = THIN_BORDER;
      });
    };

    // Applique uniquement les bordures (sans changer le fond) — pour la page Template
    const applyBordersOnly = (sheet: Record<string, unknown>) => {
      Object.keys(sheet).forEach((key) => {
        if (!/^[A-Z]+\d+$/.test(key) || !sheet[key]) return;
        const cell = sheet[key] as Record<string, unknown>;
        if (!cell.s) cell.s = {};
        (cell.s as Record<string, unknown>).border = THIN_BORDER;
      });
    };

    // Génération dynamique d'un XLSX via xlsx-js-style (export des styles : alignement, etc.)
    // Données identiques à tes captures
    const headers = [
      "Marque\n(Obligatoire)",
      "Référence\n(Obligatoire)",
      "Catégorie\n(Obligatoire)",
      "Sous catégorie",
      "Sous-sous Catégorie",
      "Provenances\n(Obligatoire)",
      "Vendu Par\n(Obligatoire)",
      "Qte min",
      "Collection\n(Obligatoire)",
      "Tailles\n(ex: 1*S,1*M — Obligatoire)",
      "Couleurs\n(Obligatoire)",
      "Stock",
      "Prix HT\n(Obligatoire)",
      "Prix réduit HT",
      "Poids KG\n(Obligatoire)",
      "Compositions\n(Obligatoire)",
      "Caractéristiques\n(Max 5)",
      "Description (fr)",
      "Description (en)",
      "Taille donnée pour shooting",
      "Instructions pour shooting",
      "Dimensions",
      "",
    ];
    const rows = [
      [
        "Efashion Paris",
        "EX-MIXCOLORS",
        "Femme",
        "Hauts",
        "Chemises",
        "Chine",
        "melangees",
        "",
        "Printemps / Été",
        "3*S,3*M,3*L",
        "Bleu*1-1-1,Noir*1-1-1,Rouge*1-1-1",
        "10",
        "4.50",
        "",
        "0.25",
        "croute de cuir*46.5,cachemire*53.5",
        "oversized,fleur,dentelle",
        "",
        "",
        "",
        "",
        "",
        "",
      ],
      [
        "Efashion Paris",
        "EX-COULEURS",
        "Femme",
        "Bas",
        "Jupes",
        "France",
        "couleurs",
        "",
        "Automne / Hiver",
        "1*S,1*M,1*L,1*XL",
        "rouge,gris",
        "rouge*10,gris*5",
        "4.50",
        "",
        "0.25",
        "croute de cuir*70.5,cachemire*29.5,Doublure*croute de cuir*100",
        "ceinture,jupe longue,imprimé animalier",
        "",
        "",
        "",
        "",
        "",
        "",
      ],
      [
        "Efashion Paris",
        "EX-TAILLE",
        "Enfant & Bébé",
        "Fille",
        "Ensembles",
        "Italie",
        "tailles",
        "6",
        "Toutes les saisons",
        "S,M,L",
        "Bleu,Jaune,vert",
        "Bleu*S*5,Bleu*M*10,Bleu*L*8,Jaune*S*3,Jaune*M*6,Jaune*L*4,vert*S*2,vert*M*4,vert*L*3",
        "4.50",
        "",
        "0.25",
        "croute de cuir*33.4,cachemire*33.3,polyester*33.3,Doublure*croute de cuir*100",
        "3 pièces",
        "",
        "",
        "",
        "",
        "",
        "",
      ],
      [
        "Efashion Paris",
        "EX-MAROQUINERIE",
        "Maroquinerie",
        "Femme",
        "Sacs à bandoulière",
        "Cambodge",
        "Couleurs",
        "",
        "Toutes les saisons",
        "1*TU",
        "Noir,Camel,Rose,Jaune",
        "Noir*10,Camel*8,Rose*6,Jaune*4",
        "4.50",
        "",
        "0.25",
        "croute de cuir*100,Doublure*croute de cuir*100",
        "imprimé,matelassé",
        "",
        "",
        "",
        "",
        "30x20x10",
        "",
      ],
      [
        "Efashion Paris",
        "EX-CHAUSSURES",
        "Homme",
        "Extérieur",
        "Vestes",
        "Ukraine",
        "Couleurs",
        "",
        "Toutes les saisons",
        "1*TU",
        "Noir,Camel,Rose,Jaune",
        "Noir*10,Camel*8,Rose*6,Jaune*4",
        "4.50",
        "",
        "0.25",
        "croute de cuir*100,Doublure*croute de cuir*100",
        "",
        "",
        "Derbies LIVIA",
        "",
        "40",
        "",
        "",
      ],
      [
        "Efashion Paris",
        "EX-ACCESSOIRES",
        "Accessoires",
        "Autres",
        "Parapluies",
        "Cambodge",
        "Couleurs",
        "",
        "Toutes les saisons",
        "1*TU",
        "rouge,gris",
        "rouge*10,gris*5",
        "4.50",
        "",
        "0.25",
        "croute de cuir*100,Doublure*croute de cuir*100",
        "vintage,plume,voyage,militaire,etoile",
        "",
        "Derbies SOPHIA",
        "",
        "",
        "10cm*10cm*10cm",
        "",
      ],
    ];

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Style des cellules d'en-tête (ligne 1) : beige foncé + gras + wrap
    const headerCellKeys = Object.keys(ws).filter(
      (key) => /^[A-Z]+\d+$/.test(key) && key.match(/\d+/)?.[0] === "1",
    );
    headerCellKeys.forEach((cellKey) => {
      if (!ws[cellKey]) return;
      if (!ws[cellKey].s) ws[cellKey].s = {};
      ws[cellKey].s.fill = { fgColor: { rgb: HDR_BG } };
      ws[cellKey].s.font = { bold: true, color: { rgb: HDR_TEXT } };
      if (!ws[cellKey].s.alignment) ws[cellKey].s.alignment = {};
      ws[cellKey].s.alignment.wrapText = true;
      ws[cellKey].s.alignment.vertical = "top";
    });

    // Largeur de colonnes très élargie (padding visuel)
    ws["!cols"] = [
      { wch: 36 }, // Marque (Obligatoire)
      { wch: 38 }, // Référence (Obligatoire)
      { wch: 34 }, // Catégorie (Obligatoire)
      { wch: 32 }, // Sous catégorie
      { wch: 38 }, // Sous-sous Catégorie
      { wch: 34 }, // Provenances (Obligatoire)
      { wch: 26 }, // Vendu Par (Obligatoire)
      { wch: 18 }, // Qte min
      { wch: 36 }, // Collection (Obligatoire)
      { wch: 38 }, // Tailles (Obligatoire)
      { wch: 52 }, // Couleurs (Obligatoire)
      { wch: 42 }, // Stock
      { wch: 34 }, // Prix HT (Obligatoire)
      { wch: 26 }, // Prix réduit HT
      { wch: 34 }, // Poids KG (Obligatoire)
      { wch: 68 }, // Compositions (Obligatoire)
      { wch: 58 }, // Caractéristiques
      { wch: 44 }, // Description
      { wch: 44 }, // Description (en)
      { wch: 46 }, // Taille donnée pour shooting
      { wch: 42 }, // Instructions pour shooting
      { wch: 34 }, // dimensions
      { wch: 26 }, // colonne vide supplémentaire
    ];
    // Hauteur d'entête et lignes de données élargies
    ws["!rows"] = [
      { hpt: 64 },
      { hpt: 28 },
      { hpt: 28 },
      { hpt: 28 },
      { hpt: 28 },
      { hpt: 28 },
      { hpt: 28 },
      { hpt: 28 },
    ];

    applyBordersOnly(ws as Record<string, unknown>);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");

    // 2e feuille « Memo cat. carac. » — données statiques (voir src/data/melTemplateStaticData.ts)
    try {
      const rowsCarac: (string | number)[][] = [];
      let maxCaracCols = 0;

      if (melCaracDetailedRows.length > 0) {
        for (const row of melCaracDetailedRows) {
          const r = row.map((c) => c) as (string | number)[];
          rowsCarac.push(r);
          maxCaracCols = Math.max(maxCaracCols, Math.max(0, r.length - 3));
        }
      }

      if (rowsCarac.length > 0) {
        const caracHeaders = Array.from(
          { length: maxCaracCols },
          (_, i) => `Caractéristique ${i + 1}`,
        );
        const headersCarac = [
          "Catégorie",
          "Sous catégorie",
          "Sous-sous catégorie",
          ...caracHeaders,
        ];
        const paddedRows = rowsCarac.map((row) => {
          const pad = headersCarac.length - row.length;
          return pad > 0 ? [...row, ...Array(pad).fill("")] : row;
        });
        const wsCarac = XLSX.utils.aoa_to_sheet([headersCarac, ...paddedRows]);
        wsCarac["!cols"] = [
          { wch: 34 },
          { wch: 38 },
          { wch: 40 },
          ...Array(maxCaracCols).fill({ wch: 36 }),
        ];
        const numRowsCarac = 1 + paddedRows.length;
        wsCarac["!rows"] = [
          { hpt: 42 },
          ...Array(numRowsCarac - 1).fill({ hpt: 28 }),
        ];
        Object.keys(wsCarac).forEach((key) => {
          if (!/^[A-Z]+\d+$/.test(key) || !wsCarac[key]) return;
          const cell = wsCarac[key];
          if (!cell.s) cell.s = {};
          if (!cell.s.alignment) cell.s.alignment = {};
          cell.s.alignment.horizontal = "center";
          cell.s.alignment.vertical = "center";
          cell.s.alignment.wrapText = false;
        });
        applyDataRows(wsCarac as Record<string, unknown>);
        applyMemoHeaders(wsCarac as Record<string, unknown>);
        XLSX.utils.book_append_sheet(wb, wsCarac, "Memo cat. carac.");
      } else if (melCaracFlatLibelles.length > 0) {
        const headersCarac = ["Caractéristiques"];
        const rowsCaracFlat = melCaracFlatLibelles.map((lib) => [lib]);
        if (rowsCaracFlat.length > 0) {
          const wsCarac = XLSX.utils.aoa_to_sheet([
            headersCarac,
            ...rowsCaracFlat,
          ]);
          wsCarac["!cols"] = [{ wch: 62 }];
          const numRowsFlat = 1 + rowsCaracFlat.length;
          wsCarac["!rows"] = [
            { hpt: 42 },
            ...Array(numRowsFlat - 1).fill({ hpt: 28 }),
          ];
          // Centrer tout le contenu (horizontal + vertical)
          Object.keys(wsCarac).forEach((key) => {
            if (!/^[A-Z]+\d+$/.test(key) || !wsCarac[key]) return;
            const cell = wsCarac[key];
            if (!cell.s) cell.s = {};
            if (!cell.s.alignment) cell.s.alignment = {};
            cell.s.alignment.horizontal = "center";
            cell.s.alignment.vertical = "center";
            cell.s.alignment.wrapText = false;
          });
          applyDataRows(wsCarac as Record<string, unknown>);
          applyMemoHeaders(wsCarac as Record<string, unknown>);
          XLSX.utils.book_append_sheet(wb, wsCarac, "Memo cat. carac.");
        }
      }
    } catch (_) {
      // Données statiques invalides ou erreur de génération : on garde le template seul
    }

    // 3e feuille : Couleurs par famille (familles en colonnes, nuances en lignes) — statique
    try {
      const couleursDefaut = melCouleurs;
      const familleOrder = [
        "ROUGE",
        "ROSE",
        "ORANGE",
        "JAUNE",
        "VERT",
        "BLEU",
        "VIOLET",
        "MARRON",
        "NOIR",
        "BLANC",
        "GRIS",
        "AUTRES",
      ] as const;
      const familleKeywords: Record<(typeof familleOrder)[number], string[]> = {
        ROUGE: [
          "rouge",
          "bordeaux",
          "cerise",
          "corail",
          "vermillon",
          "écarlate",
          "cramoisi",
        ],
        ROSE: [
          "rose",
          "blush",
          "fuchsia",
          "magenta",
          "framboise",
          "vieux rose",
          "rose fluo",
        ],
        ORANGE: ["orange", "pêche", "abricot", "saumon"],
        JAUNE: ["jaune", "or", "moutarde", "citron", "poussin"],
        VERT: [
          "vert",
          "olive",
          "kaki",
          "menthe",
          "émeraude",
          "sapin",
          "canard",
          "bouteille",
          "céladon",
          "pomme",
        ],
        BLEU: [
          "bleu",
          "marine",
          "cyan",
          "turquoise",
          "ciel",
          "océan",
          "pétrole",
          "denim",
          "jeans",
          "irisé",
          "nocturne",
        ],
        VIOLET: ["violet", "lavande", "lilas", "mauve", "prune", "indigo"],
        MARRON: ["marron", "caramel", "brun", "tan", "chocolat"],
        NOIR: ["noir"],
        BLANC: ["blanc", "ivoire", "écru", "crème"],
        GRIS: ["gris", "argent", "anthracite"],
        AUTRES: [],
      };
      const byFamille = new Map<string, string[]>();
      familleOrder.forEach((f) => byFamille.set(f, []));
      for (const c of couleursDefaut) {
        const nom = (c.couleur_FR || "").toLowerCase();
        let found = false;
        for (const f of familleOrder) {
          if (f === "AUTRES") continue;
          const keywords = familleKeywords[f];
          if (keywords.some((k) => nom.includes(k))) {
            byFamille.get(f)?.push(c.couleur_FR);
            found = true;
            break;
          }
        }
        if (!found) byFamille.get("AUTRES")?.push(c.couleur_FR);
      }
      const familiesToShow = familleOrder.filter(
        (f) => (byFamille.get(f)?.length ?? 0) > 0,
      );
      if (familiesToShow.length > 0) {
        const maxRows = Math.max(
          1,
          ...familiesToShow.map((f) => byFamille.get(f)?.length ?? 0),
        );
        const headerRow = ["COULEURS", ...familiesToShow];
        const dataRows: string[][] = [];
        for (let i = 0; i < maxRows; i++) {
          const row = [
            "",
            ...familiesToShow.map((f) => byFamille.get(f)?.[i] ?? ""),
          ];
          dataRows.push(row);
        }
        const wsCouleurs = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
        wsCouleurs["!cols"] = [
          { wch: 26 },
          ...familiesToShow.map(() => ({ wch: 34 })),
        ];
        // Hauteur uniforme pour toutes les lignes (évite cases grandes/petites)
        const numRowsCouleurs = 1 + dataRows.length;
        wsCouleurs["!rows"] = [
          { hpt: 42 },
          ...Array(numRowsCouleurs - 1).fill({ hpt: 28 }),
        ];
        Object.keys(wsCouleurs).forEach((key) => {
          if (!/^[A-Z]+\d+$/.test(key) || !wsCouleurs[key]) return;
          const cell = wsCouleurs[key];
          if (!cell.s) cell.s = {};
          if (!cell.s.alignment) cell.s.alignment = {};
          cell.s.alignment.horizontal = "center";
          cell.s.alignment.vertical = "center";
          cell.s.alignment.wrapText = false;
        });
        applyDataRows(wsCouleurs as Record<string, unknown>);
        applyMemoHeaders(wsCouleurs as Record<string, unknown>);
        XLSX.utils.book_append_sheet(wb, wsCouleurs, "Memo Couleurs");
      }
    } catch (_) {
      // Ignorer si les couleurs statiques posent problème
    }

    // 4e feuille : Collections (saisons) — statique
    try {
      const collections = melCollections.map((libelle) => ({
        libelle,
      }));
      if (collections.length > 0) {
        const numRowsCollection = 1 + collections.length;
        const maxRow = numRowsCollection;
        const wsCollection: Record<string, unknown> = {};
        wsCollection["!ref"] = `A1:A${maxRow}`;
        // Supprimer toute cellule hors plage pour éviter l’affichage de cases vides
        wsCollection["!cols"] = [{ wch: 34 }];
        wsCollection["!rows"] = [
          { hpt: 42 },
          ...Array(numRowsCollection - 1).fill({ hpt: 28 }),
        ];
        const centerStyle = {
          alignment: {
            horizontal: "center" as const,
            vertical: "center" as const,
            wrapText: false,
          },
        };
        wsCollection["A1"] = {
          t: "s",
          v: "Collection (saison)",
          s: { ...centerStyle, fill: { fgColor: { rgb: HDR_BG } }, font: { bold: true, color: { rgb: HDR_TEXT } }, border: THIN_BORDER },
        };
        collections.forEach((c, i) => {
          const rowNum = i + 2;
          wsCollection[`A${rowNum}`] = {
            t: "s",
            v: c.libelle,
            s: { ...centerStyle, fill: { fgColor: { rgb: rowNum % 2 === 0 ? ROW_LIGHT : ROW_PALE } }, border: THIN_BORDER },
          };
        });
        XLSX.utils.book_append_sheet(
          wb,
          wsCollection as any,
          "Memo Collections",
        );
      }
    } catch (_) {
      // Ignorer si les collections statiques posent problème
    }

    // 5e feuille : Compositions — liste unique — statique
    try {
      const libelles = [...new Set(melCompositions)].sort((a, b) =>
        (a || "").localeCompare(b || ""),
      );
      if (libelles.length > 0) {
        const numRowsComp = 1 + libelles.length;
        const wsComp: Record<string, unknown> = {};
        wsComp["!ref"] = `A1:A${numRowsComp}`;
        wsComp["!cols"] = [{ wch: 34 }];
        wsComp["!rows"] = [
          { hpt: 42 },
          ...Array(numRowsComp - 1).fill({ hpt: 28 }),
        ];
        const centerStyle = {
          alignment: {
            horizontal: "center" as const,
            vertical: "center" as const,
            wrapText: false,
          },
        };
        wsComp["A1"] = {
          t: "s",
          v: "Compositions",
          s: { ...centerStyle, fill: { fgColor: { rgb: HDR_BG } }, font: { bold: true, color: { rgb: HDR_TEXT } }, border: THIN_BORDER },
        };
        libelles.forEach((lib, i) => {
          const rowNum = i + 2;
          wsComp[`A${rowNum}`] = {
            t: "s",
            v: lib,
            s: { ...centerStyle, fill: { fgColor: { rgb: rowNum % 2 === 0 ? ROW_LIGHT : ROW_PALE } }, border: THIN_BORDER },
          };
        });
        XLSX.utils.book_append_sheet(wb, wsComp as any, "Memo Compositions");
      }
    } catch (_) {
      // Ignorer si l'API échoue
    }

    // 6e feuille : Vendu par — options fixes (à la couleur, couleurs mélangées, tailles aux choix)
    const venduParLabels = venduParOptions.map((o) => o.label);
    if (venduParLabels.length > 0) {
      const numRowsVp = 1 + venduParLabels.length;
      const wsVenduPar: Record<string, unknown> = {};
      wsVenduPar["!ref"] = `A1:A${numRowsVp}`;
      wsVenduPar["!cols"] = [{ wch: 24 }];
      wsVenduPar["!rows"] = [
        { hpt: 42 },
        ...Array(numRowsVp - 1).fill({ hpt: 28 }),
      ];
      const centerStyleVp = {
        alignment: {
          horizontal: "center" as const,
          vertical: "center" as const,
          wrapText: false,
        },
      };
      wsVenduPar["A1"] = {
        t: "s",
        v: "Vendu par",
        s: { ...centerStyleVp, fill: { fgColor: { rgb: HDR_BG } }, font: { bold: true, color: { rgb: HDR_TEXT } }, border: THIN_BORDER },
      };
      venduParLabels.forEach((label, i) => {
        const rowNum = i + 2;
        wsVenduPar[`A${rowNum}`] = {
          t: "s",
          v: label,
          s: { ...centerStyleVp, fill: { fgColor: { rgb: rowNum % 2 === 0 ? ROW_LIGHT : ROW_PALE } }, border: THIN_BORDER },
        };
      });
      XLSX.utils.book_append_sheet(wb, wsVenduPar as any, "Memo Vendu par");
    }

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "template_mise_en_ligne.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Erreur lors du téléchargement du template:", err);
    alert("Erreur lors du téléchargement du template Excel");
  } finally {
    isDownloadingTemplate.value = false;
  }
};

// Computed pour les options disponibles selon le mel du vendeur
const vendeurMel = computed(() => authStore.user?.mel || "0");

// Calculer le nombre total de références (1 référence = 1 ligne, peu importe le nombre de couleurs)
const totalReferences = computed(() => {
  const items =
    melDataSource.value === "excel"
      ? melStore.excelAnalysis?.results || []
      : melStore.references || [];

  return items.length;
});

// Calculer le nombre total de produits (références × couleurs) - gardé pour compatibilité si nécessaire ailleurs
const totalProducts = computed(() => {
  const items =
    melDataSource.value === "excel"
      ? melStore.excelAnalysis?.results || []
      : melStore.references || [];

  let total = 0;
  for (const item of items) {
    const venduPar = item.venduPar || "couleurs";

    // Si couleurs mélangées, compter 1 pièce
    if (venduPar === "melangees") {
      total += 1;
    } else {
      // Si "Toutes les couleurs" est coché, compter chaque couleur
      // Sinon, compter 1 seul produit (couleur principale)
      if (shootAllColors.value) {
        const couleurs =
          item.couleurs && Array.isArray(item.couleurs) ? item.couleurs : [1];
        total += couleurs.length;
      } else {
        total += 1;
      }
    }
  }

  return total;
});

const melOptions = computed(() => {
  const mel = vendeurMel.value;
  return {
    canUpload: mel === "1",
    canTreatment: mel === "2",
    mustChooseShooting: mel === "0",
    button1Label:
      mel === "1"
        ? "Ajouter mes photos"
        : mel === "2"
        ? "Demande de traitement"
        : "",
    button1Available: mel === "1" || mel === "2",
  };
});

// IDs des références sélectionnées pour la mise en ligne
const selectedReferenceIds = ref<string[]>([]);

// Nombre de références effectivement concernées par la mise en ligne
// (sélectionnées si une sélection existe, sinon toutes)
const effectiveReferenceCount = computed(() => {
  if (selectedReferenceIds.value.length > 0) {
    return selectedReferenceIds.value.length;
  }
  return totalReferences.value;
});

// Confirmer la mise en ligne des références du formulaire
const confirmReferencesUpload = async (
  selectedIds: string[],
  action: "primary" | "shooting",
) => {
  // Récupérer les références à traiter
  const refsToCheck =
    selectedIds.length > 0
      ? references.value.filter((r) => selectedIds.includes(r.id))
      : references.value;

  // Vérifier si les références existent déjà dans la base de données
  const refsWithReference = refsToCheck.filter((r) => !!r.reference);
  const duplicateRefs: string[] = [];
  try {
    const batchResult = await checkReferencesExistsBatch(
      refsWithReference.map((r) => ({
        reference: r.reference,
      })),
    );
    for (const result of batchResult.results) {
      if (result.exists) {
        duplicateRefs.push(result.reference);
      }
    }
  } catch (err) {
    console.error("Erreur lors de la vérification des références:", err);
  }

  // Si des références existent déjà, afficher une erreur et ne pas continuer
  if (duplicateRefs.length > 0) {
    error(
      `Les références suivantes existent déjà dans votre liste des produits : ${duplicateRefs.join(
        ", ",
      )}. Veuillez les supprimer ou modifier leurs noms.`,
    );
    return;
  }
  // Validation des champs obligatoires (prix et poids)
  const validationErrors: string[] = [];
  for (const ref of refsToCheck) {
    const refName = ref.reference || '(sans nom)';
    if (!ref.prix && !ref.prixHT) {
      validationErrors.push(`Référence "${refName}" : le prix est manquant.`);
    }
    const poidsError = getMelUnitWeightError(ref.poids);
    if (poidsError) {
      validationErrors.push(`Référence "${refName}" : ${poidsError}`);
    }
  }

  // Validation des compositions
  for (const ref of refsToCheck) {
    const refName = ref.reference || '(sans nom)';
    const hasCompositions = Array.isArray(ref.compositions) && ref.compositions.length > 0;
    const hasCompositionsString = !!(ref as any).compositionsString;

    if (!hasCompositions && !hasCompositionsString) {
      validationErrors.push(
        `Référence "${refName}" : la composition est manquante. Veuillez cliquer sur "Modifier" pour ajouter la composition.`
      );
    }
  }

  if (validationErrors.length > 0) {
    error(validationErrors.join('\n'));
    return;
  }

  melDataSource.value = "form";
  selectedReferenceIds.value = selectedIds;

  selectedMelOption.value = null;
  showPrestationChoice.value = false;
  selectedPrestation.value = null;
  shootAllColors.value = false;

  if (action === "shooting") {
    if (selectedIds.length > 60) {
      error(
        `Les demandes de shooting sont limitées à 60 références. Vous en avez sélectionné ${selectedIds.length}.`,
      );
      return;
    }

    selectedMelOption.value = "shooting";
    showPrestationChoice.value = true;
    showMelConfirmModal.value = true;
    return;
  }

  if (!melOptions.value.button1Available) {
    error("Veuillez choisir un type de shooting.");
    return;
  }

  selectedMelOption.value = melOptions.value.canUpload ? "upload" : "treatment";
  await confirmMelChoice();
};

// Ouvrir la popup de confirmation pour l'Excel
const confirmExcelUpload = () => {
  // Définir la source comme Excel
  melDataSource.value = "excel";

  // Réinitialiser les options
  selectedMelOption.value = null;
  showPrestationChoice.value = false;
  selectedPrestation.value = null;
  shootAllColors.value = false;

  // Si mel=0, afficher directement le choix de prestation
  if (melOptions.value.mustChooseShooting) {
    selectedMelOption.value = "shooting";
    showPrestationChoice.value = true;
  }

  showMelConfirmModal.value = true;
};

// Confirmer le choix MEL
const confirmMelChoice = async () => {
  if (isSubmitting.value) return;

  if (!melStore.excelAnalysis && !melStore.references) return;

  try {
    isSubmitting.value = true;

    // Recharger les données de référence pour vérifier que les packs/déclinaisons existent toujours
    await loadReferenceData();

    // Valider que tous les packs et déclinaisons sélectionnés existent encore
    const validPackIds = packs.value.map((p) => p.id.toString());
    const validDeclinaisonIds = declinaisons.value.map((d) => d.id.toString());

    // Préparer les données pour le brouillon
    const draftData: any = {
      dataSource: melDataSource.value,
    };

    // Ajouter les données selon la source
    if (melDataSource.value === "excel" && melStore.excelAnalysis) {
      // Les caractéristiques viennent directement de l'analyse Excel (colonne Caractéristiques)
      const productsWithCaracteristiques = melStore.excelAnalysis.results
        .filter((product: any) => product.status === "ok")
        .map((product: any) => {
          // Les caractéristiques sont déjà dans product.caracteristiques (tableau de nombres)
          // Les convertir en format attendu par le backend (tableau de nombres directement)
          const caracteristiques =
            product.caracteristiques && product.caracteristiques.length > 0
              ? product.caracteristiques
              : undefined;

          return {
            ...product,
            caracteristiques: caracteristiques,
          };
        });

      // Valider les packs et déclinaisons pour Excel
      for (let i = 0; i < productsWithCaracteristiques.length; i++) {
        const product = productsWithCaracteristiques[i];
        if (product.tailles && typeof product.tailles === "number") {
          if (!validPackIds.includes(product.tailles.toString())) {
            throw new Error(
              `Ligne ${i + 1} (Référence "${
                product.reference || "N/A"
              }"): Le pack ID ${
                product.tailles
              } n'existe plus. Veuillez recharger la page et sélectionner un pack valide.`,
            );
          }
        }
        if (product.declinaison && typeof product.declinaison === "number") {
          if (!validDeclinaisonIds.includes(product.declinaison.toString())) {
            throw new Error(
              `Ligne ${i + 1} (Référence "${
                product.reference || "N/A"
              }"): La déclinaison ID ${
                product.declinaison
              } n'existe plus. Veuillez recharger la page et sélectionner une déclinaison valide.`,
            );
          }
        }
      }

      draftData.products = productsWithCaracteristiques;
    } else if (melDataSource.value === "form" && references.value.length > 0) {
      // Filtrer les références selon la sélection
      if (selectedReferenceIds.value.length === 0) {
        error("Veuillez sélectionner au moins une référence à mettre en ligne.");
        isSubmitting.value = false;
        return;
      }
      const refsToProcess = references.value.filter((r) =>
        selectedReferenceIds.value.includes(r.id),
      );

      // Valider les packs et déclinaisons pour le formulaire
      // Utiliser references.value qui combine draftProducts (ancien PHP) et localReferences
      for (let i = 0; i < refsToProcess.length; i++) {
        const ref = refsToProcess[i];
        if (ref.quantitePaquet && ref.quantitePaquet !== "") {
          if (ref.venduPar !== 'tailles' && !validPackIds.includes(ref.quantitePaquet)) {
            throw new Error(
              `Référence "${ref.reference}": Le pack sélectionné n'existe plus. Veuillez recharger la page et sélectionner un pack valide.`,
            );
          }
        }
        if (ref.taillePaquet && ref.taillePaquet !== "") {
          if (!validDeclinaisonIds.includes(ref.taillePaquet)) {
            throw new Error(
              `Référence "${ref.reference}": La déclinaison sélectionnée n'existe plus. Veuillez recharger la page et sélectionner une déclinaison valide.`,
            );
          }
        }
        // Valider le pourcentage de remise
        if (ref.pourcentageRemise && ref.pourcentageRemise !== "") {
          const pourcentage = parseFloat(ref.pourcentageRemise);
          if (isNaN(pourcentage) || pourcentage < 0 || pourcentage > 100) {
            throw new Error(
              `Référence "${ref.reference}": Le pourcentage de remise doit être entre 0 et 100. Valeur reçue: ${ref.pourcentageRemise}`,
            );
          }
        }
        // Avertir (mais ne pas bloquer) si les deux remises diffèrent
        if (
          ref.prix &&
          ref.prixReduit &&
          ref.pourcentageRemise
        ) {
          const prix = parseFloat(ref.prix);
          const prixReduit = parseFloat(ref.prixReduit);
          const pourcentage = parseFloat(ref.pourcentageRemise);
          if (
            !isNaN(prix) &&
            !isNaN(prixReduit) &&
            prixReduit > 0 &&
            !isNaN(pourcentage) &&
            pourcentage > 0
          ) {
            const prixReduitAttendu = (prix * (100 - pourcentage)) / 100;
            if (Math.abs(prixReduit - prixReduitAttendu) > 0.01) {
              console.warn(
                `Référence "${
                  ref.reference
                }": Le prix réduit manuel (${prixReduit}€) diffère du prix calculé avec la remise (${prixReduitAttendu.toFixed(
                  2,
                )}€). Le prix réduit manuel sera conservé.`,
              );
            }
          }
        }
      }

      draftData.references = refsToProcess;
    }

    // Préparer les données pour créer le shooting (sans sauvegarder en brouillon)
    const requestData: any = {
      melOption: selectedMelOption.value,
      dataSource: melDataSource.value,
      shootAllColors: shootAllColors.value,
    };

    // Ajouter la prestation si c'est un shooting
    if (selectedMelOption.value === "shooting" && selectedPrestation.value) {
      requestData.prestation = selectedPrestation.value;
    }

    // Ajouter les données selon la source
    if (melDataSource.value === "excel" && melStore.excelAnalysis) {
      requestData.products = draftData.products;
    } else if (melDataSource.value === "form" && draftData.references) {
      requestData.references = draftData.references;
    }

    // Appel API backend pour créer le shooting
    const response = await saveMelChoice(requestData);

    // Nettoyer le store selon la source des données
    if (melDataSource.value === "excel") {
      melStore.clearExcelAnalysis();
    } else if (melDataSource.value === "form") {
      // Supprimer uniquement les références qui ont été mises en ligne
      if (selectedReferenceIds.value.length > 0) {
        selectedReferenceIds.value.forEach((id) => {
          melStore.removeReference(id);
          melStore.removeDraftProduct(id);
        });
      } else {
        // Toutes les références ont été mises en ligne
        melStore.clearReferences();
        melStore.clearDraftProducts();
      }
    }

    // Refresh de la liste "références à valider" après confirmation modal
    try {
      const draftResponse = await getMelDraft();
      if (draftResponse.success && draftResponse.products) {
        melStore.setDraftProducts(draftResponse.products);
      } else {
        melStore.clearDraftProducts();
      }
    } catch (refreshErr) {
      console.warn(
        "Erreur lors du refresh des références à valider:",
        refreshErr,
      );
    }

    // Réinitialiser la sélection
    selectedReferenceIds.value = [];

    // Réinitialiser la source
    melDataSource.value = null;
    showMelConfirmModal.value = false;

    if (selectedMelOption.value === "upload") {
      success(
        "Produits créés ! Vous pouvez maintenant uploader vos photos. Si vous utilisez des photos générées par IA, ajoutez également au moins une photo réelle du produit sur cintre.",
      );
      const shootingIds =
        response.shootings?.map((s: { id: number }) => s.id.toString()) ?? [];
      router.push({
        name: "catalog/uploadphoto",
        query:
          shootingIds.length > 0
            ? {
                shootingId:
                  shootingIds.length === 1 ? shootingIds[0] : shootingIds,
              }
            : undefined,
      });
    } else if (selectedMelOption.value === "treatment") {
      success(
        "Demande de traitement enregistrée ! Veuillez envoyer vos produits.",
      );
    } else {
      // Shooting demandé
      const totalShootings = response.totalShootings || 1;
      if (totalShootings > 1) {
        success(
          `${totalShootings} demandes de shooting créées avec succès ! (groupées par catégorie)`,
        );
      } else {
        success(`Demande de shooting enregistrée avec succès !`);
      }
    }
  } catch (err: any) {
    error(err.message || "Erreur lors de la confirmation");
  } finally {
    isSubmitting.value = false;
  }
};

// ========== LIFECYCLE HOOKS ==========

const loadReferenceData = async () => {
  try {
    isLoadingReferenceData.value = true;
    const data = await getReferenceData();

    // Charger toutes les données sauf les couleurs (gérées par le store)
    const rawMarques = data.marques || [];
    marques.value = rawMarques.map(
      (m: {
        id: number;
        label: string;
        value?: string | number;
        defaut?: boolean;
      }) => ({
        id: m.id,
        label: m.label,
        value: String(m.value ?? m.id),
        defaut: m.defaut === true,
      }),
    );
    // La sélection de la marque par défaut est gérée par le watcher
    categories.value = data.categories;
    sousCategories.value = data.sousCategories;
    sousSousCategories.value = data.sousSousCategories;
    collections.value = data.collections;
    provenances.value = data.provenances;
    packs.value = data.packs;
    declinaisons.value = data.declinaisons;
    compositions.value = data.compositions;

    // Charger l'arbre des catégories (structure identique à ProductSheetModal)
    try {
      categoriesTree.value = await getCategoriesTree("fr");
    } catch (treeErr) {
      console.warn("Impossible de charger l'arbre des catégories:", treeErr);
    }

    isReferenceDataLoaded.value = true;
  } catch (err) {
    error(
      `Erreur lors du chargement des données : ${
        err instanceof Error ? err.message : "Erreur inconnue"
      }`,
    );
  } finally {
    isLoadingReferenceData.value = false;
  }
};

const loadCouleurs = async () => {
  try {
    const couleurs = await getCouleursOfficielles();
    couleursList.value = couleurs;
  } catch (err) {
    console.warn("Impossible de charger les couleurs:", err);
    error("Erreur lors du chargement des couleurs");
  }
};
const findMelCategoryPathFromLeafId = (
  leafId: number | string | null | undefined,
): { catId?: string; sousId?: string; sousSousId?: string } => {
  if (leafId == null) return {};

  const id = String(leafId);

  for (const cat of categories.value) {
    const catKey = cat.id.toString();
    const catStruct = categoriesStructure.value[catKey];
    if (!catStruct) continue;

    for (const [sousId, sousStruct] of Object.entries(
      catStruct.sousCategories,
    )) {
      // 3 级：sous-sous-catégorie
      if (sousStruct.sousSousCategories[id]) {
        return { catId: catKey, sousId, sousSousId: id };
      }

      // 2 级：sous-catégorie
      if (sousId === id) {
        return { catId: catKey, sousId };
      }
    }

    // 1 级：catégorie
    if (catKey === id) {
      return { catId: catKey };
    }
  }

  return {};
};

const findMelCategoryPathFromLeafLabel = (
  leafLabel: string,
): { catId?: string; sousId?: string; sousSousId?: string } => {
  if (!leafLabel) return {};

  // 3 级 & 2 级：遍历整棵树找 label
  for (const cat of categories.value) {
    const catKey = cat.id.toString();
    const catStruct = categoriesStructure.value[catKey];
    if (!catStruct) continue;

    for (const [sousId, sousStruct] of Object.entries(
      catStruct.sousCategories,
    )) {
      // 3 级：sous-sous
      const entries = Object.entries(sousStruct.sousSousCategories);
      const foundSousSous = entries.find(
        ([, label]) => String(label) === leafLabel,
      );
      if (foundSousSous) {
        const [ssId] = foundSousSous;
        return { catId: catKey, sousId, sousSousId: ssId };
      }

      // 2 级：sous
      if (sousStruct.label === leafLabel) {
        return { catId: catKey, sousId };
      }
    }

    // 1 级：cat
    if (catStruct.label === leafLabel) {
      return { catId: catKey };
    }
  }

  return {};
};

const resolveMelCategoryPathForProduct = (
  prod: ProduitCatalogue,
): {
  leafId: number | string | null;
  leafLabel: string;
  catId?: string;
  sousId?: string;
  sousSousId?: string;
} => {
  const anyProd: any = prod;

  // ① 尝试用 ID，当成 leaf
  const leafIdRaw =
    anyProd.id_categorie ??
    prod.catLevel3Id ??
    prod.catLevel2Id ??
    prod.catLevel1Id ??
    null;

  let catId: string | undefined;
  let sousId: string | undefined;
  let sousSousId: string | undefined;

  if (leafIdRaw != null) {
    const byId = findMelCategoryPathFromLeafId(leafIdRaw);
    catId = byId.catId;
    sousId = byId.sousId;
    sousSousId = byId.sousSousId;
  }

  // ② 如果 ID 没找到，再用 label 兜底
  const leafLabel =
    (Array.isArray(prod.categories) && prod.categories.length
      ? String(prod.categories[prod.categories.length - 1])
      : "") ||
    (anyProd.categorie as string | undefined) ||
    "";

  if (!catId && leafLabel) {
    const byLabel = findMelCategoryPathFromLeafLabel(leafLabel);
    catId = byLabel.catId ?? catId;
    sousId = byLabel.sousId ?? sousId;
    sousSousId = byLabel.sousSousId ?? sousSousId;
  }

  return {
    leafId: leafIdRaw,
    leafLabel,
    catId,
    sousId,
    sousSousId,
  };
};

const applyPrefillFromProduct = async (prod: ProduitCatalogue) => {
  isPrefillingFromProduct.value = true;

  const anyProd: any = prod;

  const loadPrefillCaracteristiques = async (categoryId: string) => {
    await loadFormCaracteristiques(categoryId);
    formSelectedCaracteristiques.value = [];
    if (!prod.id) return;

    try {
      const selectedIds = await getProduitCaracteristiqueIds(prod.id);
      const validIds = selectedIds.filter((id) =>
        formCaracteristiques.value.some((c) => c.id_caracteristique === id),
      );
      formSelectedCaracteristiques.value = validIds;
      if (validIds.length > 5) {
        warning(
          "Ce produit contient plus de 5 caractéristiques. Veuillez en sélectionner 5 maximum.",
        );
      }
    } catch (e) {
      formSelectedCaracteristiques.value = [];
    }
  };

  try {
    formData.reference = prod.reference || "";
    formData.poids = prod.poids != null ? String(prod.poids) : "";
    formData.prix = prod.prix != null ? prod.prix.toFixed(2) : "";
    formData.prixReduit = "";
    formData.dateRemise = "";
    formData.pourcentageRemise = "";

    if (prod.marqueId != null && marques.value.length) {
      const m = marques.value.find((mk) => Number(mk.value) === prod.marqueId);
      formData.marque = m ? m.value : "";
    } else {
      formData.marque = "";
    }

    formData.categorie = "";
    selectedSousCategorie.value = "";
    selectedSousSousCategorie.value = "";

    const { catId, sousId, sousSousId } =
      resolveMelCategoryPathForProduct(prod);

    if (catId) {
      formData.categorie = catId;
      await nextTick();
    }
    if (sousId) {
      selectedSousCategorie.value = sousId;
      await nextTick();
    }
    if (sousSousId) {
      selectedSousSousCategorie.value = sousSousId;
      await loadPrefillCaracteristiques(sousSousId);
    } else if (sousId) {
      // Si pas de sous-sous-catégorie mais une sous-catégorie existe, charger les caractéristiques de la sous-catégorie
      await loadPrefillCaracteristiques(sousId);
    } else {
      formCaracteristiques.value = [];
      formSelectedCaracteristiques.value = [];
    }

    {
      const raw =
        (prod as any).collectionId ??
        anyProd.id_collection ??
        (typeof anyProd.collection === "object"
          ? anyProd.collection?.id
          : anyProd.collection);

      if (raw != null && collections.value.length) {
        const rawStr = String(raw).trim();
        let col =
          collections.value.find((c) => String(c.id) === rawStr) ??
          collections.value.find((c) => c.label === rawStr);

        formData.collection = col ? String(col.id) : "";
      } else {
        formData.collection = "";
      }
    }

    // 5️⃣ Pays d’origine / provenance
    {
      const raw =
        anyProd.id_provenance ??
        anyProd.provenanceId ??
        (typeof anyProd.provenance === "object"
          ? anyProd.provenance?.id
          : anyProd.provenance);

      if (raw != null && provenances.value.length) {
        const rawStr = String(raw).trim();

        let p =
          provenances.value.find((pr) => String(pr.id) === rawStr) ??
          provenances.value.find((pr) => pr.label === rawStr);

        formData.paysOrigine = p ? String(p.id) : "";
      } else {
        formData.paysOrigine = "";
      }
    }

    // 6️⃣ vendu par
    formData.venduPar = ["couleurs", "melangees", "tailles"].includes(
      prod.venduPar as any,
    )
      ? (prod.venduPar as any)
      : "couleurs";

    // 7️⃣ Taille du paquet / Quantité du paquet
    formData.taillePaquet =
      prod.declinaisonId != null ? String(prod.declinaisonId) : "";

    // 先等一帧，让 quantiteOptions 根据 taillePaquet 算好
    await nextTick();

    {
      const anyProd: any = prod;
      const rawPack =
        prod.packId ??
        anyProd.id_pack ??
        (typeof anyProd.pack === "object" ? anyProd.pack?.id : anyProd.pack);

      if (rawPack != null && packs.value.length) {
        const rawStr = String(rawPack).trim();

        // 先按 id 找
        let pack =
          packs.value.find((p) => String(p.id) === rawStr) ||
          // 再按 value 或 label 兜底
          packs.value.find((p) => p.value === rawStr || p.label === rawStr);

        formData.quantitePaquet = pack ? String(pack.id) : "";
      } else {
        formData.quantitePaquet = "";
      }
    }

    formData.dimensions = anyProd.dimensions || "";
    formData.minimumCommande =
      anyProd.minimumCommande != null ? String(anyProd.minimumCommande) : "";

    const perSizeByColorId: Record<number, number[]> = {};

    if (formData.venduPar === "melangees" && prod.id) {
      try {
        const produitCouleurs = await getProduitCouleurs(prod.id);

        let tailleCount = 0;
        if (formData.taillePaquet) {
          const decl = declinaisons.value.find(
            (d) => String(d.id) === formData.taillePaquet,
          );
          if (decl?.tailles) {
            tailleCount = decl.tailles
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean).length;
          }
        }
        if (!tailleCount && taillesListForMelangees.value.length) {
          tailleCount = taillesListForMelangees.value.length;
        }

        for (const pc of produitCouleurs) {
          const cid = pc.id_couleur;
          if (!cid) continue;

          const arr =
            tailleCount > 0
              ? new Array(tailleCount).fill(0)
              : new Array(taillesListForMelangees.value.length || 0).fill(0);

          if (pc.description_paquet) {
            pc.description_paquet
              .split("-")
              .map((s: string) => s.trim())
              .forEach((v: string, idx: number) => {
                if (idx >= arr.length) return;
                const n = parseInt(v, 10);
                if (!Number.isNaN(n)) arr[idx] = n;
              });
          }

          perSizeByColorId[cid] = arr;
        }
      } catch (e) {
        console.error("getProduitCouleurs error in prefill", e);
      }
    }

    // 9️⃣ Couleurs
    const baseColors: Array<{
      id: number;
      nom: string;
      isMain: boolean;
      hexColor?: string | null;
    }> = [];

    if (
      Array.isArray(prod.colorIds) &&
      prod.colorIds.length &&
      couleursList.value.length
    ) {
      prod.colorIds.forEach((cid, index) => {
        const found = couleursList.value.find((c) => c.id_couleur === cid);
        if (found) {
          baseColors.push({
            id: found.id_couleur,
            nom: found.couleur_FR,
            isMain: index === 0,
            hexColor: found.hexColor || null,
          });
        }
      });
    } else if (typeof prod.couleur === "string" && prod.couleur.trim()) {
      const names = prod.couleur
        .split(",")
        .map((s: string) => s.trim())
        .filter(Boolean);

      names.forEach((name, index) => {
        const found = couleursList.value.find((c) => c.couleur_FR === name);
        if (found) {
          baseColors.push({
            id: found.id_couleur,
            nom: found.couleur_FR,
            isMain: index === 0,
            hexColor: found.hexColor || null,
          });
        }
      });
    }

    formCouleurs.value = baseColors.map((c) => ({
      ...c,
      ...(formData.venduPar === "melangees"
        ? {
            quantitesParTaille:
              perSizeByColorId[c.id] ||
              (taillesListForMelangees.value.length
                ? new Array(taillesListForMelangees.value.length).fill(0)
                : undefined),
          }
        : {}),
      ...(formData.venduPar === "tailles" && taillesListForMelangees.value.length
        ? {
            tailleStocks: (c as any).tailleStocks?.length
              ? (c as any).tailleStocks.map((ts: any) => ({ ...ts }))
              : taillesListForMelangees.value.map((t) => ({ taille: t, quantity: null })),
          }
        : {}),
    }));

    // 🔟 Description
    try {
      const desc = await getProduitDescription(prod.id);
      formData.descriptionFr =
        desc?.texte_fr ?? anyProd.description_fr ?? anyProd.descriptionFr ?? "";
      formData.descriptionEn =
        desc?.texte_uk ?? anyProd.description_en ?? anyProd.descriptionEn ?? "";
      formData.descriptionIt =
        desc?.texte_it ?? anyProd.description_it ?? anyProd.descriptionIt ?? "";
      formData.descriptionEs =
        desc?.texte_es ?? anyProd.description_es ?? anyProd.descriptionEs ?? "";
      formData.descriptionDe =
        desc?.texte_de ?? anyProd.description_de ?? anyProd.descriptionDe ?? "";
      formData.descriptionZh =
        desc?.texte_zh ?? anyProd.description_zh ?? anyProd.descriptionZh ?? "";
    } catch (e) {
      console.error("getProduitDescription error", e);
      formData.descriptionFr =
        anyProd.description_fr ?? anyProd.descriptionFr ?? "";
      formData.descriptionEn =
        anyProd.description_en ?? anyProd.descriptionEn ?? "";
      formData.descriptionIt =
        anyProd.description_it ?? anyProd.descriptionIt ?? "";
      formData.descriptionEs =
        anyProd.description_es ?? anyProd.descriptionEs ?? "";
      formData.descriptionDe =
        anyProd.description_de ?? anyProd.descriptionDe ?? "";
      formData.descriptionZh =
        anyProd.description_zh ?? anyProd.descriptionZh ?? "";
    }

    formCompositions.value = [];

    // S'assurer que compositionType est défini (au cas où le watcher n'a pas été déclenché)
    ensureCompositionType();

    // S'assurer que les données de compositions sont prêtes avant de pré-remplir
    // Le watcher sur formData.categorie a déjà défini formCompositionType et appelé loadFormCompositionsData()
    // Mais on doit s'assurer que formLocalisations est bien chargé avant de pré-remplir
    await nextTick();
    if (formCompositionType.value && formLocalisations.value.length === 0) {
      // Si formLocalisations est vide, recharger les données de composition
      loadFormCompositionsData();
      await nextTick();
    }

    await prefillCompositionsFromProduct(anyProd);
  } finally {
    setTimeout(() => {
      isPrefillingFromProduct.value = false;
    }, 0);
  }
};

const prefillCompositionsFromProduct = async (anyProd: any) => {
  const raw =
    anyProd.compositionsRaw ?? anyProd.compositions ?? anyProd.composition;

  if (!raw || typeof raw !== "string") {
    return;
  }

  const parsedCompositions: Array<{
    localisationId: number;
    compositionId: number;
    compositionName: string;
    percentage: number | "";
  }> = [];

  const parts = raw
    .split(",")
    .map((p: string) => p.trim())
    .filter(Boolean);

  for (const part of parts) {
    const segs = part
      .split("*")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!segs.length) continue;

    let localisationLabel: string | null = null;
    let compositionLabel = "";
    let percentage: number | "" = 0;

    if (segs.length === 2) {
      // 形如 "croute de cuir*100"
      compositionLabel = segs[0];
      percentage = Number(segs[1]) || 0;
    } else if (segs.length >= 3) {
      // 形如 "Doublure*croute de cuir*100"
      localisationLabel = segs[0];
      compositionLabel = segs[1];
      percentage = Number(segs[2]) || 0;
    }

    // 找 localisationId
    let localisationId = 0;
    if (localisationLabel && formLocalisations.value.length) {
      const loc = formLocalisations.value.find(
        (l) => l.libelle.toLowerCase() === localisationLabel.toLowerCase(),
      );
      if (loc) localisationId = loc.id_composition_localisation;
    }

    // 找 compositionId
    let compositionId = 0;
    if (formCompositionsList.value.length) {
      const comp = formCompositionsList.value.find(
        (c) => c.libelle === compositionLabel,
      );
      if (comp) compositionId = comp.id_composition;
    }

    // 如果 formCompositionsList 为空（"autres" 类型），通过 API 查找 compositionId
    if (compositionId === 0 && compositionLabel) {
      try {
        const options = await getCompositionAutocomplete(compositionLabel, "");
        const match = options.find(
          (o: any) => o.label.toLowerCase() === compositionLabel.toLowerCase(),
        );
        if (match) compositionId = match.id;
      } catch (e) {
        // 忽略错误
      }
    }

    parsedCompositions.push({
      localisationId,
      compositionId,
      compositionName: compositionLabel,
      percentage,
    });
  }

  // 一次性赋值，避免响应式更新过程中的问题
  formCompositions.value = parsedCompositions;
};

onMounted(async () => {
  forceFormMethodOnCompactViewport();
  window.addEventListener("resize", forceFormMethodOnCompactViewport);

  if (!authStore.isInitialized) {
    await authStore.checkAuth();
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: "auth" });
    return;
  }
  await loadReferenceData();
  await loadCouleurs();
  // Mettre en pause la sauvegarde automatique pendant toute la phase de restauration
  isPreferredSavePaused.value = true;

  loadPreferredFields();

  isRestoringPreferred.value = true;

  // S'assurer que compositionType est défini si une catégorie a été restaurée
  ensureCompositionType();

  await nextTick();
  try {
    if (formData.categorie || formCompositionType.value) {
      loadFormCompositionsData();
    }
  } catch (e) {
    /* no-op */
  }

  await nextTick();

  isRestoringPreferred.value = false;

  //PRODUIT::GetAll with premel='1'
  try {
    const draftResponse = await getMelDraft();
    if (draftResponse.success && draftResponse.products) {
      melStore.setDraftProducts(draftResponse.products);

      // Nettoyer localStorage: supprimer les références locales qui existent déjà dans la base de données
      // (pour éviter les doublons, car la base de données a priorité)
      const dbRefs = new Set(
        draftResponse.products.map((p: any) =>
          (p.reference || p.reference_base || "").trim().toLowerCase(),
        ),
      );
      const currentLocalRefs = melStore.references;
      const filteredLocalRefs = currentLocalRefs.filter(
        (r: any) => !dbRefs.has((r.reference || "").trim().toLowerCase()),
      );
      if (filteredLocalRefs.length !== currentLocalRefs.length) {
        melStore.references = filteredLocalRefs;
        melStore.saveReferencesToLocalStorage();
      }
    }
  } catch (e) {
    console.warn("cant load draft:", e);
  }

  if (melStore.prefillProduct) {
    // isPreferredSavePaused reste true pendant le pré-remplissage pour éviter d'écraser les données
    await applyPrefillFromProduct(melStore.prefillProduct);
    melStore.clearPrefillProduct();
    await nextTick();
  }

  // Réactiver la sauvegarde automatique seulement après tout le processus d'initialisation
  isPreferredSavePaused.value = false;
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", forceFormMethodOnCompactViewport);
});

// Watcher pour charger les caractéristiques quand la sous-sous-catégorie change
watch(selectedSousSousCategorie, (newValue) => {
  // Ne pas recharger pendant le pré-remplissage depuis un produit (géré dans applyPrefillFromProduct)
  if (isPrefillingFromProduct.value) return;

  if (newValue) {
    loadFormCaracteristiques(newValue);
  } else {
    formCaracteristiques.value = [];
    formSelectedCaracteristiques.value = [];
  }
});

// Watcher pour pré-sélectionner la marque : marque par défaut du vendeur (comme "vendu par"), sinon la seule disponible
watch(
  () => marques.value,
  (newMarques) => {
    // Ne pas exécuter pendant la restauration des préférences
    if (isRestoringPreferred.value || isPreferredSavePaused.value) return;

    if (!newMarques?.length) return;

    // Ne set la valeur par défaut QUE si formData.marque est vide
    // (pour ne pas écraser une valeur restaurée depuis localStorage ou saisie manuellement)
    if (formData.marque) return;

    const marqueDefaut = newMarques.find((m) => m.defaut);
    if (marqueDefaut) {
      formData.marque = marqueDefaut.value;
    } else if (newMarques.length === 1) {
      formData.marque = newMarques[0].value;
    }
  },
  { immediate: true },
);

// Watcher pour réinitialiser la quantité du paquet quand la taille du paquet change
watch(
  () => formData.taillePaquet,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      // Ne pas réinitialiser pendant un pré-remplissage depuis une référence
      if (
        isPrefillingFromReference.value ||
        isRestoringPreferred.value ||
        isPrefillingFromProduct.value
      )
        return;

      // Si on est en mode couleurs mélangées, réinitialiser les quantités par taille
      if (formData.venduPar === "melangees") {
        formData.quantitePaquet = "";
        formCouleurs.value = formCouleurs.value.map((c) => ({
          ...c,
          quantitesParTaille: undefined,
        }));
      }
      // Si on est en mode tailles, réinitialiser les tailleStocks
      if (formData.venduPar === "tailles") {
        formCouleurs.value = formCouleurs.value.map((c) => ({
          ...c,
          tailleStocks: taillesListForMelangees.value.map((t) => ({ taille: t, quantity: null })),
        }));
      }
    }
  },
);

// Watcher pour réinitialiser les quantités par taille quand on change de "vendu par"
watch(
  () => formData.venduPar,
  (newValue, oldValue) => {
    // Ne pas toucher aux quantités pendant les restaurations/pré-remplissages
    if (
      isRestoringPreferred.value ||
      isPrefillingFromProduct.value ||
      isPrefillingFromReference.value
    ) {
      return;
    }

    if (oldValue === "melangees" && newValue !== "melangees") {
      // On quitte le mode mélangées, supprimer les quantités par taille
      formCouleurs.value = formCouleurs.value.map((c) => ({
        ...c,
        quantitesParTaille: undefined,
      }));
    } else if (newValue === "melangees" && oldValue !== "melangees") {
      // On passe en mode mélangées, initialiser les quantités par taille
      if (taillesListForMelangees.value.length > 0) {
        formCouleurs.value = formCouleurs.value.map((c) => ({
          ...c,
          quantitesParTaille: new Array(
            taillesListForMelangees.value.length,
          ).fill(0),
        }));
      }
    }
    if (oldValue === "tailles" && newValue !== "tailles") {
      // On quitte le mode tailles, supprimer les tailleStocks
      formCouleurs.value = formCouleurs.value.map((c) => ({
        ...c,
        tailleStocks: undefined,
      }));
    } else if (newValue === "tailles" && oldValue !== "tailles") {
      // On passe en mode tailles: pas de Qté Paquet (pack) en vendu par tailles
      formData.quantitePaquet = "";
      // On passe en mode tailles, initialiser les tailleStocks
      if (taillesListForMelangees.value.length > 0) {
        formCouleurs.value = formCouleurs.value.map((c) => ({
          ...c,
          tailleStocks: taillesListForMelangees.value.map((t) => ({ taille: t, quantity: null })),
        }));
      }
    }
  },
);

// Watcher pour charger les compositions dès le choix de la catégorie principale
watch(
  () => formData.categorie,
  async (newValue) => {
    // Ne pas réinitialiser pendant la restauration depuis le localStorage / autocomplete
    if (isRestoringPreferred.value || isPrefillingFromReference.value) return;

    selectedSousCategorie.value = "";
    selectedSousSousCategorie.value = "";

    if (newValue) {
      formCompositionType.value = inferCompositionTypeFromCategorie(newValue);
      loadFormCompositionsData();
    } else {
      formCompositionType.value = null;
      formLocalisations.value = [];
      formCompositionsList.value = [];
      formCompositions.value = [];
    }

    await nextTick();

    if (
      !isRestoringPreferred.value &&
      !selectedSousCategorie.value &&
      !isPrefillingFromProduct.value &&
      sousCategorieOptions.value.length > 0
    ) {
      isCategorieOpen.value = false;
      isSousCategorieOpen.value = true;
      isSousSousCategorieOpen.value = false;
    }
  },
);
watch(selectedSousCategorie, async (newValue) => {
  if (!isRestoringPreferred.value) {
    selectedSousSousCategorie.value = "";
  }

  if (!newValue) return;

  await nextTick();

  if (
    !isRestoringPreferred.value &&
    !selectedSousSousCategorie.value &&
    !isPrefillingFromProduct.value &&
    sousSousCategorieOptions.value.length > 0
  ) {
    isCategorieOpen.value = false;
    isSousCategorieOpen.value = false;
    isSousSousCategorieOpen.value = true;
  }
});

watch(
  () => formCouleurs.value,
  (newCouleurs) => {
    if (preferredFieldsLoaded.value && !isPreferredSavePaused.value)
      savePreferredFields();

    // Initialiser tailleStocks pour les nouvelles couleurs ajoutées en mode "tailles"
    if (
      formData.venduPar === "tailles" &&
      taillesListForMelangees.value.length > 0
    ) {
      const needsUpdate = newCouleurs.some((c: any) => !Array.isArray(c.tailleStocks));
      if (needsUpdate) {
        formCouleurs.value = newCouleurs.map((c: any) => ({
          ...c,
          tailleStocks: Array.isArray(c.tailleStocks)
            ? c.tailleStocks
            : taillesListForMelangees.value.map((t) => ({ taille: t, quantity: null })),
        }));
      }
    }
  },
  { deep: true },
);
</script>
<style lang="scss" scoped>
.mel-page {
  --mel-ink: #202938;
  --mel-muted: #667085;
  --mel-border: #aeb7c3;
  --mel-surface: #ffffff;
  --mel-soft: #f3f5f8;
  --mel-primary: #2f4a56;
  --mel-primary-soft: #edf3f4;
  --mel-accent: #c56c4d;
  min-height: 100%;
  background: var(--mel-soft);
  color: var(--mel-ink);
  padding-bottom: 3rem;
}

.mel-shell {
  width: min(100%, 1500px);
  margin-inline: auto;
  padding-inline: clamp(1rem, 3vw, 2.5rem);
}

.mel-hero {
  position: relative;
  overflow: hidden;
  padding-block: clamp(1.5rem, 4vw, 1.5rem);
  padding-bottom: 0px;
  background: #f2f4f7;
  color: #ffffff;
  margin-top: 40px;
}

.mel-hero::after {
  position: absolute;
  right: clamp(2rem, 9vw, 9rem);
  bottom: -4.5rem;
  width: 14rem;
  height: 14rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  content: "";
  pointer-events: none;
}

.mel-eyebrow {
  margin: 0 0 0.6rem;
  color: #e7b49f;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.mel-hero h1 {
  max-width: 48rem;
  margin: 0;
  color: #111;
  font-size: clamp(1.5rem, 3vw, 1.6rem);
  font-weight: 750;
  letter-spacing: -0.025em;
  line-height: 1.15;
}

.mel-hero-layout {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.mel-excel-shortcut {
  position: relative;
  z-index: 1;
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  min-height: 2.65rem;
  padding: 0.65rem 1.15rem;
  color: #202938;
  background: #ffffff;
  border: 1px solid #7c8796;
  border-radius: 0.65rem;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 160ms ease, border-color 160ms ease;
}

.mel-excel-shortcut:hover {
  background: #e7eaee;
  border-color: #202938;
}

.mel-excel-shortcut:focus-visible {
  outline: 3px solid rgba(47, 74, 86, 0.2);
  outline-offset: 2px;
}

.mel-excel-label-short {
  display: none;
}

.mel-hero-copy {
  max-width: 46rem;
  margin: 0.8rem 0 0;
  color: #d5dce4;
  font-size: 1rem;
  line-height: 1.65;
}

.mel-content {
  padding-top: clamp(1.5rem, 3vw, 2.5rem);
}

.mel-form-card {
  background: var(--mel-surface);
  border: 1px solid var(--mel-border);
  border-radius: 1rem;
  box-shadow: 0 8px 26px rgba(32, 41, 56, 0.045);
}

.mel-section-heading h2 {
  margin: 0;
  color: var(--mel-ink);
  font-weight: 720;
  letter-spacing: -0.015em;
}

.mel-section-heading p {
  margin: 0.25rem 0 0;
  color: var(--mel-muted);
  line-height: 1.5;
}

.mel-form-card {
  margin-bottom: 1.5rem;
  padding: clamp(1.25rem, 2.4vw, 2rem);
}

.mel-section-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  margin-bottom: 1.7rem;
  padding-bottom: 1.1rem;
  border-bottom: 1px solid #e7ebf0;
}

.mel-section-heading > span {
  display: inline-flex;
  flex: 0 0 2.15rem;
  align-items: center;
  justify-content: center;
  width: 2.15rem;
  height: 2.15rem;
  color: #ffffff;
  background: var(--mel-accent);
  border-radius: 0.65rem;
  font-size: 0.9rem;
  font-weight: 800;
}

.mel-section-heading h2 {
  font-size: 1.18rem;
}

.mel-section-heading p {
  font-size: 0.86rem;
}

.mel-form-card > .space-y-2 > .grid,
.mel-form-card > .space-y-4 > .grid {
  row-gap: 1.35rem;
}

.mel-form-card :deep(label) {
  color: #354052;
}

.mel-form-card :deep(label.block.text-sm),
.mel-form-card :deep(h4.text-base) {
  font-size: 0.92rem;
  font-weight: 650;
}

.mel-form-card :deep(input:not([type="checkbox"]):not([type="radio"]):not([type="file"])),
.mel-form-card :deep(select),
.mel-form-card :deep(textarea),
.mel-form-card :deep(button.w-full.border) {
  min-height: 3rem;
  color: var(--mel-ink);
  background-color: #ffffff;
  border-color: #7c8796;
  border-radius: 0.65rem;
  font-size: 0.95rem;
}

.mel-form-card :deep(textarea) {
  min-height: 7rem;
  padding: 0.8rem 0.9rem;
  line-height: 1.55;
}

.mel-form-card :deep(input:focus),
.mel-form-card :deep(select:focus),
.mel-form-card :deep(textarea:focus),
.mel-form-card :deep(button.w-full.border:focus) {
  border-color: #202938 !important;
  outline: none;
  box-shadow: 0 0 0 3px rgba(47, 74, 86, 0.14) !important;
}

.mel-form-card :deep(input[type="checkbox"]),
.mel-form-card :deep(input[type="radio"]) {
  accent-color: var(--mel-primary);
}

.mel-form-card :deep(.text-xs) {
  font-size: 0.78rem;
  line-height: 1.45;
}

.mel-form-card :deep(.text-red-600),
.mel-form-card :deep(.text-red-500) {
  color: #b42318;
}

.mel-form-card :deep(.bg-blue-50) {
  background-color: var(--mel-primary-soft);
}

.mel-form-card :deep(.border-blue-500),
.mel-form-card :deep(.focus\:border-blue-500:focus) {
  border-color: #66838e;
}

.mel-form-card :deep(.text-blue-600),
.mel-form-card :deep(.text-blue-700),
.mel-form-card :deep(.text-blue-800) {
  color: var(--mel-primary);
}

.mel-actions {
  padding-top: 0.35rem;
}

.mel-actions :deep(button) {
  min-width: 15rem;
  min-height: 3rem;
  padding-inline: 1.25rem;
  border-radius: 0.7rem;
  font-size: 0.92rem;
  box-shadow: none;
  transform: none !important;
}

.mel-actions :deep(button:first-child) {
  background: var(--mel-primary);
}

.mel-actions :deep(button:first-child:hover) {
  background: #243b45;
}

.mel-actions :deep(button:last-child) {
  color: #4d5868;
  background: #ffffff;
  border: 1px solid #cbd3dd;
}

.mel-actions :deep(button:last-child:hover) {
  color: var(--mel-ink);
  background: #f5f7f9;
}

:deep(.mel-excel),
:deep(.mel-references) {
  border-color: var(--mel-border);
  border-radius: 1rem;
  box-shadow: 0 8px 26px rgba(32, 41, 56, 0.045);
}

:deep(.mel-excel) {
  padding: clamp(1.25rem, 2.4vw, 2rem);
}

:deep(.mel-excel .text-green-500),
:deep(.mel-excel .text-green-600) {
  color: #3f7651;
}

:deep(.mel-excel .bg-green-600) {
  background-color: #3f7651;
}

:deep(.mel-excel code),
:deep(.mel-excel li) {
  overflow-wrap: anywhere;
  word-break: normal;
}

:deep(.mel-references) {
  margin-top: 1.75rem;
  padding: clamp(1.25rem, 2.4vw, 2rem);
}

:deep(.mel-references > h3) {
  margin-bottom: 1.1rem;
  color: var(--mel-ink);
  font-size: 1.15rem;
}

:deep(.mel-references th),
:deep(.mel-references td) {
  padding: 0.8rem 0.65rem;
  color: #364152;
  font-size: 0.8rem !important;
  line-height: 1.4;
}

:deep(.mel-references thead) {
  background: #f3f5f8;
}

:deep(.mel-references .bg-blue-50),
:deep(.mel-references .bg-blue-100) {
  background-color: var(--mel-primary-soft);
}

:deep(.mel-references .border-blue-200) {
  border-color: #cbd9de;
}

:deep(.mel-references .text-blue-600),
:deep(.mel-references .text-blue-800) {
  color: var(--mel-primary);
}

:deep(.mel-references .bg-blue-600) {
  background-color: var(--mel-primary);
}

:deep(.mel-references .bg-green-600) {
  background-color: #4b8159;
}

:deep(.mel-references .bg-amber-600) {
  background-color: #a76825;
}

:deep(.mel-references .bg-red-600) {
  background-color: #b6463d;
}

@media (max-width: 1024px) {
  .mel-excel-shortcut {
    display: none;
  }

  :deep(.mel-excel .border-l) {
    padding-top: 1.5rem;
    padding-left: 0;
    border-top: 1px solid #e3e7ec;
    border-left: 0;
  }
}

@media (max-width: 639px) {
  .mel-page {
    padding-bottom: 1.5rem;
  }

  .mel-hero::after {
    display: none;
  }

  .mel-form-card,
  :deep(.mel-excel),
  :deep(.mel-references) {
    border-radius: 0.8rem;
  }

  .mel-excel-label-long {
    display: none;
  }

  .mel-excel-label-short {
    display: inline;
  }

  .mel-section-heading {
    margin-bottom: 1.3rem;
  }

  .mel-actions :deep(button) {
    min-width: 0;
    width: 100%;
  }
}
</style>
